import { CognitoUser, CognitoUserPool, CognitoUserSession, IMfaSettings } from "amazon-cognito-identity-js";
import moment, { Moment } from "moment";
import {
    AdminBulkUpdateTypes,
    BankCustodian,
    CustodianDefaultAgreementModel,
    EmployerAgreementModel,
    EmployerThemeResponse,
    FeeWaiverType,
    PartnerAccountsMigrationModel,
    PartnerContentType,
    PartnerThemeResponse,
    UserHsaAccountSubStatus
} from "@/admin/types";
import {
    CensusManager,
    Communication,
    CommunicationEmployerGroup,
    EmployeeListSearchFields,
    PartnerEmployerList
} from "@/employer/types";
import * as drivewealth from "./custodianSettings/drivewealthTypes";
import { onboardingStepsKeyRing, UserOnboardingSteps } from "@/shared/userOnboarding";
import { FetchKeyedResource } from "@/shared/fetchStateHelper";
import * as Flagging from "@/shared/flagging";
import { bool } from "aws-sdk/clients/signer";
import Bowser from "bowser";

type UserOnboardingStepKey = keyof typeof onboardingStepsKeyRing;

export interface EmployerPayer {
    employerPays: boolean;
    pricePerEmployeePerMonth: number;
    balanceFeeWaiverEnabled: boolean;
}
export enum HsabSubmitExpenseError {
    None = 0,
    CreateExpense = 1,
    UploadFiles = 2,
    SubmitForReimbursement = 3,
}

export enum HsabSubmitExpenseLastCompletedRequest {
    none = 0,
    createOrUpdateHsabExpense = 1,
    uploadFiles = 2,
    submitForReimbursement = 3,
}

export enum EmploymentStatus {
    None = "none",
    New = "new",
    Active = "active",
    Terminated = "terminated"
}

export const employmentStatusMap: Record<EmploymentStatus, string> = {
    none: "Unemployed",
    new: "New",
    active: "Active",
    terminated: "Terminated"
};

export interface FeeSchedule {
    monthlyFeeAmount: number;
    isEmployerBalanceFeeWaiverEnabled: boolean;
    investmentFees: InvestmentFeeSchedule[];
}

// Store
export interface AccountAlert {
    notificationId: number;
    isRead: boolean;
    content: string;
    dateCreated: string;
}

export interface AccountAlertState {
    fetchState: FetchState;
    alertUpdateFetchState: FetchState;
    accountAlertsDictionary: {
        [key: string]: AccountAlert
    };
    panelOpen: boolean;
}

export interface NavigationState {
    mobileMenuVisible: boolean;
    lastPartnerPortalTab: string;
}
export interface TalonEnabledResponse {
    isTalonAvailableForUser: boolean;
}
export type FeatureFlags = Flagging.FeatureFlags;

export interface FeatureFlagState {
    featureFlags: FeatureFlags;
    flagsLoaded: boolean;
    flagsTimedOut: boolean;
    userContext: UserTargeting;
}

export interface UserTargeting {
    email?: string;
    partnerId?: number;
    custodian?: string;
    employerId?: number;
}

export interface FileManagementState {
    uploadedFiles: {
        [key: string]: CloudFile
    };
    downloadedFiles: {
        [key: string]: CloudFile
    };
}

export interface CloudFile {
    status: FetchState;
    remoteFileName: string;
    remoteAddress: string;
    payload: any;
    interval?: number;
    exceededLimit?: boolean;
}

export enum FetchState {
    Uninitialized,
    Loading,
    Loaded,
    Failed
}

export interface EmployerAssociation {
    employerId: number;
    employerName: string;
    fundingOption: FundingOption;
    hasFundingOptionHistory: boolean;
    onBoardingProgress: OnBoardingProgress;
    isAccessBasedEmployer?: boolean;
    partnerId?: number;
    employerUserRoles: number[];
    censusManager: CensusManager;
    isTerminated: boolean;
    wexEmployerCode: string;
}

export interface PartnerAssociation {
    partnerId: number;
    partnerUserRoles: number[];
    companyName: string;
    custodian: BankCustodian;
}

export interface PartnerUserState {
    partnerUsersList: PartnerUser[];
    selectedPartnerId: number;
    pageNumber: number;
    partnerUserDictionary: PartnerUserDictionary;
    numberOfOwners: number;
    total: number;
    notificationEmail: string;
    notificationEmailFetchState: FetchState;
}

export interface PartnerEmailResponse {
    error: string;
    email: string;
}

export interface PartnerUserDictionary {
    [partnerId: number]: {
        fetchState: FetchState;
        users: PartnerUser[];
    };
}

export interface PartnerUsersResponse {
    error: string;
    users: PartnerUser[];
    numberOfOwners: number;
    pageNumber: number;
    pageSize: number;
    total: number;
}

export interface SearchCapabilityState extends SearchCapability {
    fetchState: FetchState;
}
export interface SearchCapability {
  isSearchEngineEnabled: boolean;
  isEmployerSearchEngineEnabled: boolean;
  isUserSearchEngineEnabled: boolean;
  isEmployeeSearchEngineEnabled: boolean;
  isEmployerTypeAheadEnabled: boolean;
}

export interface PartnerUserRequest {
    partnerId: number;
    searchFirstname: string;
    searchLastname: string;
    searchEmail: string;
    pageNumber: number;
    searchRole: UserRole;
}

export interface PartnerUser {
    inviteId: number;
    claimed: boolean;
    firstName: string;
    lastName: string;
    phone: string;
    jobTitle: string;
    email: string;
    isConfirmed: boolean;
    migratedToOkta: boolean;
    userRoles: number[];
}

export interface PartnerUserSaveRequest {
    partnerId: number;
    partnerUser: PartnerUser;
}

export interface PartnerUserSaveResponse {
    error: string;
    partnerUser: PartnerUser;
}

export interface OnBoardingProgress {
    currentStep: number;
    wizardComplete: boolean;
}

export enum CommunicationPortal {
    Employer = 1,
    Employee
}

export enum CommunicationDashboard {
    Hsa = "hsa",
    Notional = "notional",
    Combined = "combined"
}

export enum CommunicationClient {
    All = "all",
    Web = "web",
    Mobile = "mobile"
}

export interface UserState {
    user: User;
    bendUser: User;
    contactEmail: string;
    employerId: number;
    employersState: EmployerAssociation[];
    partnerId: number;
    partners: PartnerAssociation[];
    onBoardingProgress: OnBoardingProgress;
    accountType: UserServiceAccountType;
    loggedIn: boolean;
    userList?: SearchResultUser[];
    userListState: FetchState;
    validGroups: string[];
    checkValidGroups: boolean;
    noAccess: boolean;
    loginUrl: string;
    site: string;
    intervalId?: number;
    lastActivity?: number;
    cognitoDR?: boolean;
    employeeFundingOption: FundingOption;
    canEmployerUpdateCensus: boolean;
    partnerSettingsState: FetchState;
    partnerUsesCustomClaims: boolean;
    partnerClaimsOptIn: boolean;
    partnerDisableTalon: boolean;
    partnerConsumerLiveChatEnabled: boolean;
    consumerInitiateCobrowseEnabled: boolean;
    chatFunctionalityType: ChatFunctionalityType;
    partnerIsEnrollmentDisabled: boolean;
    partnerDisableInvestmentActivity: boolean;
    communications: Map<CommunicationPortal, Communication[]>;
    communicationsFetchState: FetchState;
    isAutoEnrolled: boolean;
    authType: AuthType;
    accountHolderPartnerId: number;
    accountHolderEmployerId: number;
    isNotionalDashboardActive: boolean;
    loginMethod: LoginMethod;
    adminPortalUsers: AdminPortalUser[];
    adminPortalUserResult: AdminUserResponse;
    adminPortalUserSearchFetchState: FetchState;
    currentAdminPortalUser: AdminPortalUser;
    hsaRepResponse: HSARepResponse;
    lastEmployerLogin: EmployerLoginState;
    partnerProducts: Product[];
}

export interface EmployerLoginState {
    partnerId: number;
    employerId: number;
}

export interface HsabEnrollmentState {
    employerSettingsFetchState: FetchState;
    planYearEnrollmentDetailsFetchState: FetchState;
    employerSettings: HsabEnrollmentSettingsResponse;
    planYearEnrollmentDetailsResponse: PlanYearEnrollmentDetailsResponse;
}
export interface HsabEnrollmentSettingsResponse {
    allowOptInOutAutoClaims: boolean;
    enableAutomaticallyFiled: boolean;
}

export interface HsabEnrollmentUpdateReimbursementMethodRequest {
    PlanYearName: string;
    PlanYearStartDate: string;
    ReimbursementMethod: HsabReimbursementMethod;
    PlanYearHasDebitCard: boolean;
}

export interface HsabEnrollmentSettingsUpdateRequest {
    autoFileClaim: boolean;
    autoFileHsa: boolean;
    linkHealthCareData: boolean;
}

export enum AuthType {
    Cognito = "Cognito",
    Okta = "Okta"
}

export enum LoginMethod {
    Unknown = "unknown",
    DirectLogin = "directLogin",
    Sso = "sso",
    Biometrics = "biometrics"
}

export const loginMethodMap: Record<LoginMethod, string> = {
    unknown: "Unknown",
    directLogin: "Direct Login",
    sso: "SSO",
    biometrics: "Biometrics"
};

export interface HsaContributionLimits {
    individualAmount: number;
    familyAmount: number;
}

export interface UserDetails {
    userId: number;
    firstName: string;
    lastName: string;
    phone: string;
    onBoardingProgress: OnBoardingProgress;
    userEmployers: EmployerAssociation[];
    userPartners: PartnerAssociation[];
    planType: PlanType;
    accountType: UserServiceAccountType;
    employerPartnerId?: number;
    employeePartnerId?: number;
    employeeFundingOption: FundingOption;
    employer: {
        employerId: number,
        name: string,
        custodian: string
    };
    mfaStatus: MfaStatusEnum;
    showMfa: boolean;
    provider: AuthType;
    isAutoEnrolled: boolean;
    five9Hash: string;
    salesforceHash: string;
    glanceHash: string;
    email: string;
    five9CampaignKey: string;
    salesforceBase2Url: string;
    salesforceScrt2Url: string;
    salesforceBootstrapUrl: string;
    glanceUrl: string;
    glanceDataSiteAttribute: string;
    chatDelay: number;
    chatFunctionalityType: ChatFunctionalityType;
    consumerInitiateCobrowseEnabled: boolean;
    isConfirmed: boolean;
    wexInstanceId: string;
    wexInstanceCode: string;
}

export interface SimpleUser {
    userId: number;
    firstName: string;
    lastName: string;
    phone: string;
    mobilePhone: string;
    partnerId: number;
    partnerName: string;
    employerId: number;
    employerName: string;
    ssnLastFour: string;
    isConfirmed: boolean;
    emailAddress: string;
    username: string;
}

export interface UserCreateResponse {
    employerId?: number;
    invitationSent: boolean;
    addedToEmployer: boolean;
}

// Models
export type NewAccountCallback = (NewAccountRequest) => void;

export enum MfaStatusEnum {
    Disabled = "Disabled",
    Sms = "Sms",
    Totp = "Totp"
}

export enum AgreementCategory {
    Investment = "investment",
    Claims = "claims",
    Other = "other"
}

export enum SigningDocumentContentType {
    Html = "html",
    Pdf = "pdf"
}

export enum PlanType {
    None = "none",
    Family = "family",
    Individual = "individual"
}

// UserService/UserService.Contracts/V1/Enum/AccountType.cs
export enum UserServiceAccountType {
    None = "none",
    Individual = "individual",
    Employee = "employee"
}

export interface EmployerCreateResponse {
    employerId: number;
}

export interface LoginRequest {
    username: string;
    password: string;
    success: (result: User) => void;
    failure: (error: any) => void;
    newPassword: (userAttributes: any, requiredAttributes: any, callback: NewAccountCallback) => void;
}

export interface User {
    displayName: string;
    groups: string[];
    firstName: string;
    lastName: string;
    email: string;
    userId?: number;
    phone?: string;
    username: string;
    five9Hashkey?: string;
    salesForceHashkey?: string;
    glanceHashKey?: string;
    campaignKey?: string;
    chatDelay?: number;
    wexInstanceCode?: string;
    glanceUrl?: string;
    glanceDataSiteAttribute?: string;
    consumerInitiateChatEnabled?: boolean;
}

export interface PartnerCreateEmployerEvent {
    employerId: number;
    initialUser?: {
        firstName: string;
        lastName: string;
        email: string;
    };
}

export interface NewAccountRequest {
    firstName: string;
    lastName: string;
    password: string;
    phone: string;
}

export interface EmployerUserState {
    employerUsers: EmployerUser[];
    postFetchState: FetchState;
}

export interface EmployerUser {
    employerUserId: number;
    isConfirmed: boolean;
    firstName: string;
    lastName: string;
    roles: string[];
    email: string;
    jobTitle: string;
    userId: number;
    userHsaEnrolled: boolean;
}

export interface EmployerUserWithEmailFailure extends EmployerUser {
    inviteEmailFailed?: boolean;
    reactivatingExistingEmployerUser?: boolean;
    statusCode: number;
    serviceErrorMessage: string;
}

export interface SearchResultUser {
    firstName: string;
    lastName: string;
    email: string;
    employerName: string;
}

export enum NotificationType {
    Danger,
    Info,
    Success,
    Warning
}

export interface Notification {
    readonly message: string;
    readonly type: NotificationType;
}

export interface Advice3 {
    ruleExecutionId: number;
    actionId: string;
    name: string;
    deliveryChannel: AdvisorDeliveryChannel;
    deliveryPage: AdvisorDeliveryPage;
    campaign: string;
    message: string;
    priority: number;
    link: string;
    linkText: string;
    isRouteLink: boolean;
    isLinkTargetNew: boolean;
    isNew: boolean;
}

export interface ActionHistory {
    ruleExecutionId: number;
    actionId: string;
}

export enum AdvisorDelayType {
    None = "none",
    DayCount = "dayCount",
    OneTime = "oneTime",
    LoginCount = "loginCount",
    PageViewCount = "pageViewCount"
}

export enum AdvisorDeliveryChannel {
    None = "none",
    AdvisorPopup = "advisorPopup",
    Email = "email"
}

export enum AdvisorDeliveryPage {
    None = "none",
    Dashboard = "dashboard",
    HealthcareExpense = "healthcareExpense",
    Cash = "cash",
    Investment = "investment",
    Contributions = "contributions",
    ContributionPlanner = "contributionPlanner",
    Resources = "resources",
    Settings = "settings",
    Reimburse = "reimburse"
}

export enum AdvisorDelayPage {
    None = "none",
    Dashboard = "dashboard",
    HealthcareExpense = "healthcareExpense",
    Cash = "cash",
    Investment = "investment",
    Contributions = "contributions",
    ContributionPlanner = "contributionPlanner",
    Resources = "resources",
    Settings = "settings",
    Reimburse = "reimburse"
}

export enum AdvisorEventType {
    None = "none",
    Login = "login",
    DashboardView = "dashboardView",
    HealthcareExpenseView = "healthcareExpenseView",
    CashView = "cashView",
    InvestmentView = "investmentView",
    ReimburseView = "reimburseView",
    ContributionsView = "contributionsView",
    ContributionPlannerView = "contributionPlannerView",
    ResourcesView = "resourcesView",
    SettingsView = "settingsView",
    SsoTaxLinkClick = "SsoTaxLinkClick",
    SsoEStatementsLinkClick = "SsoEStatementsLinkClick",
    SsoInvestmentLinkClick = "SsoInvestmentLinkClick",
    TDASsoLinkClick = "TDASsoLinkClick",
    CardActivationClick = "CardActivationClick",
    EStatementsClick = "EStatementsClick",
    StatementPreferencesClick = "StatementPreferencesClick"
}

export interface Advisor3Rule {
    id: number;
    name: string;
    query: string;
    actionIds: number[];
}

export interface Advisor3State {
    fetchState: FetchState;
    advices: Advice3[];
    bannerAdvice: Advice3;
}

export interface AdvisorRulePreviewResult {
    data: AdvisorRuleQueryResultData;
    error: string;
}

export interface AdvisorRuleQueryResultData {
    columns: object[];
    rows: object[];
}

export interface AdvisorPreviewTemplateResponse {
    deliveryChannel: AdvisorDeliveryChannel;
    subject: string;
    message: string;
}

export enum DeliveryChannel {
    Unknown,
    AdvisorPopup,
    DashboardTile,
    Interstitial,
}

export interface NotificationState {
    notifications: Notification[];
}

export interface NotificationError {
    error: any;
    rethrow: boolean;
    timeout?: 0|number;
}

export interface StateAbbreviation {
    code: string;
    name: string;
}

export interface StateState {
    status: FetchState;
    abbreviations: StateAbbreviation[];
}

export interface ChangeUserRoleParams {
    employerUserId: number;
    employerId: number;
    userRoles: string[];
}

export enum UserRole {
    None = "none",
    CustomerService = "customerService",
    Admin = "admin",
    SecurityAdmin = "securityAdmin",
    ContributionManager = "contributionManager",
    EmployeeManager = "employeeManager",
    EmployerOwner = "employerOwner",
    FraudPrevention = "partnerFraudPrevention",
    SalesRep = "partnerSalesRep",
    AdminPortalEmployerView = "adminPortalEmployerView",
    HoldOps = "partnerHoldOps",
    HoldFraud = "partnerHoldFraud",
    RegisteredInvestmentAdvisor = "registeredInvestmentAdvisor"
}

// Used to support legacy role based security/permissions
export enum UserRoleInt {
    partnerAdmin = 2,
    partnerSecurity = 3,
    employerCensus = 5,
    employerSecurity = 6
}

export enum UserRoleNames {
    employerCensus = "Employer Census",
    employerSecurity = "Employer Security",
    partnerAdmin = "Partner Admin",
    partnerSecurity = "Partner Security",
}

export const userRoleMap: Record<UserRole, string> = {
    none: "N/A",
    customerService: "Customer Service",
    admin: "Admin",
    securityAdmin: "Security",
    contributionManager: "Contribution Manager",
    employeeManager: "Employer Manager",
    employerOwner: "Owner",
    partnerFraudPrevention: "Fraud Prevention",
    partnerSalesRep: "Sales Rep",
    adminPortalEmployerView: "Admin Portal Employer View",
    partnerHoldOps: "Hold Operations",
    partnerHoldFraud: "Hold Fraud",
    registeredInvestmentAdvisor: "Registered Investment Advisor"
};

export interface CreateEmployerUserParams {
    employerId: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    jobTitle: string;
    roles: number[];
}

export interface AddEmployeeGroupRequest {
    email: string;
    verificationCode: string;
}

export interface Authentication {
    userPool: CognitoUserPool;

    createCognitoUser (username: string);

    accessToken (): Promise<string>;

    getAwsCredentials (): Promise<string>;

    getUser (): null | {
        username: string
    };

    forceRefreshSession (): Promise<User>;

    postLoginProcess (promise: Promise<CognitoUserSession>, username: string,
                      requiredGroups: ReadonlyArray<string>, cognitoUser: CognitoUser): Promise<User>;

    login (username: string, password: string, requiredGroups: ReadonlyArray<string>,
           mfaFunc: (t: string, u: CognitoUser) => void): Promise<User>;

    loginInitializePassword (username: string, initialPassword: string, requestedPassword: string,
                             mfaFunc: (s: MfaStatusEnum, u: CognitoUser) => void): Promise<void>;

    verifyEmail (verificationCode?: string): Promise<void>;

    logout ();

    confirmPassword (username: string, verificationCode: string, password: string): Promise<any>;

    setInitialPassword (username: string, oldPassword: string, newPassword: string,
                        mfaFunc: (s: MfaStatusEnum, u: CognitoUser) => void): Promise<void>;

    resetPassword (username: string): Promise<any>;

    autoRecoverUser (email: string): Promise<any>;

    setSignInUserSession (username: string, accessToken: string, idToken: string, refreshToken: string): Promise<any>;

    authenticateCognitoUser (username: string, password: string,
                             mfaFunc: (s: MfaStatusEnum, u: CognitoUser) => void): Promise<CognitoUser>;

    setMFAPreference (cognitoUser: CognitoUser, smsMfaSettings: IMfaSettings | null,
                      softwareTokenMfaSettings: IMfaSettings | null);

    associateToken (cognitoUser: CognitoUser);

    verifyToken (cognitoUser: CognitoUser, token: string);

    updateUserAttribute (cognitoUser: CognitoUser, attributeName: string, attributeValue: string);

    sendPhoneVerification (cognitoUser: CognitoUser);

    verifyPhone (cognitoUser: CognitoUser, verificationCode: string);

    getPhone (cognitoUser: CognitoUser);

    isPhoneVerified (cognitoUser: CognitoUser);

    setAdmin ();
}

export interface ImpersonationInfo {
    userName: string;
    email: string;
    userDetail: UserDetails;
}

export interface ImpersonationInitiateData {
    cognitoUsername: string;
    impersonationInfo: ImpersonationInfo;
    impersonationToken: string;
}

export interface ImpersonationState {
    impersonation: ImpersonationInfo;
    isActive: boolean;
    isStarting: boolean;
    initiateResponseFetchState: FetchState;
    impersonationInitiateData: ImpersonationInitiateData;
}

export interface ErrorHandlerArgs {
    error: any;
    rethrow: boolean;
    timeout?: 0|number;
    overrideNotificationMessage?: string;
}

export interface GradientPoint {
    color: string;
    alpha: number; // 0.0 to 1.0 inclusive
}

export interface BaseListParams {
    id: number;
    pageNumber: number;
    pageSize: number;
    sortOrder: SortOrder;
    sortOption: EmployeeListSortOption;
    clearState: boolean;
    partnerId: number;
}

export interface EmployeeListParams extends BaseListParams {
    searchTerm: EmployeeListSearchFields;
}

export interface ListParams extends BaseListParams {
    searchTerm: string;
    includeTerminated: boolean;
}

export interface PendingContributionsListParams extends ListParams {
    displayExpiredOnly: boolean;
}

export interface PagedResult {
    index: number;
    pageSize: number;
}

export interface PageableState {
    pages: PagedResult[];
    totalRecords: number;
    pageSize: number;
    pageNumber: number;
}

export enum AdvisorAttributes {
    Login = "Login"
}

export interface PrivateCipInfo {
    ssn: string;
    dateOfBirth: string;
    allowSignupWithNewEmployer: boolean;
}

export interface CipPostResult {
    isUserMatch: boolean;
    isEmployerMatch: boolean;
    isReadyToClaimMigrationMatch: boolean;
    migratedUserPartnerId?: number;
    isSelfReportedEmployee: boolean;
    hasExistingAccount: boolean;
    hasAccountWithOtherEmployer: boolean;
}

export interface PortalCustomizationState {
    partnerId: number;
    employerId: number;
    isPartnerCustomLogo: boolean;
    isEmployerCustomLogo: boolean;
    partnerLogo: string;
    employerLogo: string;
    fetchEmployerLogoState: FetchState;
    isCustomDebitCardImage: boolean;
    debitCardImage: string;
    fetchState: FetchState;
    debitCardFetchState: FetchState;
    supportInfo: SupportInfoState;
    hsaStoreInfo: PartnerHsaStoreInfo;
    hsaStoreFetchState: FetchState;
    employeeLogoutRedirectFetchState: FetchState;
    employeeLogoutRedirectUrl: string;
    communicationKitTypeFetchState: FetchState;
    communicationKitType: CommunicationKitType;
}

export interface CustodianSettingsState {
    employerCustodianSettings: EmployerCustodianSettings;
    userCustodianSettings: UserCustodianSettings;
}

export interface EmployerCustodianSettings {
    suppressInviteEmailLink: boolean;
    suppressEmployerOnboardingBankInfo: boolean;
    fetchState: FetchState;
    employerCustodian: BankCustodian;
    currentEmployerId: number;
    validateEmployeeEmailLength: boolean;
    disableEmployeeEdit: boolean;
    employerCustodianInfoTemplate: string;
    displayBankAccountHolds: boolean;
}

export interface CustodianStatementLink {
    url: string;
    target: string;
}

export interface UserCustodianSettings {
    footerTemplate: string;
    investmentRiskDisclaimer: string;
    taxLink: CustodianStatementLink;
    statementsLink: CustodianStatementLink;
    statementPreferencesLink: CustodianStatementLink;
    userHsaCustodian: BankCustodian;
    displayMyProfileEditMode: boolean;
    displayMFASettings: boolean;
    allowEmailEdit: boolean;
    profileEditInformationHtml: string;
    logoutRedirectUrl: string;
    faqTemplate: string;
    faqInstructionsTemplate: string;
    displayInvestmentsSection: boolean;
    displayFooterLegalSection: boolean;
    contactDetailsSetAtBank: boolean;
    displayAdvice: boolean;
    displayFaqExternalLinks: boolean;
    displaySettingsLinkedAccounts: boolean;
    priorYearContributionOption: boolean;
    allowReimbursement: boolean;
    showPendingReimbursements: boolean;
    displayFaqInstructions: boolean;
    showAlternateOnboardingCompleteMessage: boolean;
    fetchState: FetchState;
    termsUrl: string;
    hsaTermsUrl: string;
    feeTemplate: string;
    isFeeTemplateExternalUrl: boolean;
    cashAccountFooter: string;
    contributionsFooter: string;
    quarterlyPaperStatementFee: number;
    manageHsaCardsInPortal: boolean;
    displayPriorYearContributionDisclaimerCopy: boolean;
    fetchStateKey: FetchStateKey;
    displayProjectedContributions: boolean;
    allowUserToEditContributions: boolean;
    showCashExpensesGraphWidget: boolean;
    showExpenseCategoryWidget: boolean;
    showExpenseEligibilityWidget: boolean;
    showHealthcareExpensesV2: boolean;
    displayHealthcareClaims: boolean;
    displayPdfForESign: boolean;
    displayHealthcareBluebookLink: boolean;
    faqLinkAddress: string;
    eSignPdfUrl: string;
    allowFamilyMemberSsnInput: boolean;
    showHsaStoreShopTab: boolean;
    showChildExpenseRows: boolean;
    transactionPayrollOneTimeIsAdjustment: boolean;
    disabledRouteRedirects: {
        [key: string]: string
    };
    displayCashAccountMfaPopup: boolean;
    displayNewHealthcareExpenses: boolean;
    disableHsaInvestEnrollButtonAfterDate: string;
    disablePortalFunctionalityAfterDate: string;
    displayFdicBanner: boolean;
    fdicDisclaimer: string;
    transferOrRolloverHsaFundsUrl: string;
    loginHelpUrl: string;
    contactUsUrl: string;
    mobileAppUrl: string;
}

export interface FdicState {
    forceHideFdic: boolean;
    routeHideFdic: boolean;
}

// defined on backend as CardService.Contracts.V1.Enums.CardStatus
export enum CardStatus {
    None = "none",
    Active = "active",
    LostOrDamaged = "lostOrDamaged",
    Closed = "closed",
    ReplacementRequested = "replacementRequested",
    Pending = "pending",
    Error = "error",
    Embossing = "embossing",
    FraudOrStolen = "fraudOrStolen",
    Fraud = "fraud",
    Stolen = "stolen",
    Suspended = "suspended",
    PendingClose = "pendingClose",
}

export interface DebitCardRecord {
    isPrimary: boolean;
    cardNumberLast4: string;
    isPinSet: boolean;
    firstName: string;
    lastName: string;
    cardId: null|number;
    cardStatus: CardStatus;
    authorizedSignerPendingId: null|number;
    userCanManageCard: boolean;
}

export interface PotentialCardHolder {
    firstName: string;
    lastName: string;
}

export interface DebitCardListResponse {
    cards: DebitCardRecord[];
    potentialCardHolders: PotentialCardHolder[];
    primaryCard: null|DebitCardRecord;
}

export interface CardProviderOption {
    cardProvider: CardProvider;
    capability: CardProviderCapability;
}

export interface DebitCardManagementState {
    debitCardsFetchState: FetchState;
    debitCards: DebitCardRecord[];
    potentialCardHolders: PotentialCardHolder[];
    primaryCard: null|DebitCardRecord;
    cardProviderOptionsFetchState: FetchState;
    cardProviderOptions: null|CardProviderOption;
    focusedDebitCard: null|DebitCardRecord;
    modalViewState: DebitCardManagerModalViewState;
    deactivationReason: null|DebitCardDeactivationReasons;
    pendingNewCardHolder: null|PotentialCardHolder;
}

export interface DebitCardsState {
    debitCardsFetchState: FetchState;
    isChangingStatus: boolean;
    debitCards: DebitCardInfo[];
}

export enum FiservDebitCardsDetailLocation {
    ActionMenu,
    ReplacementRequestedActionMenu,
    SetPinForm,
    ChangePinForm,
    DeactivateForm,
    ReplacementForm,
    ConfirmReplacementForm,
}

export interface FiservDebitCardsState {
    fiservDebitCardsFetchState: FetchState;
    fiservDebitCards: FiservDebitCardInfo[];
    isCardStatusChanging: boolean;
    detailLocation: FiservDebitCardsDetailLocation;
    fiservDebitCardDependentModalOpen: boolean;
}

export interface AvidiaStatementState {
    [year: number]: {
        fetchState: FetchState;
        listing: AvidiaStatementsList;
    };
}

export interface HsaBankStatementState {
    year: number;
    recentLookbackWindow: number;
    fetchState: FetchState;
    list: HsaBankStatementsList;
    pdfs: {
        [identifier: string]: GeneratedPdfState
    };
    anonDocuments: {
        [identifier: string]: AnonDocumentState
    };
}

export interface GeneratedPdfState {
    fetchState: FetchState;
    pdfData: Blob;
}

export interface AnonDocumentKey {
    key: string; // GUID
    signature: string; // HMAC signature
}

export interface AnonDocumentState {
    fetchState: FetchState;
    documentKey: AnonDocumentKey;
}

export interface HsaBankTaxDocumentsState {
    year: number;
    documentsPerYear: {
        [year: number]: HsaBankTaxDocumentsForYear
    };
    pdfs: {
        [identifier: string]: GeneratedPdfState
    };
    anonDocuments: {
        [identifier: string]: AnonDocumentState
    };
}

export interface HsaBankTaxDocumentsForYear {
    fetchState: FetchState;
    list: HsaBankTaxDocumentList;
}

export interface HsaBankStatementsList {
    statements: HsaBankStatementListItem[];
}

export interface HsaBankStatementListItem {
    identifier: string;
    title: string;
    dateGenerated: string;
    datePeriodStart: string;
    datePeriodEnd: string;
    hsabDocumentType: HsabDocumentType;
}
export enum HsabDocumentType {
    None = "none",
    AccountSummary = "accountSummary",
    Tax = "tax",
    NonHsaAccountSummary = "nonHsaAccountSummary"
}
export interface HsaBankTaxDocumentList {
    documents: HsaBankTaxDocumentListItem[];
}

export interface HsaBankTaxDocumentListItem {
    documentType: HsaBankTaxDocumentType;
    identifier: string;
    title: string;
    dateGenerated: string;
    taxYear: number;
}

export enum HsaBankTaxDocumentType {
    None = "None",
    Form1099Sa = "Form1099Sa",
    Form5498Sa = "Form5498Sa",
    Form5498SaCorrected = "Form5498SaCorrected",
}

export interface DebitCardInfo {
    debitCardId: string;
    firstName: string;
    lastName: string;
    lastFour: string;
    isActivated: boolean;
    isAwaitingReissue: boolean | null;
}

export interface FiservDebitCardListResponse {
    cards: FiservDebitCardInfo[];
}

export enum FiservDebitCardStatus {
    None = "none",
    Active = "active",
    Inactive = "inactive",
    Cancelled = "cancelled",
    ReplacementRequested = "replacementRequested",
    Pending = "pending",
    Error = "error",
    Embossing = "embossing"
}

export interface FiservDebitCardInfo {
    fiservUserInfoId: number | null;
    firstName: string;
    lastName: string;
    cardNumberLast4: string | null;
    isPinSet: boolean;
    isCardStatusChanging: boolean;
    cardStatus: FiservDebitCardStatus;
    fiservAuthorizedSignerPendingId: number | null;
}

// This is from bootstrap vue https://bootstrap-vue.org/docs/components/table#row-details-support
export interface BootstrapVueRowDetails {
    toggleDetails (): any;
}

export interface SetCardPinRequest {
    oldPin: string | null;
    pin: string;
}

export interface OrderReplacementCardRequest {
    cardStatus: CardStatus;
}

export interface DependentRequest {
    firstName: string;
    lastName: string;
}

export interface CardAddDependentResponse {
    firstName: string;
    lastName: string;
    cardNumberLast4: string;
    isPinSet: boolean;
}

export interface AvidiaStatementsList {
    priorYear: number;
    nextYear: number;
    statements: {
        date: string;
        url: string;
        title: string;
    } [];
}

export interface DisabledRoutesDictionary {
    [key: string]: string;
}

export interface SupportInfoRequest {
    partnerId: number;
    employerId: number;
}

export interface SupportInfoState extends SupportInfo {
    fetchState: FetchState;
    partnerId: number;
    employerId: number;
    isFallbackData: boolean;
}

export type HoursConfig = "hide" | "default" | "custom";

export interface SupportInfo {
    supportPhone: string;
    supportEmail: string;
    supportWebsite: string;
    website: string;
    displayName: string;
    customFromEmail: string;
    emailHeaderImageUrl: string;
    emailButtonColor: string;
    emailButtonTextColor: string;
    isPrimaryContactServiceContact: boolean;
    primaryContactTitleOverride: string;
    primaryContactHoursConfig: HoursConfig;
    primaryContactCustomHours: string;
    secondaryContactPhone: string;
    secondaryContactShowPhone: boolean;
    secondaryContactEmail: string;
    secondaryContactTitle: string;
    secondaryContactHoursConfig: Exclude<HoursConfig, "default">;
    secondaryContactCustomHours: string;
}

export interface PartnerHsaStoreInfo {
    storeLink: string;
    eligibilityLink: string;
    storeType: StoreType;
}

export interface BillingPeriodEmployer {
    employerId: number;
    employerName: string;
}

export interface BillingPeriodResponse {
    periodId: number;
    periodName: string;
}

export interface BillingPeriodWithEmployersResponse extends BillingPeriodResponse {
    employers: BillingPeriodEmployer[];
}

export interface BillingState {
    monthlyBillingPeriods: BillingPeriodWithEmployersResponse[];
    monthlyBillingPeriodsFetchState: FetchState;
    billingPeriodSelectedState: FetchState;
    billingPeriodId: number;
    billingPeriodName: string;
    billingEmployerId: number;
    billingEmployerName: string;
    reconBillingPeriods: BillingPeriodWithEmployersResponse[];
    reconBillingPeriodsFetchState: FetchState;
    reconPeriodSelectedState: FetchState;
    reconPeriodId: number;
    reconPeriodName: string;
    reconEmployerId: number;
    reconEmployerName: string;
}


export enum TrxDateRangeType {
  // a full specific year 1/1-12/31 of that year inclusive
  FullYear,
  // the 12 months leading up to and inclusive of the current date
  TrailingYear
}
export interface TrxDateRange {
  variant: TrxDateRangeType;
  year?: number;
  toString: () => string;
}

export interface QuickSightState {
    fetchState: FetchState;
    userReportsFetchState: FetchState;
    userReports: QuickSightUserReportList;
}

export interface ReportCategory {
    reportCategoryId: number;
    categoryName: string;
}

export enum QuickSightReportType {
    None = "none",
    Employer = "employer",
    Partner = "partner"
}

export interface QuickSightUserReport {
    quickSightReportId: number;
    quickSightLinkId: string;
    categoryName: string;
    sortId: number;
    reportName: string;
}

export interface QuickSightUserReportsApiResponse {
    reportsByCategory: QuickSightUserReportList;
}

export interface QuickSightUserReportList {
    [key: string]: QuickSightUserReport[];
}

export interface QuickSightReport {
    quickSightReportId: number;
    reportLinkId: string;
    reportName: string;
    reportType: QuickSightReportType;
    reportCategoryId: number;
    reportCategoryName: string;
    isEnabled: boolean;
    partnerIds: number[];
    allowedRoles: number[];
    dateCreated: string;
    isSubReport: boolean;
}

export interface QuickSightReportState {
    fetchState: FetchState;
    postFetchState: FetchState;
    categoryFetchState: FetchState;
    categories: ReportCategory [];
    reports: QuickSightReport[];
}

export interface RowItemFormatting {
    index: number;
    format: string;
}

export enum ReportItemFormat {
    Date = "Date",
    Currency = "Currency",
    Number = "Number"
}

export enum QueryStatus {
    RUNNING = "RUNNING",
    SUCCEEDED = "SUCCEEDED"
}

export enum ReportParameterType {
    TimeStamp = "timeStamp",
    Lookup = "lookup"
}

export enum FundingOption {
    None = "none",
    BendCalculatePull = "bendCalculatePull",
    ErCalculateBendPull = "erCalculateBendPull",
    ErCalculateErPush = "erCalculateErPush"
}

export const fundingOptionMap: Record<FundingOption, string> = {
    none: "N/A",
    bendCalculatePull: "Bend Calculates Bend Pulls",
    erCalculateBendPull: "Employer Calculates Bend Pulls",
    erCalculateErPush: "Employer Calculates Employer Pushes"
};

export enum EnrollmentOption {
    None = "none",
    EmployerBulk = "employerBulk",
    EmployeeSetup = "employeeSetup"
}

export interface UserPlanType extends UserPlanTypeUpdate {
    isEditableByUser: boolean;
}

export interface UserPlanTypeUpdate {
    planType: PlanType;
    planTypeChangeEffectiveDate: string;
}

export interface UserPlanState {
    userPlan: UserPlanType;
    fetchState: FetchState;
    userCoverageLevels: UserCoverageLevel[];
}

export interface UserCoverageLevel {
    coverageLevel: PlanType;
    coverageLevelEffectiveDate: string;
    coverageLevelEffectiveYear: number;
}

export interface UserCoverageLevels {
    coverageLevels: UserCoverageLevel[];
}

export interface UserCoverageLevelUpdateResponse {
    activeCoverageLevel: UserCoverageLevel;
    coverageLevels: UserCoverageLevel[];
}

export interface ExpenseAttachmentDictionary {
    [expenseId: number]: DownloadLinkDictionary;
}

export interface DownloadLinkDictionary {
    [filename: string]: string; // download link
}

export interface ExpenseEvidenceUploadResponse {
    fileNames: string [];
}

export interface ExpenseAttachmentLinkAdd {
    expenseId: number;
    filename: string;
    link: string;
}

export interface ExpenseState {
    likelyExpenses: ExpenseGroup;
    potentialExpenses: ExpenseGroup;
    unlikelyExpenses: ExpenseGroup;
    expenseAttachments: ExpenseAttachmentDictionary;
    trasactionsToRemove: TransactionToRemove [];
    importedExpenses: ImportedExpense[];
    importedExpensesFetchState: FetchState;
    searchResultDictionary: {
        [key: number]: ExpenseResultSet
    };
    expensesByExternalIdFetchStateDictionary: { [externalId: string]: FetchState };
    expensesByExternalIdDictionary: { [externalId: string]: Expense };
}

export interface ExpenseStateNew {
    expenses: ExpenseGroup;
    currentRequest: ExpenseRequest;
    expenseAttachments: ExpenseAttachmentDictionary;
    trasactionsToRemove: TransactionToRemove [];
    transactionRemoveCompleted: boolean;
    importedExpenses: ImportedExpense[];
    importedExpensesFetchState: FetchState;
}

export interface TransactionToRemove {
    transactionId: number;
    timerId: NodeJS.Timer;
    transactionType: ExpenseEligibilityGroup;
}

export interface ExpenseBalanceSummaryState {
    fetchState: FetchState;
    likelyEligible: number;
    potentiallyEligible: number;
    unlikelyEligible: number;
    ineligible: number;
    unreimbursed: number;
    reimbursed: number;
    reimbursedYtd: number;
    reimburseMeBar: ExpenseReimburseMeParams[];
}

export interface ExpenseGroup {
    records: ExpenseDictionary;
    page: number;
    numberOfPages: number;
    transactionType: string;
    expenseFetchState: FetchState;
    pageInvalidations: any[];
}

export interface ExpenseDictionary {
    [key: number]: PossibleExpense [];
}

export interface PossibleExpense {
    transactionId: number;
    eligibleAmount: number;
    transactionAmount: number;
    description: string;
    institutionName: string;
    transactionDate: string;
    dateOfService: string;
    predictedStatus: PredictedEligibility;
    eligibilityStatus: ExpenseEligibility;
    expenseCategory: ExpenseCategory;
    expense: Expense;
}

export enum ExpenseEligibilityGroup {
    None = 0,
    LikelyEligible = 1,
    PotentialEligible = 2,
    UnlikelyEligible = 3
}

export enum ExpenseCategory {
    None = "none",
    Dental = "dental",
    Medical = "medical",
    Eye = "eye",
    Pharmacy = "pharmacy",
    Other = "other"
}

export enum WexExpenseAccountType {
    Unknown = "unknown",
    Medical = "medical",
    DependentCare = "dependentCare",
    Transportation = "transportation",
    Premium = "premium",
    Tuition = "tuition",
    Lifestyle = "lifestyle",
}

export enum HsabExpenseCategory {
    None = "none",
    Unknown = "unknown",
    Medical = "medical",
    Dental = "dental",
    Vision = "vision",
    Pharmacy = "pharmacy",
    Premium = "premium",
    DependentCare = "dependentCare",
    Parking = "parking",
    MassTransit = "massTransit",
    Lifestyle = "lifestyle",
    Tuition = "tuition",
}

export enum DrivewealthTransactionUnit {
    ByShare = "byShare",
    ByAmount = "byAmount"
}

export enum ManuallyMarkedAsPaidStatus {
    Unknown = "unknown",
    MarkedAsPaid = "markedAsPaid",
    NotMarkedAsPaid = "notMarkedAsPaid"
}

export interface GetHsabExpenseCategoriesResponse {
    [category: string]: HsabExpenseCategoryResponse;
}

export interface HsabExpenseCategoryResponse {
    expenseCategory: HsabExpenseCategory;
    eligibleStartDate: string;
    eligibleEndDate: string | null;
    expenseTypes: HsabExpenseTypeResponse[];
}

export interface HsabExpenseTypeResponse {
    expenseType: string;
    expenseSubTypes: HsabExpenseSubTypeResponse[];
    eligibleStartDate: string;
    eligibleEndDate: string | null;
}

export interface HsabExpenseSubTypeResponse {
    subTypeName: string;
    customDisplayName: string;
    eligibleStartDate: string;
    eligibleEndDate: string | null;
    planEligibility: HsabExpenseCategoryPlanEligibilityRange[];
}

export interface HsabExpenseCategoryPlanEligibilityRange {
    eligibleStartDate: string;
    eligibleEndDate: string | null;
    planName: string;
    planDisplayName: string;
    planStartDate: string;
    isHsa: boolean;
    reimbursementMethod: HsabExpenseReimbursementMethod;
}

export enum HsabExpenseReimbursementMethod {
    None = "none",
    DirectDeposit = "directDeposit",
    Check = "check",
}
export const hsabExpenseReimbursementMethodMap: Record<HsabExpenseReimbursementMethod, string> = {
    none: "None",
    check: "Check",
    directDeposit: "Direct deposit"
};

export interface PayFromPlanOption {
    planName: string;
    planDisplayName: string;
    isHsa: boolean;
    planStart: Moment;
    account: HsabAccountNametag;
    reimbursementMethod: HsabExpenseReimbursementMethod;
}

export enum ExpenseEligibility {
    None = "none",
    Eligible = "eligible",
    Ineligible = "ineligible"
}

export enum PredictedEligibility {
    None = "none",
    LikelyEligible = "likelyEligible",
    MayBeEligibleHigh = "mayBeEligibleHigh",
    MayBeEligibleLow = "mayBeEligibleLow",
    UnlikelyEligible = "unlikelyEligible"
}

export interface ExpenseReimburseMeParams {
    amount: number;
    transactionId?: number;
    expenseId: number;
    eligibility: ExpenseEligibility;
}

export interface Balance {
    type: BalanceType;
    amount: number;
}

export interface BalanceResponse {
    balances: Balance [];
}

export interface PartnerBankPendingTransactionResponse {
    transactions: {
        status: string,
        description: string,
        amount: number,
        date: string
    }[];
}

export interface ExpenseEligibilityStatus {
    transactionType: ExpenseEligibilityGroup;
    expenseStatusList: ExpenseEligibilityStatusItem[];
}

export interface ExpenseEligibilityStatusItem {
    transactionId: number;
    eligibilityStatus: ExpenseEligibility;
}

export interface UserBankAccountState {
    fetchState: FetchState;
    fetchKey: FetchStateKey;
    accountNumber: string;
    routingNumber: string;
}

export interface HsaAccountsCollection {
    items: HsaAccountSummary[];
}

export interface HsaAccountSummary {
    accountNumber: string;
    routingNumber: string;
    memberNumber: string;
    isBankAccountDisabled: boolean;
    custodian: BankCustodian;
    subStatus: UserHsaAccountSubStatus;
    lastUpdated: string;
    dateAccountOpened: string;
    dateAccountClosed: string;
    dateOfStatusChange: string;
}

export interface SingleInvestmentProfile {
    acountNumber: string;
    dateAccountInactivated: string;
}

export interface BankAccountStatus {
    isClosed: boolean;
}

export enum AchVerifier {
    None = "none",
    Umb = "umb",
    Finicity = "finicity"
}
export const achVerifierDisplayMap: Record<AchVerifier, string> = {
    none: "None",
    umb: "UMB",
    finicity: "Finicity"
};

export interface BankAccountState {
    fetchState: FetchState;
    linkedAccountsFetchState: FetchState;
    accountNumber: string;
    linkFinancialCardUrl: string;
    linkedCardState: LinkedCardState;
    linkedCards: Card[];
    linkedCardNeedsMaintenance: boolean;
    linkedCardMaintenanceUrl: string;
    linkedCardNeedsMaintenanceFetchState: FetchState;
    linkedAccounts: LinkedAccountApi[];
    microEntryVerificationAccounts: MicroEntryVerificationAccount[];
    microEntryVerificationAccountsFetchState: FetchState;
    linkedAccountState: LinkedAccountState;
    linkedVerificationState: LinkedVerificationState;
    verificationResponse: LinkedBankAccountVerificationStatus;
    isLinkedCardIframeLoading: boolean;
    achVerifier: AchVerifier;
    achVerifierUrl: string;
    achVerifierUrlState: FetchState;
    isAchVerifyIFrameLoaded: boolean;
    isAccountDelayInfoPanelLoaded: boolean;
    bankAchAccountChoices: BankAchAccountChoiceModel[];
    defaultContributionAccountId: null | number;
    defaultReimbursementAccountId: null | number;
    defaultScheduledContributionAccountId: null | number;
    hasDirectDepositAccount: boolean;
    hasMissingUses: boolean;
    isPendingAccount: boolean;
    consumerBankAccountGetDateCreated: string;
    consumerBankAccountGetHttpStatusCode: string;
    consumerBankAccountGetErrorMessage: string;
    isAccountClosed: boolean;
    accountStatusFetchState: FetchState;
    priorYearContributionsCutoffDate: string;
    beneficiaries: Beneficiaries;
    beneficiariesFetchState: FetchState;
    isFinicityConnectScriptLoaded: boolean;
    finicityIFrameLocation: FinicityIFrameLocation;
}

export enum FinicityIFrameLocation {
    linkedAccounts,
    expenseForm,
}

export interface AuthorizedSignerState {
    fetchState: FetchState;
    authorizedSigners: AuthorizedSigners;
    canOrderDebitCard: boolean;
    debitCardEligibilityFetchState: FetchState;
    username?: string;
}

export interface AuthorizedSigners {
    [key: number]: AuthorizedSigner;
}

export interface AuthorizedSigner {
    id: number;
    firstName: string;
    middleInitial?: string;
    lastName: string;
    dateOfBirth: string;
    ssn: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
    status: AuthorizedSignerStatus;
    externalId: string;
    authorizedSignerType: AuthorizedSignerType;
    userFamilyMemberExternalId?: string;
}

export interface GetAuthorizedSignerResponse {
    authorizedSigners: AuthorizedSigner[];
}

export interface AddAuthorizedSignerRequest {
    authorizedSigner: AuthorizedSigner;
    orderDebitCard?: boolean;
}

export interface AddAuthorizedSignerResponse {
    authorizedSigner: AuthorizedSigner;
}

export interface UpdateAuthorizedSignerResponse {
    authorizedSigner: AuthorizedSigner;
}

export interface DebitCardEligibilityResponse {
    canOrder: boolean;
}

export enum AuthorizedSignerStatus {
    None = "none",
    Active = "active",
    Inactive = "inactive"
}

export enum AuthorizedSignerType {
    Unknown = "unknown",
    AuthorizedSigner = "authorizedSigner",
    PowerOfAttorney = "powerOfAttorney",
    AuthorizedRepresentative = "authorizedRepresentative",
    Guardian = "guardian",
    Parent = "parent",
    Conservator = "conservator"
}

export interface BeneficiaryState {
    beneficiaries: Beneficiary[];
    fetchState: FetchState;
    username?: string;
}

export interface Beneficiaries {
    [key: number]: Beneficiary;
}

export interface GetBeneficiaryResponse {
    beneficiaries: Beneficiary[];
}

export interface AddBeneficiaryResponse {
    beneficiary: Beneficiary;
}

export interface BeneficiaryUpdateResponse {
    beneficiary: Beneficiary;
}

export interface Beneficiary {
    id: number;
    firstName: string;
    middleInitial?: string;
    lastName: string;
    dateOfBirth: string;
    ssn: string;
    address1: string;
    address2: string;
    city: string;
    stateCode: string;
    zip: string;
    share: number;
    type: BeneficiaryType;
    relationshipType: BeneficiaryRelationshipType;
    status: BeneficiaryStatus;
    estateName: string;
    processingStatus: BeneficiaryProcessingStatus;
}

export interface BeneficiaryUpdateRequest extends Beneficiary {
    beneficiaryShares: BeneficiaryShare[];
}

export interface BeneficiaryShare {
    beneficiaryId: number;
    share: number;
}

export enum BeneficiaryStatus {
    None = "none",
    Active = "active",
    Inactive = "inactive"
}

export enum BeneficiaryRelationshipType {
    None = "none",
    Spouse = "spouse",
    Dependent = "dependent",
    Other = "other",
    Estate = "estate"
}

export enum BeneficiaryType {
    None = "none",
    Primary = "primary",
    Contingent = "contingent"
}

export enum BeneficiaryProcessingStatus {
    Unknown = "unknown",
    Pending = "pending",
    Processing = "processing",
    Completed = "completed"
}

export interface BankAccountDefaultsRequest {
    defaultContributionAccountId: null | number;
    defaultReimbursementAccountId: null | number;
    defaultScheduledContributionAccountId: null | number;
    hasDirectDepositAccount: boolean;
    hasMissingUses: boolean;
    isPendingAccount: boolean;
}

export interface InvestmentTransferState {
    successfulRequestsThisSession: InvestmentTransferRequest[];
}

//
// export interface HsabInvestmentState {
//     iframe: FetchKeyedResource<HsabInvestmentIframe>;
//     investmentAccountStatus: FetchKeyedResource<HsabInvestmentAccountStatus>;
// }

export interface HsabInvestmentIframe {
    url: string;
}

export interface HsabInvestmentAccountStatus {
    investmentEnabled: boolean;
    investmentAccounts: HsabInvestmentAccount[];
    investmentBalance: number;
}

export interface HsabInvestmentAccount {
    accountName: string;
    accountBalance: number;
}

export interface Card {
    id: number;
    bank: string;
    number: string;
    oAuthInstitutionLoginId: number;
}

export interface Bank {
    id: number;
    name: string;
}

export interface VerificationSubmission {
    linkedBankAccountId: undefined | number;
    verifyAmount1: number;
    verifyAmount2: number;
}

export interface LinkedAccount {
    accountNumber: string;
    routingNumber: string;
    accountId?: number;
    isVerified: boolean;
    name: string;
}

export interface LinkedAccountAdd {
    accountNumber: string;
    routingNumber: string;
    name: string;
    type: LinkedAccountType;
}

export enum LinkedAccountState {
    Link,
    List,
    Form,
    Verify,
    Loading,
    Choice,
    Success
}

export enum LinkedCardState {
    Link,
    List,
    Form,
    Delete,
    Loading,
    Error
}

export enum LinkedAccountType {
    Checking,
    Savings
}

export enum LinkedVerificationState {
    Hidden,
    Form,
    Uninitialized,
    Loading,
    Success,
}

export interface LinkedAccountApi {
    bankAccountNumber: string;
    bankRoutingNumber: string;
    bankName: string;
    linkedBankAccountId: number;
    verificationStatus: AccountVerificationStatus;
    bankAccountName: string;
    linkedBankAccountType: string;
    hasPersonalContributionSchedules: boolean;
    isTokenizedAccountNumber: bool;
    apiErrorStatusCode: string;
    apiErrorMessage: string;
    apiErrorTimestamp: string;
    use: LinkedAccountUse;
    hasAccountOwnerVerification?: boolean;
    isDeleted?: boolean;
    dateLastModified?: string;
}

export interface LinkedAccountDisplayRow extends LinkedAccountApi {
    isEdit: boolean;
    isMissingUse: boolean;
    name: string;
}
export interface LinkedAccountUse {
    id: number;
    nickName: string;
    accountUse: LinkedBankAccountUse;
    status: LinkedBankAccountUseStatus;
}
export enum LinkedBankAccountUseStatus {
    Unspecified = "unspecified",
    Inactive = "inactive",
    Active = "active",
    Pending = "pending"
}
export const linkedBankAccountUseStatusDisplayMap: Record<LinkedBankAccountUseStatus, string> = {
    unspecified: "Unspecified",
    inactive: "Inactive",
    active: "Active",
    pending: "Pending"
};
export enum AccountVerificationStatus {
    None = "none",
    Pending = "pending",
    Verified = "verified",
    Failed = "failed",
    Expired = "expired",
    ConfirmationRequired = "confirmationRequired"
}
export const accountVerificationStatusDisplayMap: Record<AccountVerificationStatus, string> = {
    none: "None",
    pending: "Pending",
    verified: "Verified",
    failed: "Failed",
    expired: "Expired",
    confirmationRequired: "Confirmation Required"
};
export enum LinkedBankAccountUse {
    Unspecified = "unspecified",
    DirectDeposit = "directDeposit",
    ReimbursementDebitCard = "reimbursementDebitCard",
    VebaWithdrawals = "vebaWithdrawals",
    Other = "other",
}
export const bankAccountUseDisplayMap: Record<LinkedBankAccountUse, string> = {
    unspecified: "Pending Activation",
    directDeposit: "Direct Deposit",
    reimbursementDebitCard: "Debit Card",
    vebaWithdrawals: "VEBA Withdrawals",
    other: "HSA contributions"
};
export interface MicroEntryVerificationAccount {
    microDepositAccountVerificationId: number;
    finicityAccountId: string;
    accountName: string;
    accountLast4: string;
    verificationStatus: number;
}

export enum LinkedBankAccountVerificationStatus {
    None = 0,
    Pending = 1,
    Verified = 2,
    Failed = 3,
    Expired = 4
}

export enum MicroEntryVerificationAccountStatus {
    None = 0,
    Pending = 1,
    ReadyForVerification = 2,
    Verified = 3,
    Failed = 4,
    Deleted = 5,
}
export const microEntryVerificationAccountStatusMap: Record<MicroEntryVerificationAccountStatus, string> = {
    0: "None",
    1: "Pending",
    2: "Ready for Verification",
    3: "Verified",
    4: "Failed",
    5: "Deleted",
};
export interface LinkedAccountDefaultUpdateRequest {
    defaultContributionAccountId: undefined | number;
    defaultReimbursementAccountId: undefined | number;
}

export interface LinkedAccountsGetResponse {
    linkedBankAccounts: LinkedAccountApi[];
    verificationMethod: AchVerifier;
    defaultContributionAccountId: number;
    defaultReimbursementAccountId: number;
    defaultScheduledContributionAccountId: number;
    hasDirectDepositAccount: boolean;
    hasMissingUses: boolean;
    isPendingAccount: boolean;
    consumerBankAccountGetHttpStatusCode: string;
    consumerBankAccountGetErrorMessage: string;
    consumerBankAccountGetDateCreated: string;
}

export interface LinkedAccountManagementRequest {
    accountNumber: string;
    routingNumber: string;
    accountName: string;
    bankName: string;
    accountType: LinkedAccountType;
    emailAddress: string;
    cognitoUsername: string;
}

export interface LinkedAccountManagementResponse {
    outcome: string;
}


export interface LinkedCardsResponse {
    linkedCards: Card[];
}

export interface ExpenseBalanceSummary {
    likelyEligible: number;
    potentiallyEligible: number;
    unlikelyEligible: number;
    ineligible: number;
    reimbursed: number;
    reimbursedYtd: number;
    unreimbursed: number;
}

export interface ExpenseReimbursedSummaryResponse {
    yearly: {
        year: number,
        amount: number
    }[];
    monthly: ExpenseReimbursedMonthly[];
    fromDate: string;
    toDate: string;
    graphStartDate: string;
}

export interface ExpenseReimbursedMonthly {
    month: string;
    amount: number;
}

export interface PartnerBankHsaUserValidationResponse {
    validationMessages: string[];
}

export interface ReimburseMeState {
    cashTotal: number;
    unreimbursedTotal: number;
    fetchState: FetchState;
    page: number;
    pageSize: number;
    numberOfPages: number;
    numberOfReimbursements: number;
    records: Reimbursement[];
}

export interface ContactInfoState {
    contactInformation: ContactInformation;
    fetchStateKey: FetchStateKey;
    contactInformationState: FetchState;
    contactInformationValidationErrors: string[];
    userConfirmedInfo: boolean;
    mailingAddressValidated: boolean;
    showLobAddressSuggestionModal: boolean;
    lobAddressSuggestion: LobAddressValidationResponse;
}

export interface UserIdpProfileState {
    fetchState: FetchState;
    username: string;
    userProfile: UserAccountCredentialsResponse;
}

export interface OnboardingState {
    steps: UserOnboardingSteps[];
}

export interface FamilyMemberState {
    fetchState: FetchState;
    family: FamilyMembers;
    username?: string;
}

export interface FamilyMembers {
    [key: number]: FamilyMember;
}

export interface PendingBalance {
    amount: number;
}

export interface ExtendedContribution extends PayrollContribution {
    balance: number;
}

export interface ReimbursementRequest {
    amount: number;
    reimbursementLinkedAccountId: number;
    expenseIds?: number [];
}

export interface UpdateReimbursementRequest {
    reimbursementRequestId?: number;
    transactionId?: string;
    custodianTransactionId?: string;
    isBankRequestSuccess: boolean;
    description: string;
}

export interface ReimbursementRequestResponse {
    reimbursementRequestId: number;
}

export interface ReimburseBankRequest {
    amount: number;
    reimbursementLinkedAccountId: number;
}

export interface ReimburseBankResponse {
    amount: number;
    description: string;
    transactionId: string;
    custodianTransactionId: string;
}

export interface DayTradeSummaryResponse {
    trades: any[];
}

export interface FetchStateKey {
    username?: string;
    partnerId: number;
    accountId?: number;
}

export interface HsaDailyBalanceState {
    fetchState: FetchState;
    fetchKey: FetchStateKey;
    predictedDailyBalancesFetchState: FetchState;
    dailyBalances: HsaDailyBalance[];
    balanceOnFile: {
        fetchState: FetchState;
        asOfDate: string;
        amount: number;
    };
    dailyBalancesDictionary: {
        [key: number]: HsaDailyBalance[]
    };
    predictedDailyBalances: ExtendedContribution[];
    currentEndDateKey: string;
    dailyBalancesTransactionsDictionary: TransactionDictionary;
    accountStartDate: string;
    periodStartBalance: number;
    tooltipDate: string;
    instantBalance: {
        fetchState: FetchState;
        amount?: number;
        pendingAmount?: number;
        fetchKey: FetchStateKey;
        amountAsOfDate: string;
    };
    doesHsaAccountExist: boolean;
}

export interface HsaDailyBalance {
    balance: number;
    date: string;
    timestamp?: number;
    transactionSummaries?: TransactionSummary[];
}

export interface TransactionSummary {
    amount: number;
    description: string;
}

export interface HsaTotalState {
    fetchKey: FetchStateKey;
    userPayrollFetchState: FetchState;
    payrollSummary: PayrollSummary;
    earlyDepositCompleteDate: moment.Moment;
    showProjectedVsActualLinks: boolean;
    nextYear?: number;
    allowPayrollContributions: boolean;
    alwaysUseInitialAnnualContributionForTotalEmployeePayrollContributions: boolean;
    otherHsaAmountsByYear: OtherHsaByYear;
    otherHsaAmountsFetchState: FetchState;
    otherHsaAmountsFetchKey: FetchStateKey;
}

export interface InvestmentDailyBalanceState {
    records: BankDailyBalanceRecord[];
    dailyBalances: BankDailyBalanceRecord[];
    balanceOnFile: {
        fetchState: FetchState;
        fetchKey: FetchStateKey;
        asOfDate: string;
        amount: number;
        custodian: InvestmentCustodian;
        requestDates: { // the dates for which the daily balances were retrieved
            startDate: string,
            endDate: string
        }
    };
    dailyBalancesDictionary: {
        [key: number]: BankDailyBalanceRecord[]
    };
    balanceCache: {
        [key: string]: BankDailyBalanceRecord[]
    };
    mostRecentBalance: {
        fetchState: FetchState;
        fetchKey: FetchStateKey;
        amount: number;
        cashAmount: number;
        investedAmount: number;
        asOfDate: string;
    };
    currentEndDateKey: string;
    hasDailyBalances: boolean;
}

export interface InvestmentBalancesResponse {
    records: BankDailyBalanceRecord[];
    balanceType: string; // TODO: should this be the BalanceType enum?
    fromDate: string;
    toDate: string;
    hasBalances: boolean;
}

export interface PayrollSummary {
    hsaLimit: number;
    employerAmount: number;
    nextEligiblePayDate: moment.Moment;
    nextPayDate: moment.Moment;
    nextEligiblePayPeriodAmount: number;
    eligibilityStatus: EligibilityStatus;
    remainingPayPeriods: number;
    oneTimeLift: OneTimePayrollContribution;
    totalOneTimeAmount: number;
    totalPastRecurring: number;
    totalUpcomingRecurring: number;
    totalUpcomingOneTime: number;
    nextYearEmployerTotal: number;
    nextYearEmployeeTotal: number;
    priorYearHsaLimit?: number;
    priorYearTotalPayrollAmount?: number;
    hsaIrsLimitsByYear: HsaIrsLimitsByYear;
    personalScheduledContributionsByYear: PersonalScheduledContributionsByYear;
    nextPayrollFunding: EmployeePayrollFunding;
}

export interface EmployeePayrollFunding {
    payrollDate: string;
    employerAmount: number;
    employeeRecurringAmount: number;
    employeeOneTimeAmount: number;
}

export interface PayrollSummaryPersonalScheduledContributions {
    personalScheduledContributionsByYear: PersonalScheduledContributionsByYear;
}

export interface PayrollSummaryWithPriorEmployer extends PayrollSummary {
    otherEmployerAmount: number;
    otherEmployeeAmount: number;
}

export interface HsaIrsLimitsByYear {
    [year: number]: number;
}

export interface OtherHsaByYear {
    [year: number]: number;
}

export interface PersonalScheduledContributionsByYear {
    [year: number]: PersonalScheduledContribution[];
}

export interface PersonalScheduledContribution {
    amount: number;
    effectiveYear: number;
    isProjected: boolean;
}

export interface OneTimePayrollContribution {
    payDate: moment.Moment;
    amount: number;
    isOneTimeLiftLocked: boolean;
}

export enum EligibilityStatus {
    None = "none",
    NoAccount = "noAccount",
    Ineligible = "ineligible",
    Eligible = "eligible",
    Terminated = "terminated"
}

export enum BalanceType {
    Current = "current",
    Available = "available",
    AvailableCredit = "availableCredit",
    Investment = "investment"
}

export interface HsaState {
    fetchState: FetchState;
    fetchKey: FetchStateKey;
    transactionTotalFetchState: FetchState;
    transactionsDictionary: TransactionDictionary;
    reimbursementTransactionExpenses: BankTransactionReimbursement[];
    increasingTransactionsDictionary: TransactionDictionary;
    decreasingTransactionsDictionary: TransactionDictionary;
    contributionsDictionary: TransactionDictionary;
    transactionPageCount: number;
    increasingTransactionPageCount: number;
    decreasingTransactionPageCount: number;
    contributionsPageCount: number;
    page: number;
    contributionTotalsDictionary: ContributionTotalsDictionary;
    contributionsFilter: ContributionType;
}

export interface InvestmentHistoryState {
    fetchState: FetchState;
    fetchKey: FetchStateKey;
    historyPages: {
        [key: number]: InvestmentTransfer
    };
    historyPageCount: number;
    historyPage: number;
    historyTotalRecordCount: number;
    historyIsPolling: boolean;
}

export interface ContributionTotalsDictionary {
    [key: number]: ContributionTotalsEntry;
}

export interface ContributionTotalsEntry {
    totalAmountReimbursed: number;
    totalEmployerPayrollContributions: number;
    employeePreTaxContributions: number;
    employeePostTaxContributions: number;
    pendingEmployeePersonalContributions: number;
    actualEmployeePersonalContributions: number;
    bankDirectContributions: number;
    fetchState: FetchState;
}

export interface HsaLimitState {
    hsaLimitByYear: HsaLimitDictionary;
    fetchState: FetchState;
    fetchKey: FetchStateKey;
}

export interface HsaLimitDictionary {
    [key: number]: HsaLimit;
}

export interface HsaLimit {
    year: string;
    limit: number;
    otherHSAAmount: number;
}

export interface ContributionLimitResponse {
    fetchState: FetchState;
    contributionLimits: HsaLimit[];
}

interface PagedResponse {
    page: number;
    numberOfPages: number;
    totalNumberOfRecords: number;
}

export interface TransactionResponse extends PagedResponse {
    records: Transaction[];
}

export enum AccountCancelUploadStatus {
    None = "none",
    Pending = "pending",
    Completed = "completed",
    Failed = "failed"
}

export interface BulkLiquidationState {
    fileUploadFetchState: FetchState;
    getFetchState: FetchState.Uninitialized;
}

export enum DrivewealthLiquidationFileUploadStatus {
    None = "none",
    Pending = "pending",
    LiquidationInitiated = "liquidationInitiated",
    PartialSuccess = "partialSuccess",
    Success = "success",
    Failed = "failed"
}

export interface DrivewealthAccountCancelUploadListPage extends PagedResponse {
    records: DrivewealthAccountCancelUploadModel[];
}

export interface DrivewealthAccountCancelUploadModel {
    drivewealthAccountCancelUploadId: number;
    fileName: string;
    dateUploaded: string;
    uploadStatus: AccountCancelUploadStatus;
    updateType: AdminBulkUpdateTypes;
    numberOfRecords: number;
    completedCount: number;
    failedCount: number;
}

export interface BulkLiquidationUploadListPage extends PagedResponse {
    records: BulkLiquidationUploadModel[];
}

export interface BulkLiquidationUploadModel {
    drivewealthLiquidationFileUploadId: number;
    dateCreated: string;
    dateLastModified: string;
    uploadStatus: DrivewealthLiquidationFileUploadStatus;
    byCognitoUsername: string;
    fileName: string;
    fileNameGuid: string;
    recordCount: number;
    downloadUrl: string;
}

export interface ChartDataItem {
    balance: number;
    netContribution: number;
    date: string;
    timestamp?: number;
    transactionSummaries: { amount: number, description: string }[];
}

export enum DrivewealthAccountPerformanceTimePeriod {
    YearToDate = "1",
    Months1 = "2",
    Months3 = "3",
    Months6 = "4",
    Years1 = "5",
    Max = "6",
}

export interface TrailingReturns {
    trailing1DayReturn: number;
    trailing1MonthReturn: number;
    trailing1WeekReturn: number;
    trailing1YearReturn: number;
    trailing3MonthReturn: number;
    trailing3YearReturn: number;
    trailing5YearReturn: number;
}

export interface DrivewealthAccountPerformanceSelectOption {
    text: string;
    value: DrivewealthAccountPerformanceTimePeriod;
    firstValidDate: Moment;
}

export interface DrivewealthAccountPerformanceState {
    selectedTimePeriod: DrivewealthAccountPerformanceTimePeriod;
}

export interface UserInvestmentFeeAcknowledgementState {
    fetchState: FetchState;
    postFetchState: FetchState;
    signedAcknoledgements: UserInvestmentFeeAcknowledgementResponse;
}

export interface UserInvestmentFeeAcknowledgementResponse {
    signedChoiceFee: boolean;
}

export interface DrivewealthAccountIdentifierArgs {
    username: string;
    partnerId: number;
    accountId: number;
}

export interface DrivewealthAccountPerformanceRequestArgs extends DrivewealthAccountIdentifierArgs {
    timePeriod: DrivewealthAccountPerformanceTimePeriod;
}

export interface ChartPoint {
    value: number;
    date: string;
}

export enum ChartPointDataType {
    Number,
    Currency,
    Date
}

export interface DrivewealthAccountPerformance {
    accountId: string;
    accountNumber: string;
    startDate: string;
    endDate: string;
    lastUpdated: string;
    beginningMarketValue: number;
    beginningPositionBalance: number;
    beginningCashBalance: number;
    endingMarketValue: number;
    endingPositionBalance: number;
    endingCashBalance: number;
    periodGainLoss: number;
    periodCashFlows: number;
    periodDeposits: number;
    periodWithdrawals: number;
    periodFees: number;
    periodIncome: number;
    periodReturn: number;
    mtdReturn: number;
    ytdReturn: number;
    return1Year: number;
    return3Year: number;
    return5Year: number;
    inceptionReturn: number;
    performanceReturns: DrivewealthPerformanceReturn[];
    emptyDataSetMessage: string;
}

export interface DrivewealthPerformanceReturn {
    date: string;
    gainLoss: number;
    positionBalance: number;
    cashBalance: number;
    marketValue: number;
    cashFlows: number;
    deposits: number;
    withdrawals: number;
    fees: number;
    income: number;
    rateOfReturn: number;
}

export interface InvestmentBalance {
    date: string;
    balance: number;
}

export interface DrivewealthAccountRateOfReturnModel {
    emptyDataSetMessage: string;
    ytdRateOfReturn: number;
    mtdRateOfReturn: number;
    oneMonthRateOfReturn: number;
    threeMonthRateOfReturn: number;
    sixMonthRateOfReturn: number;
    oneYearRateOfReturn: number;
    inceptionRateOfReturn: number;
}

export interface InvestmentAccountState {
    ssoUrl: string;
    ssoBody: string;
    ssoRelayState: string;
    ssoFetchState: FetchState;
    createUrl: string;
    createBody: string;
    createFetchState: FetchState;
}

export interface InvestmentAccountSsoResponse {
    url: string;
    body: string;
    relayState: string;
}

export interface InvestmentAccountCreateResponse {
    url: string;
    body: string;
}

export interface InvestmentHistory extends PagedResponse {
    records: InvestmentTransfer[];
}

export interface InvestmentTransfer {
    amount: number;
    transferType: InvestmentTransferDirection;
    status: string;
    dateCreated: string;
}

export interface InvestmentProfile {
    cognitoUsername: string;
    accountNumber: string;
    investmentCustodian: InvestmentCustodian;
    dateAccountInactivated: string;
}

export interface RecentBalanceResponse {
    recentBalance: number;
}

export interface TransactionDictionary {
    [key: number]: Transaction[];
}

export interface Transaction {
    id?: string;
    postedDate: string;
    source: TransactionSource;
    displaySource: string;
    description: string;
    amount: number;
    moneyIncreasing: boolean;
    displayDescription: string;
    status: TransactionApiStatus;
    displayStatus: string;
    linkedBankAccount: TransactionLinkedBank;
}

export enum TransactionApiStatus {
    None = "none",
    Complete = "complete",
    Rejected = "rejected",
    Processing = "processing"
}

export interface TransactionLinkedBank {
    accountLast4: string;
    name: string;
}

export enum TransactionSource {
    None = "none",
    PayrollEmployer = "payrollEmployer",
    PayrollRecurring = "payrollRecurring",
    PayrollOneTime = "payrollOneTime",
    Reimbursement = "reimbursement",
    DebitCard = "debitCard",
    // haven't found this value anywhere service side, so i don't know where this is coming from
    PendingActivity = "pendingActivity",
    PersonalContribution = "personalContribution",
    PayrollPostTax = "payrollPostTax",
    Fee = "fee",
    Investment = "investment",
    Interest = "interest",
    InterestReversal = "interestReversal",
    Transfer = "trusteeTransfer",
    Contribution = "contribution",
    Disbursement = "disbursement",
    PennyDeposit = "pennyDeposit"
}

export enum TransactionStatus {
    Processing,
    Contributed,
    Reimbursed,
    HSADebitCard,
    Transferred,
    Fee,
    Rejected,
    Complete,
    UnKnown,
    Disbursed,
    Adjustment
}

export interface PayrollContribution {
    date: string;
    employerAmount: number;
    employeeRecurringAmount: number;
    employeeOneTimeAmount: number;
    total: number;
}

export interface ContactInformation {
    firstName: string;
    middleInitial: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    mobilePhoneNumber: string;
    mailingAddress: Address;
    physicalAddress: Address;
    isMailingSameAsPhysical: boolean;
    lastFourSsn: string;
    memberNumber: string;
    dateOfBirth: string;
    lastLogin: string;
    username: string;
    maritalStatus?: MaritalStatus;
    fieldEditSettings?: EmployerEmployeeProfileSettings;
    mothersMaidenName?: string;
    employmentInfo?: EmploymentInfo;
    rowVersion?: number;
    termsIdentifier?: string;
    termsSignedDate?: string;
}

export interface EmploymentInfo {
    dateOfHire: string;
    payrollFrequency: string;
    payrollFrequencyEffectiveDate: string;
    employeeClassification: string;
    employeeClassificationEffectiveDate: string;
    employerDivision: string;
}

export interface EmployerEmployeeProfileSettings {
    emailAddress: EmployerEmployeeProfileSettingValue;
    mailingAddress: EmployerEmployeeProfileSettingValue;
    homePhone: EmployerEmployeeProfileSettingValue;
    mobileNumber: EmployerEmployeeProfileSettingValue;
    maritalStatus: EmployerEmployeeProfileSettingValue;
    hsaCoverageLevel: EmployerEmployeeProfileSettingValue;
}

export enum EmployerEmployeeProfileSettingValue {
    Display = "display",
    Edit = "edit"
}

export enum MaritalStatus {
    Undefined = "undefined",
    Married = "married",
    Single = "single"
}

export const maritalStatusDisplayMap: Record<MaritalStatus, string> = {
    undefined: "",
    married: "Married",
    single: "Single"
};

export interface Address {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zip: string;
    isUndeliverable?: boolean;
}

export interface LobAutoCompleteAddressRequest {
    address: string;
    city: string;
    state: string;
    zip: string;
    properCase: boolean;
    validSuggestions: boolean;
    geoIpSearch: boolean;
}

export interface EnrollmentPii {
    ssn: string;
    dateOfBirth: string;
    emailAddress: string;
    zip: string;
}

export interface EnrollmentState {
    fetchState: FetchState;
    firstName: string;
    employerName: string;
    isClaimed: boolean;
    isEnrollmentDisabled: boolean;
    isSelfReportedEmployeeAndUnclaimedState: boolean;
    isEnrolled: boolean;
    claimLinkId: string;
    email: string;
    isClaiming: boolean;
    emailVerify: boolean;
    wasClaimedAlready: boolean;
    emailAvailability: {
        isAvailable: boolean,
        email: string,
        isConfirmInProgress: boolean
    };
    feeScheduleState: FeeScheduleState;
    previouslyEnteredPii?: EnrollmentPii;
    feeWaiverFetchState: FetchState;
    feeWaiver: PartnerFeeWaiver;
    displayLoginMessage: boolean;
    hasExistingAccount: boolean;
    hasAccountWithOtherEmployer: boolean;
}

export interface FeeScheduleState {
    fetchState: FetchState;
    feeSchedule: FeeSchedule;
}

export interface EnrollmentVerifyRequest {
    dateOfBirth: string;
    last4Ssn: string;
    email: string;
    claimCode: string;
    password: string;
}

export interface PersonaState {
    fetchState: FetchState;
    persona: string;
}

export interface FamilyMember {
    id: number;
    firstName: string;
    middleInitial?: string;
    lastName: string;
    fullName: string;
    tag: string;
    dateOfBirth: string;
    debitCardHolder: boolean;
    ssn: string;
    relationship: FamilyMemberRelationship;
    isStudent?: boolean;
    status: FamilyMemberStatus;
    externalId?: string;
}

export interface FamilyUpdateRequest {
    members: FamilyMember[];
}

export interface PlanDetails {
    planType: PlanType;
    effectiveDate: string;
    healthPlanEffectiveDate?: string;
    otherHsaAmount: number;
    enrollHsa: boolean;
}

export interface ContributionState {
    fetchState: FetchState;
    fetchKey: FetchStateKey;
    updateRecurringEmployeeContributionFetchState: FetchState;
    contribution: ContributionMisc;
    allowPriorYearContribution: boolean;
    allowPriorFetchState: FetchState;
    planDetails: PlanDetails;
    planDetailsFetchState: FetchState;
    otherHsaPriorYearCutoffDate: Date;
    otherHsaPriorYearCutoffDateFetchState: FetchState;
}

export interface OtherHsaAmountModel {
    year: number;
    amount: number;
}

export interface ContributionMisc {
    annualPreTaxContributionAmount: number;
    annualPostTaxContributionAmount: number;
    perPayPeriodAmount: number;
    otherHsaContributionAmount: number;
    payrollCycle: string;
    coverageType: string;
    noOfPayPeriods: number;
    employerContributionAmount: number;
    planContributionLimit: number;
    hsaEffectiveDate: string;
    isEnrollmentInDecember: boolean;
    planContributionLimitForDecEnrollment: number;
    nextEffectivePayrollDate: string;
    employerContributionAmountForDecEnrollment: number;
    earlyDepositCompleteDate: string;
    upcomingPayDates: string[];
}

export interface UpdateRecurringContribution {
    amount: number;
    date: string;
}

export interface PendingContributions {
    contributions: PendingContributionItem[];
}

export interface PendingContributionItem {
    amount: number;
    displaySource: string;
    displayDescription?: string;
    transactionDate: string;
    transactionType: string;
    linkedBankAccount: TransactionLinkedBank;
}

export interface PendingReimbursement {
    pendingReimbursements: PendingReimbursementItem[];
    count: number;
}

export interface PendingReimbursementItem {
    amount: number;
    description: string;
    bankAccountNumber: string;
    datePending: string;
}

export interface OnboardingWizard {
    statuses: OnboardingWizardStatus[];
}

export { UserOnboardingSteps };
export { UserOnboardingStepKey };

export interface OnboardingWizardStatus {
    complete: boolean;
    step: UserOnboardingSteps;
    order: number;
    stepKey: UserOnboardingStepKey;
    label: string;
}

export interface WizardState {
    onboardingWizardState: FetchState;
    onboardingWizard: OnboardingWizard;
    transitionToEmployee: boolean;
    stepsCompleting: UserOnboardingSteps[];
    allowDrivewealthPreEnrollment: boolean;
    showUnsavedChangesPrompt: boolean;
}

export enum TransactionFilter {
    None = 0,
    Increasing = 1,
    Decreasing = 2
}

export enum TermsTab {
    Bank = 0,
    Bend = 1,
    FeeSchedule = 2,
    DownloadForms = 3
}

export enum UserTermsType {
    Wizard = 0,
    Bulk = 1,
    Prompt = 2,
}

export interface LoginPromptOrchestratorState {
    acceptedTerms: TermsAndConditionsModel[];
    acceptedTermsFetchState: FetchState;
    promptToDisplay: LoginPromptToDisplay;
    isTermsSignatureRequired: boolean;
    isMfaPromptRequired: boolean;
    calculationsComplete: boolean;
}

export enum LoginPromptToDisplay {
    None,
    Terms,
    Mfa
}

export interface TermsAndConditionsModel {
    termsIdentifier: string;
    termsType: UserTermsType;
}

export interface PayrollUpdateResponse {
    resultCode: PayPeriodUpdateResultCode;
    earlyDepositCompleteDate: string;
}

export interface OneTimeliftUpdateResponse {
    resultCode: PayPeriodUpdateResultCode;
}

export enum PayPeriodUpdateResultCode {
    None = "none",
    MissedDeadline = "missedDeadline",
    UnrecognizedPayDate = "unrecognizedPayDate",
    OverLimit = "overLimit",
    OneTimeAmountAutoDecreasedToLimit = "oneTimeAmountAutoDecreasedToLimit",
    IneligiblePayDate = "ineligiblePayDate"
}

export interface TransactionTotalsResponse {
    totalSpent: number;
    taxSavings: number;
    totalEmployerPayrollContributions: number;
    employeePreTaxContributions: number;
    employeePostTaxContributions: number;
    pendingEmployeePersonalContributions: number;
    actualEmployeePersonalContributions: number;
    bankDirectContributions: number;
    year: number;
}

export enum InvestmentCustodian {
    None = "none",
    Umb = "umb",
    Tda = "tda",
    Drivewealth = "drivewealth",
    HsaBank = "hsaBank"
}

export enum FeeCollectionType {
    HsaAccount = "hsaAccount",
    InvestmentAccount = "investmentAccount",
    Partner = "partner",
    Employer = "employer",
    HsaBank = "hsaBank"
}

export interface FeeCollectionModel {
    feeCollectionAccountType: FeeCollectionType;
}

export interface PartnerPortalState {
    statusFetchState: FetchState;
    configFetchState: FetchState;
    status: PartnerPortalStatus;
    config: PartnerPortalSingleSignOnConfig;
}

export interface PartnerPortalStatus {
    isAllowed: boolean;
  hasActiveHsaAccount: boolean;
  hasActiveNonHsaAccount: boolean;
}

export interface PartnerPortalSingleSignOnConfig {
    action: string;
    value: string;
}

export interface Expense {
    id?: number;
    name: string;
    amount: number;
    reimbursedAmount: number;
    transactionAmount: number;
    comment: string;
    expensePaid: boolean;
    recurringType: ExpenseRecurringType;
    expenseCategory: ExpenseCategory;
    eligibilityStatus: ExpenseEligibility;
    attachmentNames: string[];
    expenseEvidenceAttachments: any[];
    institutionName?: string;
    dateOfService: string;
    isManualExpense: boolean;
    isPayToProvider?: boolean;
    providerName?: string;
    providerMemberNumber?: string;
    providerAddress1?: string;
    providerAddress2?: string;
    providerCity?: string;
    providerStateCode?: string;
    providerZip?: string;
    wexAccountType?: WexExpenseAccountType;
    wexExpenseCategory?: string;
    wexExpenseType?: string;
    wexClaimModel?: WexClaim;
    numberOfMiles?: number;
    mileageDollarValue?: number;
}
export interface ExpenseResponse {
    id?: number;
    name: string;
    amount: number;
    reimbursedAmount: number;
    transactionAmount: number;
    comment: string;
    expensePaid: boolean;
    recurringType: ExpenseRecurringType;
    expenseCategory: ExpenseCategory;
    eligibilityStatus: ExpenseEligibility;
    attachmentNames: string[];
    expenseEvidenceAttachments: any[];
    institutionName?: string;
    dateOfService: string;
    isManualExpense: boolean;
    isAnyAttachmentInFailedStatus: boolean;
    expenseEvidence: HsabExpenseEvidenceResponse[];
    isSubmitted: boolean;
}
export interface ProviderTypeAheadResponse {
  providers: ProviderLookupItem[];
}
export interface ProviderLookupItem {
    providerName: string;
    address1: string;
    address2: string;
    city: string;
    stateCode: string;
    zipCode: string;
    isPayToPayee: boolean;
    expenseCategory: HsabExpenseCategory;
    expenseType: string;
    expenseSubType: string;
    expenseSubTypeDisplayName: string;
    accountNumber: string;
}
export interface HsabPayee {
    payeeId?: number;
    userId?: number;
    payeeName: string;
    memberAccountNumber?: string;
    address1: string;
    address2?: string;
    city: string;
    stateCode: string;
    zip: string;
}

export interface HsabPayeeLastActive {
    lastActiveDate: string;
    payee: HsabPayee;
}

export interface ExpenseEvidenceUpdateParams {
    expenseId: number;
    transactionType: ExpenseEligibilityGroup;
    expenseEvidenceNames: any[];
    isDelete: boolean;
}

export interface ExpenseEvidenceStoreState {
    evidenceByExpenseId: { [expenseId: number]: HsabExpenseEvidenceResponse[] };
    evidenceByExpenseIdFetchState: { [expenseId: number]: FetchState };
    evidenceByIdFetchState: { [expenseEvidenceId: number]: FetchState };
}

export enum WexUploadStatus {
    Unknown = "unknown",
    NotUploaded = "notUploaded",
    Success = "success",
    Failed = "failed",
}
export const WexUploadStatusMap: Record<WexUploadStatus, string> = {
    unknown: "N/A",
    notUploaded: "Not Uploaded",
    success: "Success",
    failed: "Failed"
};
export enum ExpenseRecurringType {
    OneTime = "oneTime",
    Recurring = "recurring"
}

export interface ExpenseRequest {
    pageNumber: number;
    pageSize: number;
    expiryPage: boolean;
    eligibilityStatus: ExpenseEligibility;
    groups: ExpenseEligibilityGroup[];
}

export interface ExpenseEditResponse {
    expense: Expense;
}

export interface ExpenseEditRequest {
    transactionId: number;
    name: string;
    amount: number;
    comment: string;
    expensePaid: boolean;
    eligibilityStatus: ExpenseEligibility;
    recurringType: ExpenseRecurringType;
    expenseCategory: ExpenseCategory;
    dateOfService: string;
    isPayToProvider?: boolean;
    providerName?: string;
    providerMemberNumber?: string;
    providerAddress1?: string;
    providerAddress2?: string;
    providerCity?: string;
    providerStateCode?: string;
    providerZip?: string;
    wexAccountType?: WexExpenseAccountType;
    wexExpenseCategory?: string;
    wexExpenseType?: string;
    numberOfMiles?: number;
    mileageDollarValue?: number;
}

export interface ExpenseEditParams {
    transactionId: number;
    expense: Expense;
    expenseType: ExpenseEligibilityGroup;
    isEligibleAmountChanged?: boolean;
    expenseLinkRequest?: ExpenseLinkRequest;
    hasLinkedClaim?: boolean;
}

export interface UpdateExpensesEligibilityStatusResponse {
    expenses: EligibilityStatusExpense[];
}

export enum ExpenseEligibilityGroupName {
    None = "none",
    LikelyEligible = "likelyEligible",
    PotentialEligible = "potentialEligible",
    UnlikelyEligible = "unlikelyEligible"
}

export interface EligibilityStatusExpense {
    expenseId: number;
    transactionId: number;
    name: string;
    amount: number;
    reimbursedAmount: number;
    comment: string;
    expensePaid: boolean;
    recurringType: ExpenseRecurringType;
    expenseCategory: ExpenseCategory;
    transactionEligibility: ExpenseEligibilityGroupName;
    dateOfService: string;
    transactionAmount: number;
    isManualExpense: boolean;
}

export interface ExpenseAttachmentParams {
    attachmentName: string;
    transactionType?: ExpenseEligibilityGroup;
    expense: Expense;
}

export interface UnreimburseExpense extends Expense {
    isSelected: boolean;
    transactionId: number;
}

// export interface AvailableExpense extends ExpenseResponse {
//     isSelected: boolean;
// }
//
// export interface SubmittedExpense extends ExpenseResponse {
//     isSelected: boolean;
//     wexClaimModel: WexClaim;
// }

export interface Reimbursement {
    id: number;
    transactionId: number;
    reimbursementAmount: number;
    createdDate: string;
    numberOfExpenses: number;
    expenseTotal: number;
    isReimbursementPosted: boolean;
    status: ReimbursementStatusType;
    isSelected: boolean;
    reimbursementType: ReimbursementType;
    description: string;
    childExpenseLoaded: boolean;
    childExpenses: Expense[];
    dateReimbursed: string;
}

export enum ReimbursementType {
    None = "none",
    Ach = "ach",
    DebitCard = "debitCard",
    Disbursement = "disbursement"
}

export enum ReimbursementStatusType {
    None = "none",
    Pending = "pending",
    Complete = "complete",
    Failed = "failed",
    InProcess = "inProcess"
}

export interface UnreimbursedPage {
    page: number;
    force: boolean;
}

export interface ExpenseReimbursedSummaryYearDataPoint {
    year: number;
    amount: number;
}

export interface ExpenseReimbursedSummaryMonthDataPoint {
    month: moment.Moment;
    amount: number;
}

export interface ExpenseReimbursedSummaryState {
    fetchState: FetchState;
    yearlyDataPoints: ExpenseReimbursedSummaryYearDataPoint[];
    monthlyDataPoints: ExpenseReimbursedSummaryMonthDataPoint[];
    graphStartDate: string;
}

export interface ExpenseUnreimburseState {
    fetchState: FetchState;
    countFetchState: FetchState;
    transactionFetchState: FetchState;
    attachmentsCountFetchState: FetchState;
    page: number;
    pageSize: number;
    numberOfPages: number;
    records: UnreimburseExpense[];
    unreimbursedExpensesCount: number;
    unreimbursedWithoutAttachmentsCount: number;
    graph: UnreimbursedGraphResponse;
    showExpensesWithoutAttachments: boolean;
}

export interface ExpenseReimburseState {
    fetchState: FetchState;
    transactionFetchState: FetchState;
    page: number;
    pageSize: number;
    expenseTransactionDictionary: ExpenseTransactionDictionary;
    numberOfPages: number;
    numberOfReimbursements: number;
    records: Reimbursement[];
}

export interface WexClaim {
    wexClaimId: number;
    wexClaimNumber: string;
    wexFlowId: string;
    planName: string;
    planYearName: string;
    approvedAmount: number | null;
    deniedAmount: number | null;
    appliedDeductibleAmount: number | null;
    appliedCoinsuranceAmount: number | null;
    appliedCopayAmount: number | null;
    receiptStatus: WexReceiptStatus;
    claimReceiptStatus: HsabExpenseDisplayStatus;
    isSuspectedDuplicate: boolean;
    totalClaimAmount: number;
    paidAmount: number;
    planYearFinalFilingDate: string | null;
    scheduledForReimbursementDate: string | null;
    denialReasons: WexClaimDenialReason[];
}
export interface WexClaimDenialReason {
  id: number;
  denialCode: string;
  reason: string;
  isUploadReceiptAllowed: boolean;
  denialDate: string;
}
export enum WexClaimStatus {
    None = "none",
    NA = "nA",
    PendingReimbursementStatus = "pendingReimbursementStatus",
    HoldStatus = "holdStatus",
    PaidStatus = "paidStatus",
    PendingReceiptStatus = "pendingReceiptStatus",
    DeniedStatus = "deniedStatus",
}
export const WexClaimStatusMap: Record<WexClaimStatus, string> = {
    none: "N/A",
    nA: "N/A",
    pendingReimbursementStatus: "Pending",
    holdStatus: "Hold",
    paidStatus: "Paid",
    pendingReceiptStatus: "Pending Receipt",
    deniedStatus: "Denied"
};
export enum WexReceiptStatus {
    None = "none",
    ReceiptNotNeeded = "receiptNotNeeded",
    ReceiptRequired = "receiptRequired",
    ReceiptReceived = "receiptReceived",
    ReceiptAvailable = "receiptAvailable",
    ReceiptOverdue = "receiptOverdue",
    ReceiptNewNeeded = "receiptNewNeeded",
    ReceiptUploaded = "receiptUploaded",
    ReceiptReUploaded = "receiptReUploaded",
    NA = "nA",
    ReceiptPending = "receiptPending",
}

export enum HsabExpenseDisplayStatus {
    None = "none",
    Active = "active",
    Applied = "applied",
    Available = "available",
    Cancelled = "cancelled",
    Completed = "completed",
    Denied = "denied",
    Error = "error",
    Expired = "expired",
    Hold = "hold",
    Inactive = "inactive",
    Locked = "locked",
    MarkedAsPaid = "markedAsPaid",
    NewReceiptNeeded = "newReceiptNeeded",
    Paid = "paid",
    PartiallyPaid = "partiallyPaid",
    Pending = "pending",
    PendingReceipt = "pendingReceipt",
    PendingReimbursement = "pendingReimbursement",
    ReceiptOverdue = "receiptOverdue",
    ReceiptNeeded = "receiptNeeded",
    Repaid = "repaid",
    RepaymentDue = "repaymentDue",
    Resubmitted = "resubmitted",
    ScheduledReimbursement = "scheduledReimbursement",
    Submitted = "submitted",
    Unsubmitted = "unsubmitted",
}

export const HsabExpenseDisplayStatusMap: Record<HsabExpenseDisplayStatus, string> = {
    none: "N/A",
    active: "Scheduled",
    applied: "Applied",
    available: "Available",
    cancelled: "Cancelled",
    completed: "Completed",
    denied: "Denied",
    error: "Error",
    expired: "Filing period closed",
    hold: "On hold",
    inactive: "Inactive",
    locked: "Locked",
    markedAsPaid: "Marked as paid",
    newReceiptNeeded: "New receipt needed",
    paid: "Paid",
    partiallyPaid: "Partially paid",
    pending: "Pending",
    pendingReceipt: "Pending receipt",
    pendingReimbursement: "Payment pending",
    receiptNeeded: "Receipt needed",
    receiptOverdue: "Receipt overdue",
    resubmitted: "Resubmitted",
    repaid: "Repaid",
    repaymentDue: "Repayment due",
    submitted: "Submitted",
    scheduledReimbursement: "Payment scheduled",
    unsubmitted: "Unsubmitted",
};
export interface HsabExpenseState {
    wexSyncStatus: boolean;
    wexSyncFetchState: FetchState;
    expenseTypesAndSubtypesByCategories: {
        [category: string]: HsabExpenseCategoryResponse;
    };
    expenseTypesAndSubtypesByCategoriesFetchState: FetchState;
    expensesByIdFetchStateDictionary: { [hsabExpenseId: number]: FetchState };
    expensesByIdDictionary: { [hsabExpenseId: number]: HsabExpenseResponse };
    expensesByExternalIdFetchStateDictionary: { [externalId: string]: FetchState };
    expensesByExternalIdDictionary: { [externalId: string]: HsabExpenseResponse };
    amountReimbursedToday: number;
    dailyReimburseMeLimit: number;
    providerSearchTypeAheadResult: ProviderTypeAheadResponse;
    providerSearchTypeAheadFetchState: FetchState;
    loadExpenseCategoriesRequest: Promise<any>;
}

interface PagedRecordsResult {
    page: number;
    pageSize: number;
    numberOfPages: number;
    totalRecords: number;
}

export interface ExpensePagedResponse extends PagedRecordsResult {
    records: Expense[];
}

export interface HsabExpensePagedResponse extends PagedRecordsResult {
    records: HsabExpenseResponse[];
}

export interface ManagedExpensePagedResponse extends PagedRecordsResult {
    records: ManagedExpenseResponse[];
    filterOptions: ManagedExpenseFilterOptions;
}

export interface ManagedExpenseFilterOptions {
    categories: HsabExpenseCategory[];
    years: number[];
}

export interface HsabExpenseEvidenceShoeboxPagedResponse extends PagedRecordsResult {
    records: HsabExpenseEvidenceShoeboxResponse[];
}
export interface TalonClaimExpenseStatuses  {
    expenseId: number;
    status: HsabExpenseDisplayStatus;
}

export interface ExpenseUnreimburseGetResponse {
    records: UnreimburseExpense[];
    page: number;
}

export interface ReimbursementGetResponse {
    records: Reimbursement[];
    page: number;
    numberOfPages: number;
    numberOfReimbursements: number;
}

export interface ExpenseTransactionDictionary {
    [key: number]: Expense[];
}

export interface ReimbursementExpensesGetResponse {
    appliedExpenses: Expense[];
    id: number;
    createdDate: string;
    numberOfExpenses: number;
    expenseTotal: number;
    IsReimbursementPosted: boolean;
    Status: ReimbursementStatusType;
}

export interface BankTransactionReimbursement extends ReimbursementExpensesGetResponse {
    BankTransactionId: number;
}

export interface ReimbursementExpensesByBankTransactionsGetResponse {
    reimbursements: BankTransactionReimbursement[];
}

export interface UnreimbursedGraphPoint {
    amount: number;
    date: string;
}

export interface UnreimbursedGraphResponse {
    fromDate: string;
    toDate: string;
    graphStartDate: string;
    graphEndDate: string;
    records: UnreimbursedGraphPoint[];
}

export interface BankProfile {
    accountNumber: string;
    routingNumber: string;
}

export interface UserPreferences {
    paperStatement: boolean;
}

export interface PreferencesState {
    fetchState: FetchState;
    usePaperStatements: boolean;
}

export interface PersonalContributionRequest {
    amount: number;
    isPriorYear: boolean;
    linkedAccountId: number;
}

type QueryStringParameters = Record<string, string | (string | null)[]>;

export interface IndividualSignup {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    fetchState: FetchState;
    hsaContributionLimits: HsaContributionLimits;
    partnerId: number;
    isEmployeeSignup: boolean;
    isEmailVerified: boolean;
    bendUserId: string;
    signature: string;
    enrollmentReferralCodes?: QueryStringParameters;
    healthPlanType: HealthPlanType;
    hasHsaWithAnotherProvider: boolean;
}

export interface IndividualEmailVerifyRequest {
  email: string;
  verificationCode: string;
}

export interface IndividualEmailVerifyResponse {
    bendUserId: string;
    signature: string;
}

export interface IndividualLoginAccountRequest {
    bendUserId: string;
    signature: string;
    username: string;
    password: string;
}

export interface ExpenseByCategoryState {
    expenseByCategoryDictionary: ExpenseByCategoryDictionary;
    currentSelectedKey: string | number;
    fetchState: FetchState;
}

export interface ExpenseByCategoryDictionary {
    [key: string]: ExpenseAmountGroupedByCategory;
}

export interface ExpenseAmountGroupedByCategory {
    expenseAmountByCategories: ExpenseAmountByCategory[];
    year?: number;
    totalExpenseAmount: number;
}

export interface ExpenseAmountByCategory {
    category: ExpenseCategory;
    amount: number;
}

export interface BankDailyBalanceRecord {
    balance: number;
    date: string;
    transactionSummaries?: TransactionSummary[];
    timestamp?: number;
}

export interface CurrentInvestmentBalance {
    balance: number;
    date: string;
    cashPositionBalance?: number;
    investedBalance?: number;
}

export interface CurrentInvestmentBalances {
    tda?: CurrentInvestmentBalance;
    drivewealth?: CurrentInvestmentBalance;
    umb?: CurrentInvestmentBalance;
}

export interface InvestmentDailyBalanceRecord extends BankDailyBalanceRecord {
    investmentCustodian: InvestmentCustodian;
    hasInvestmentAccount: boolean;
    hsaInvestmentThreshold: number;
    investedBalance?: number;
    cashPositionBalance?: number;
}

export interface BankDailyBalancePage {
    records: BankDailyBalanceRecord[];
}

export interface BankTransactionDailyBalancePage {
    records: BankDailyBalanceRecord[];
    accountStartDate: string;
    periodStartBalance: number;
}

export interface BankDailyBalanceRequest {
    startDate: string;
    endDate: string;
    username?: string;
    partnerId?: number;
    accountId?: number;
}

export interface InvestmentDailyBalanceRequest extends BankDailyBalanceRequest {
    investmentCustodian?: InvestmentCustodian;
}

export interface ImportedExpense {
    importedExpenseId: number;
    amount: number;
    amountRemaining: number;
    cognitoUsername: string;
    expenseCategory: string;
    expenseType: string;
    dateOfService: string;
    name: string;
    importedExpenseReimbursements: ImportedExpenseReimbursements[];
    fileNames: string[];
    source: string;
}

export interface ImportedExpenseReimbursements {
    importedExpenseReimbursementId: number;
    dateOfReimbursement: string;
    amount: number;
}

export interface CardWidgetTab {
    title: string;
    disabled: boolean;
    visible: boolean;
}

export interface PartnerBilling {
    invoicing: string;
    contractOwner: string;
}

export interface IndividualFee {
    monthlyFeeAmount: number;
    effectiveStartDate: string;
    feeWaiverType: FeeWaiverType;
    feeWaiverThreshold: number;
}

export interface PageSizeState {
    pageSize: number;
}

export interface PageSizeSelectOption {
    value: number;
    text: string;
}

export type InvestmentTransferDirection = "toInvestment" | "fromInvestment";

export interface InvestmentTransferRequest {
    amount: number;
    transferType: InvestmentTransferDirection;
}

export interface EmployerSuspension {
    employerId: number;
    isSuspended: boolean;
    dateCreated: string;
    reason: string;
}

export interface AchContributionState {
    fetchState: FetchState;
    records: AchContributionRecord[];
    pages: {
        [key: string]: AchContributionRecord[]
    };
    totalRecords: number;
    pageNumber: number;
    selectedAchId: string;
    selectedPayrollDate: string;
}

export interface AchContributionRecord {
    employerAchId: string;
    payrollDate: string;
    effectiveYear: string;
    achDate: string;
    achAmount: number;
    isDisbursalComplete: boolean;
    hasHoldOverAmount: boolean;
    EmployeeCount: number;
    holdoverAmount: number;
    hasMultiple: boolean;
    achDetails: AchFileDetail [];
}

export interface HoldOverAmount {
    processedDate: string;
    holdAmount: number;
    hasMultiple: boolean;
    achDetails: AchFileDetail [];
}

export interface AchFileDetail {
    processDate: string;
    userCount: string;
    fileAchAmount: number;
    clearedOnHoldAmount: number;
}

export interface HoldOverAmountSummary {
    records: HoldOverAmount [];
}

export interface SsoValidationState {
    fetchState: FetchState;
    cognitoTokens: CognitoTokens;
    error: boolean;
    isLogoutLoginMode: boolean;
}

export interface SamlState {
    provider: string;
}

export interface CognitoTokens {
    email: string;
    username: string;
    accessToken: string;
    idToken: string;
    refreshToken: string;
}

export interface DeductibleTrackerState {
    iframeUrlFetchState: FetchState;
    iframeUrl: string;
    healthPlanIsLinkedFetchState: FetchState;
    healthPlanIsLinked: boolean;
    healthPlansFetchState: FetchState;
    healthPlans: HealthPlan[];
    connectionError: boolean;
    talonEnabledForUser: boolean;
    talonEnabledForUserState: FetchState;
}

export interface HealthPlan {
    name: string;
    year: number;
    benefits: HealthPlanBenefit[];
}

export interface HealthPlanBenefit {
    amount: number;
    max: number;
    network: string;
    scope: string;
    type: string;
}

export interface ClaimAccountOnboardingState extends ClaimAccountOnboardingResponse {
    fetchState: FetchState;
    postFetchState: FetchState;
}

export interface ClaimAccountOnboardingResponse {
    termsAccepted: boolean;
    personaType: PersonaType;
    onboardingComplete: boolean;
    currentOnboardingStep: ClaimAccountOnboardingStep;
}

export interface BankAchAccountChoiceModel {
    number: string;
    name: string;
    bankAccountType: BankAccountType;
    id: string;
    institution: string;
}

export interface BankAchValidationResponseModel {
    accountChoices: BankAchAccountChoiceModel[];
}

export enum BankAccountType {
    Checking = 1,
    Savings = 2
}

export enum ClaimAccountOnboardingStep {
    Terms = "terms",
    Persona = "persona",
    Complete = "complete"
}

export enum PersonaType {
    None = "none",
    Pay = "pay",
    Save = "save",
    Invest = "invest"
}

export enum EmployeeSearchSortOption {
    Relevance = "relevance",
    Name = "name",
    Employer = "employer",
    Partner = "partner",
    Ssn = "ssn",
    AccountNumber = "accountNumber",
    Email = "email",
    EmployeeId = "employeeId",
    ExternalEmployerId = "externalEmployerId",
    Status = "status"
}

export const employeeSearchSortOptionMap: Record<EmployeeSearchSortOption, string> = {
    relevance: "Relevance",
    name: "Last Name",
    employer: "Employer",
    partner: "Partner",
    ssn: "SSN",
    accountNumber: "Account Number",
    email: "E-Mail",
    employeeId: "External Employee ID",
    externalEmployerId: "Employer Alias",
    status: "Status"
};

export enum SortOrder {
    None = "none",
    Ascending = "ascending",
    Descending = "descending"
}

export enum EmployeeListSortOption {
    None = 0,
    Name = 1,
    PlanType = 2,
    HsaStartDate = 3,
    EmploymentStatus = 4,
    EnrollmentStatus = 5,
    ExternalEmployeeId = 6
}

export interface BirthYearResponse {
    birthYear: number;
}

export interface HealthcareExpenseState {
    forecastRequestFetchState: FetchState;
    forecastRequest: HealthcareExpenseForecastRequest;
    hsaForecastFetchState: FetchState;
    hsaForecastResponse: HsaForecastResponse;
    conditionsFetchState: FetchState;
    conditions: HealthcareExpenseCondition[];
}

export interface HealthcareExpenseForecastRequest {
    birthYear: number;
    zipCode: number;
    sex: string;
    persona: string;
    usesTobacco: boolean;
    deductibleAmount: number;
    maxOutOfPocket: number;
    premium: number;
    retirementAge: number;
    currentHsaBalance: number;
    currentInvestmentBalance: number;
    employerHsaContributions: number;
    employeeHsaContributions: number;
    conditions: string[];
    minimumInvestmentBalance: number;
    familyMembers: HouseholdMember[];
}

export interface HsaForecastResponse {
    hsaForecasts: HsaForecast[];
    totalTaxSavings: number;
}

export interface HsaForecast {
    year: number;
    predictedHsaBalance: number;
    costNotCoveredByHsa: number;
    costsCoveredByHsa: number;
    taxSavings: number;
    totalAnnualCost: number;
    oopCoverageLikelihood: number;
}

export interface HealthcareExpenseCondition {
    conditionName: string;
    conditionCode: string;
}

export interface BirthYearState {
    birthYearFetchState: FetchState;
    birthYear: number;
}

export interface DevinirTransferInKindState {
    nonCompliantMessageAcknowledged: boolean;
}

export interface LoginRedirectState {
    routeObject: any;
}

export type VerifyIdentityStatus =
    "Pass" |
    "Fail" |
    "NeedInfo" |
    "TimedOut" |
    "RetryLimitReached" |
    "Error" |
    "PromptSsn" |
    "PromptDob" |
    "PromptDobAndSsn" |
    "DocumentsRequested";

export interface KbaQuizQuestion {
    questionType: string;
    questionText: string;
    answer: string[];
}

export interface VerifyIdentityResponse {
    status: VerifyIdentityStatus;
    idNumber: string;
    kbaQuizQuestions: KbaQuizQuestion[];
    documentsFormUrl?: string;
}

export interface KbaQuizAnswer {
    questionType: string;
    answer: string;
}

export interface ValidateKbaQuizRequest {
    idNumber: string;
    enterpriseProfile?: string;
    kbaQuizAnswers: KbaQuizAnswer[];
}

export enum RevealType {
    SocialSecurityNumber = "SocialSecurityNumber"
}

export interface RevealPersonalInformationRequest {
    revealForUserName: string;
    reason: string;
    revealType: RevealType;
}

export interface RevealPersonalInformationResponse {
    sensitiveInformation: string;
    revealType: RevealType;
}

export interface RevealState {
    revealState: FetchState;
    revealedInformation: RevealPersonalInformationResponse;
}

export interface IdentityChallengeState {
    fetchState: FetchState;
    questions: KbaQuizQuestion[];
    answers: string[];
    verifyIdentityStatus?: VerifyIdentityStatus;
    verifyIdentityIdNumber?: string;
    hasAnsweredWave1: boolean;
    documentsFormUrl?: string;
}

export enum MfaManageStep {
    Authenticate,
    Manage,
    AddTOTP,
    AddSMS,
    RemoveMFA,
    VerifyPhone,
    VerifyMfa,
    PostMfa,
    AddEmail,
    VerifyEmail,
    AddVoiceCall
}

export interface MfaState {
    cognitoUser: CognitoUser;
    step: MfaManageStep;
    phoneNumber: string;
    totpSecret: string;
    mfaStatus: MfaStatusEnum;
    showMfa: boolean;
}

export interface OktaFactorState {
    step: MfaManageStep;
    factors: OktaFactor[];
    selectedFactor: OktaFactor;
    totpData: {
        [key: string]: any
    };
    isEditingProfile: boolean;
    factorsLoadFetchState: FetchState;
}

export interface OktaFactorProfile {
    email?: string;
    phoneNumber?: string;
    credentialId?: string;
}

export interface OktaFactor {
    id: string;
    factorType: string;
    key: string;
    isRequired: boolean;
    isActive: boolean;
    displayName: string;
    profile: OktaFactorProfile;
}

export interface MfaItem {
    mfaType: OktaFactorType;
    profile: string;
    isRequired: boolean;
}

export interface UserAccountCredentialsResponse {
    provider: AuthenticationProvider;
    email: string;
    pendingEmail: string;
    mobilePhone: string;
    firstName: string;
    lastName: string;
    login: string;
    secondEmail: string;
    status: UserAccountCredentialStatus;
    mfaTypes: MfaItem[];
    hasIdpAccount: boolean;
    ssoUserId: string;
    lastLogin: string;
    profileEmail: string;
    isCredentialed: boolean;
    ssoUserSyncEnabled: boolean;
    ssoKey: string;
}

export enum SsoState {
    Pending = "Pending",
    Enabled = "Enabled"
}

export enum idpStatusChange {
    None = "none",
    Suspend = "suspend",
    Deactivate = "deactivate",
    UnSuspend = "unSuspend",
    Active = "activate",
    ResetPassword = "resetPassword",
    ResetMfa = "resetMfa",
    Unlock = "unlock",
    ReSendActivationEmail= "resendActivationEmail"
}

export interface IdpStatusChangeRequest {
    username: string;
    statusChange: idpStatusChange;
    oktaFactorTypes: OktaFactorType[];
    partnerId: number;
}

export interface IdpEmailChangeRequest {
    username: string;
    email: string;
    partnerId: number;
}

export interface VerifyIdpEmailChangeRequest {
    verifyLinkId: string;
    signature: string;
}

export enum AuthenticationProvider {
    Cognito = "cognito",
    Okta = "okta"
}

export const authTypeMap: Record<AuthenticationProvider, string> = {
    okta: "Okta",
    cognito: "AWS Cognito"
};

export enum AdminUserStatus {
    None = "none",
    Staged = "staged",
    Active = "active",
    Provisioned = "provisioned",
    Recovery = "recovery",
    LockedOut = "locked_out",
    PasswordExpired = "password_expired",
    Suspended = "suspended",
    Deprovisioned = "deprovisioned",
    Disabled = "disabled",
}

export enum UserAccountCredentialStatus {
    None = "none",
    Staged = "staged",
    Active = "active",
    Provisioned = "provisioned",
    Recovery = "recovery",
    LockedOut = "lockedOut",
    PasswordExpired = "passwordExpired",
    Suspended = "suspended",
    Deprovisioned = "deprovisioned",
    Disabled = "disabled",
}

export const userAccountCredentialStatusMap: Record<UserAccountCredentialStatus, string> = {
    none: "None",
    staged: "Staged",
    active: "Active",
    provisioned: "Provisioned",
    recovery: "Recovery",
    lockedOut: "Locked Out",
    passwordExpired: "Password Expired",
    suspended: "Suspended",
    deprovisioned: "Deprovisioned",
    disabled: "Disabled",
};

export interface OktaEnrollFactorResponse {
    factorId: string;
    status: string;
    embedded: {
        [key: string]: any
    };
}

export interface OktaAccountCheckResponse {
    requiresReAuthentication: boolean;
}

export enum OktaFactorType {
    Unknown = "unknown",
    Push = "push",
    Sms = "sms",
    Call = "call",
    Token = "token",
    Totp = "totp",
    HardwareToken = "hardwareToken",
    Question = "question",
    Web = "web",
    Email = "email",
    U2F = "u2F",
    WebAuthn = "webAuthn",
    SoftwareToken = "softwareToken",
    Custom = "custom"
}

export const oktaFactorTypeMap: Record<OktaFactorType, string> = {
    unknown: "Unknown",
    push: "Push",
    sms: "SMS",
    call: "Call",
    token: "Token",
    totp: "Token",
    hardwareToken: "Hardware Token",
    question: "Question",
    web: "Web",
    email: "Email",
    u2F: "U2F",
    webAuthn: "Web Auth",
    softwareToken: "Software Token",
    custom: "Custom"
};

export interface HouseholdMember {
    familyMemberId: number;
    memberType: MemberType;
    birthYear: number;
    firstName: string;
    lastName: string;
    sex: string;
    usesTobacco: boolean;
    conditions: string[];
    key: string;
}

export enum MemberType {
    Unknown = "unknown",
    Spouse = "spouse",
    Child = "child"
}

export interface MfaUserDisableRequest {
    code: string;
}

export interface UserSystemIdState {
    fetchState: FetchState;
    username: string;
    needsSystemId: boolean;
    userSystemId: string;
}

export interface GetUserSystemIdResponse {
    custodianRequresSystemId: boolean;
    userSystemId: string;
}

export interface Payee {
    name: string;
    address: MerchantAddress;
}

export interface MerchantAddress {
    addressLine: string;
    city: string;
    state: string;
    zipCode: string;
}

export interface Claim {
    claimNumber: string;
    patientName: string;
    type: ClaimType;
    allowedAmount: number;
    deductibleAmount: number;
    copayAmount: number;
    coinsuranceAmount: number;
    memberResponsibility: number;
    serviceName: string;
    serviceDate: string;
    serviceDateMax: string;
    providerName: string;
    providersCount: number;
    claimLines: ClaimLine[];
    linkedExpenseIds: number[];
    isSelected: boolean;
    hashValue: string;
    facilityName: string;
    insurancePaidAmountTotal: number;
    billedAmountTotal: number;
    copayCoinsuranceAmountTotal: number;
    notCoveredAmountTotal: number;
    claimDisposition: string;
    count: number;
    claimSource: ClaimSource;
    network: ClaimNetwork;
    status: string;
}
export enum ClaimNetwork {
    Unkown = "unknown",
    InNetwork = "inNetwork",
    OutOfNetwork = "outOfNetwork"
}
export enum ClaimSource {
    None = "none",
    MyMedicalShopper = "myMedicalShopper",
    Tufts = "tufts",
    HPHC = "hphc"
}

export interface ClaimLine {
    claimNumber: string;
    lineNumber: number;
    patientName: string;
    type: ClaimType;
    allowedAmount: number;
    deductibleAmount: number;
    copayAmount: number;
    coinsuranceAmount: number;
    memberResponsibility: number;
    serviceName: string;
    serviceDate: string;
    providerName: string;
    facilityName: string;
    insurancePaidAmount: number;
    billedAmount: number;
    copayCoinsuranceAmount: number;
    notCoveredAmount: number;
    claimDisposition: string;
}

export interface ClaimState {
    claimPageResultSet: ClaimResultSet;
    searchResultDictionary: {
        [key: number]: ClaimResultSet
    };
    claimLinkedExpensesDictionary: {
        [key: number]: LinkedExpenses
    };
    expenseLinkedClaimsDictionary: {
        [key: number]: LinkedClaims
    };
    userHasThirdPartyClaims: boolean;
    hphcOptIn: boolean;
    hphcOptInFetchState: FetchState;
    hasClaims: boolean;
    hasClaimsFetchState: FetchState;
}

export interface ClaimResultSet {
    claims: Claim[];
    fetchState: FetchState;
    page: number;
    pageSize: number;
    numberOfPages: number;
    recordCount: number;
    sortByColumn: ClaimsPageRequestSortableColumn;
    sortOrder: SortOrder;
}
export enum ClaimsPageRequestSortableColumn {
    Date = "date",
    Amount = "amount",
    Provider = "provider",
    Patient = "patient",
    ClaimNumber = "claimNumber",
    Category = "category",
    Service = "service",
    Status = "status"
}
export interface LinkedExpenses {
    linkedExpenses: LinkedExpense[];
    fetchState: FetchState;
}

export interface LinkedClaims {
    linkedClaims: LinkedClaim[];
    fetchState: FetchState;
}

export interface LinkedExpense {
    expense: Expense;
    linkedHsabExpense: LinkedHsabExpenseResponse;
    expenseId: number;
    claimNumber: string;
    comment: string;
}
export interface LinkedHsabExpenseResponse {
    id: number;
    dateOfService: string;
    expenseName: string;
    reimbursedAmount: number;
    amount: number;
    expenseCategory: HsabExpenseCategory;
    providerName: string;
    comment: string;
    status: HsabExpenseDisplayStatus;
}
export interface LinkedClaim {
    claim: Claim;
    expenseId: number;
    claimNumber: string;
    comment: string;
}

export enum ClaimType {
    None = "none",
    Medical = "medical",
    Pharmacy = "pharmacy",
    Dental = "dental",
    Vision = "vision",
}

export interface ClaimResponse extends PagedResponse {
    records: Claim[];
}

export enum HsabLegacyInvestmentProvider {
    None = "none",
    Devenir = "devenir",
    Schwab = "schwab"
}

export interface InvestmentThreshold {
    investmentProvider: InvestmentCustodian;
    accountCreate: number;
    moneyTransfer: number;
    sweepThreshold: number;
    isSweepThresholdEnabled: boolean;
}

export interface InvestmentProvider {
    investmentProvider: InvestmentCustodian;
}

export interface PendingPersonalContribution {
    email: string;
    firstName: string;
    lastName: string;
    pendingPersonalContributionId: number;
    amount: number;
    isOnHold: boolean;
    note: string;
    dateCreated: string;
    dateLastModified: string;
    dateToProcess: string;
    isDeleted?: boolean;
}

export enum PendingPersonalContributionPutAction {
    Hold = "hold",
    Release = "release",
    Delete = "delete"
}

export interface PendingPersonalContributionPut {
    pendingPersonalContributionId: number;
    note: string;
    action: PendingPersonalContributionPutAction;
}

export interface TransactionEditSearch {
    postDate: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    account?: string;
}

export interface TransactionEditPut {
    transactionId: number;
    effectiveYear: number;
    transactionType: string;
    isArchived: boolean;
    isAmountInverted: boolean;
    description: string;
}

export interface TransactionEditSearchItem {
    amount: number;
    description: string;
    effectiveYear: number;
    transactionType: string;
    id: number;
    isArchived: boolean;
    isAmountInverted: boolean;
}

export interface TransactionEditSearchResult {
    transactions: TransactionEditSearchItem[];
    firstName: string;
    lastName: string;
    custodianName: string;
    employerId: number;
    employerName: string;
}

export interface ClaimSearchRequest {
    serviceDate: string;
    providerName: string;
}

export interface ExpenseSearchRequest {
    startDate: string;
    endDate: string;
    description: string;
}

export interface ExpenseLinkRequest {
    claimNumbers: string[];
    expenseId: number;
    comment: string;
}

export interface ClaimLinkRequest {
    claimNumber: string;
    expenseIds: number[];
    comment: string;
}

export interface ClaimExpenseLinkRemoveRequest {
    claimNumber: string;
    expenseId: number;
}

export enum CommunicationKitType {
    None = "none",
    BendBranded = "bendBranded"
}

export enum EmployerEditSuppressionType {
    None = "none",
    HsaBankImportedProperties = "hsaBankImportedProperties"
}

export enum ChatFunctionalityType {
    ChatBotOnly = "chatBotOnly",
    LiveAgentAndChatBot = "liveAgentAndChatBot"
}

export interface PartnerSettingsRequest {
    canEmployerUpdateCensus: boolean;
    canViewEmployerSsns: boolean;
    usersAreAutomaticallyRedirectedToWex: boolean;
    partnerUsesCustomClaims: boolean;
    employeeLogoutRedirectUrl: string;
    communicationKitType: CommunicationKitType;
    employerEditSuppressionType: EmployerEditSuppressionType;
    advisorEmailFrom: string;
    advisorEmailName: string;
    claimsOptIn: boolean;
    disableTalon: boolean;
    showEmployerSignupQuestion: boolean;
    consumerLiveChatEnabled: boolean;
    consumerInitiateCobrowseEnabled: boolean;
    chatFunctionalityType: ChatFunctionalityType;
    isEnrollmentDisabled: boolean;
    dateStopEmployeeContribution: string;
    disableInvestmentActivity: boolean;
    resourceOptimizationEnabled: boolean;
    resourceVisibilityEnabled: boolean;
}

export interface PartnerSettingsResponse extends PartnerSettingsRequest {
    partnerId: number;
}

export interface PartnerSettings {
    canEmployerUpdateCensus: boolean;
    canViewEmployerSsns: boolean;
    usersAreAutomaticallyRedirectedToWex: boolean;
    partnerUsesCustomClaims: boolean;
    employeeLogoutRedirectUrl: string;
    communicationKitType: CommunicationKitType;
    employerEditSuppressionType: EmployerEditSuppressionType;
    advisorEmailName: string;
    advisorEmailFrom: string;
    claimsOptIn: boolean;
    disableTalon: boolean;
    showEmployerSignupQuestion: boolean;
    consumerLiveChatEnabled: boolean;
    consumerInitiateCobrowseEnabled: boolean;
    chatFunctionalityType: ChatFunctionalityType;
    isEnrollmentDisabled: boolean;
    dateStopEmployeeContribution: string;
    disableInvestmentActivity: boolean;
    resourceOptimizationEnabled: boolean;
    resourceVisibilityEnabled: boolean;
}

export interface PartnerSettingsState {
    fetchState: FetchState;
    settings: PartnerSettings;
}

export interface PayrollDateResponse {
    nextPayrollDate: string;
    payrollDateCutOff: string;
}

export interface ExpenseResultSet {
    expenses: Expense[];
    fetchState: FetchState;
    page: number;
    pageSize: number;
    numberOfPages: number;
    recordCount: number;
}
export interface Role {
    roleId: number;
    roleName: string;
    isEnabled: boolean;
    displayName: string;
}

export interface EmployerRolePermission {
    permissionId: number;
    permissionName: string;
    roles: Role[];
}

export interface EmployerRolePermissionRequest {
    employerRolePermissions: EmployerRolePermission[];
}

export interface EmployerRolePermissionState {
    fetchState: FetchState;
    postFetchState: FetchState;
    employerRolePermissions: EmployerRolePermission[];
    rolefetchState: FetchState;
    roles: UserRoleModel[];
    role: UserRoleModel;
    singlerolefetchState: FetchState;
}

export interface UserRoleModel {
    roleId: number;
    roleName: string;
    roleType: RoleType;
    displayName?: string;
    permissions: number[];
}

export enum RoleType {
    None = "none",
    Employer = "employer",
    Partner = "partner",
    Admin = "admin"
}

export interface PartnerFeeWaiver {
    feeWaiverType: FeeWaiverType;
    feeWaiverThreshold: number;
}

export interface InvestmentFeeSchedule {
    accountType: string;
    annualBasis: number;
    riaRevenueShareBasis: number;
    quarterlyMaximum: number;
    quarterlyMinimum: number;
}

export interface MorningstarInstrumentPerformanceGraphRequestArgs {
    username?: string;
    partnerId?: number;
    stockSymbol: string;
    dateStart: string;
    dateEnd: string;
}

export interface GenericGraphPoint {
    date?: Date | string;
    value: number;
}

export interface GenericBarChartDataPoint extends GenericGraphPoint {
    label: string;
}

export interface GenericMultiColumnBarChartDataPoint {
    label: string;
    value1: number;
    value2: number;
    position: number;
}

export enum GenericChartValueType {
    Percentage = "percentage",
    Dollar = "dollar",
}

export interface InstrumentPerformanceReturnData {
    value: number;
    annualized: boolean;
    date: string;
    timePeriod: InstrumentPerformanceTimePeriod;
}

export enum InstrumentPerformanceTimePeriod {
    ClosePrice = "ClosePrice",
    Months0 = "M0",
    Months1 = "M1",
    Months3 = "M3",
    Months6 = "M6",
    Months12 = "M12",
    Months36 = "M36",
    Months60 = "M60",
    Months120 = "M120",
}

export enum InstrumentPerformanceGraphTimePeriod {
    Days1 = "days1",
    Weeks1 = "weeks1",
    Months1 = "months1",
    Years1 = "years1",
    Years2 = "years2",
    Years3 = "years3",
    Years5 = "years5",
}

// Depericated use the UserPermissions Enum instead
export enum Permission {
    AchReportRead = "achReportRead",
    BankAccountRead = "bankAccountRead",
    BankAccountWrite = "bankAccountWrite",
    BillingReportRead = "billingReportRead",
    CensusRead = "censusRead",
    CensusWrite = "censusWrite",
    EmployeeIdUpdate = "employeeIdUpdate",
    EmployeeInviteWrite = "employeeInviteWrite",
    EmployeeRead = "employeeRead",
    EmployeeWrite = "employeeWrite",
    EmployerAccountRead = "employerAccountRead",
    EmployerAccountWrite = "employerAccountWrite",
    EmployerCensusUpdateSettingRead = "employerCensusUpdateSettingRead",
    EmployerCustodianRead = "employerCustodianRead",
    EmployerOnBoardingRead = "mployerOnBoardingRead",
    EmployerOnBoardingWrite = "employerOnBoardingWrite",
    EmployerSuspensionRead = "employerSuspensionRead",
    EnrollmentReportRead = "enrollmentReportRead",
    HsaAccountInfoRead = "hsaAccountInfoRead",
    HsaInfoReportRead = "hsaInfoReportRead",
    InviteDateRead = "inviteDateRead",
    InviteDateWrite = "inviteDateWrite",
    ManagePartnerUserRead = "managePartnerUserRead",
    ManagePartnerUserWrite = "managePartnerUserWrite",
    ManageUsersRead = "manageUsersRead",
    ManageUsersWrite = "manageUsersWrite",
    PartnerBillingReportRead = "partnerBillingReportRead",
    PartnerCommunicationWidgetRead = "partnerCommunicationWidgetRead",
    PartnerCommunicationWidgetWrite = "partnerCommunicationWidgetWrite",
    PartnerEmployerRead = "partnerEmployerRead",
    PartnerEmployerWrite = "partnerEmployerWrite",
    PartnerFindEmployeeRead = "partnerFindEmployeeRead",
    PartnerImpersonateEmployee = "partnerImpersonateEmployee",
    PartnerManageIdp = "partnerManageIdp",
    PartnerManageUserLogin = "partnerManageUserLogin",
    PartnerNotificationRead = "partnerNotificationRead",
    PartnerNotificationWrite = "partnerNotificationWrite",
    PayrollRead = "payrollRead",
    PayrollWrite = "payrollWrite",
    ReportAccessWrite = "reportAccessWrite",
    AdminDocumentView = "adminDocumentView",
    InvestmentInstrumentAllowSell = "investmentInstrumentAllowSell",
    ResyncMyMedicalShopperClaims = "resyncMyMedicalShopperClaims",
    PartnerManageExternalLinkedAccounts = "partnerManageExternalLinkedAccounts",
    PartnerManageFormDocuments = "partnerManageFormDocuments",
    BulkLiquidationFileUpload = "bulkLiquidationFileUpload"
}

export enum UserPermission {
    AchReportRead = 1,
    BankAccountRead = 2,
    BankAccountWrite = 3,
    BillingReportRead = 4,
    CensusRead = 5,
    CensusWrite = 6,
    EmployeeRead = 7,
    EmployeeWrite = 8,
    EmployeeInviteWrite = 9,
    EmployerCustodianRead = 10,
    EmployerAccountRead = 11,
    EmployerAccountWrite = 12,
    EmployerCensusUpdateSettingRead = 13,
    EmployerOnBoardingRead = 14,
    EmployerOnBoardingWrite = 15,
    EnrollmentReportRead = 16,
    HsaInfoReportRead = 17,
    InviteDateRead = 18,
    InviteDateWrite = 19,
    ManageUsersRead = 20,
    ManageUsersWrite = 21,
    PayrollRead = 22,
    PayrollWrite = 23,
    ReportAccessWrite = 24,
    EmployerSuspensionRead = 25,
    PartnerBillingReportRead = 26,
    ManagePartnerUserRead = 27,
    ManagePartnerUserWrite = 28,
    PartnerNotificationRead = 29,
    PartnerNotificationWrite = 30,
    PartnerFindEmployeeRead = 31,
    PartnerEmployerRead = 32,
    PartnerEmployerWrite = 33,
    HsaAccountInfoRead = 34,
    PartnerCommunicationWidgetRead = 35,
    PartnerCommunicationWidgetWrite = 36,
    EmployeeIdUpdate = 37,
    PartnerImpersonateEmployee = 38,
    PartnerManageIdp = 39,
    PartnerManageUserLogin = 40,
    AdminPortalEmployerView = 41,
    AdminPortalEmployerEdit = 42,
    AdminPortalAdvisorView = 44,
    AdminPortalAdvisorEdit = 45,
    AdminPortalPartnerView = 46,
    AdminPortalPartnerEdit = 47,
    AdminPortalUtilityUserEnableDisableAccount = 48,
    AdminPortalUtilityUserAdminEmailChange = 49,
    AdminPortalUtilityUserAdminDOBNameChange = 50,
    AdminPortalUtilityUserMFADisable = 51,
    AdminPortalUtilityUserPasswordReset = 52,
    AdminPortalUtilityLinkedBankAcctMangEdit = 53,
    AdminPortalUtilityUMBAccountOpenerEdit = 54,
    AdminPortalUtilityResetUserCustodianEdit = 55,
    AdminPortalUtilityLinkedFinicityAcctDtlEdit = 56,
    AdminPortalUtilityDeleteUMBLinkAccountEdit = 57,
    AdminPortalGetAllPortalUsersRead = 58,
    AdminPortalDocumentView = 59,
    AdminPortalDocumentEdit = 60,
    PartnerModifyHolds = 61,
    PartnerModifyHighRiskHolds = 62,
    PartnerModifyFraudHolds = 63,
    AdminPortalInstantTransferFailuresEdit = 64,
    AdminPortalReimbursementFailuresEdit = 65,
    AdminPortalFraudCheckQueueEdit = 66,
    AdminPortalOmnibusTransactionCodeManagementEdit = 67,
    AdminPortalEbtoView = 68,
    AdminPortalEbtoEdit = 69,
    AdminPortalReportsView = 70,
    AdminPortalPersonalContributionsEdit = 71,
    AdminPortalTransactionEdit = 72,
    AdminPortalPendingTxClearEdit = 73,
    AdminPortalPIIReportsView = 74,
  AdminPortalPartnerSettingsView = 75,
  PartnerManageFormDocuments = 76,
  AdminPortalUtilityGlobalRolePermissionEdit = 77,
  AdminPortalUtilityQuickSightReptEdit = 78,
  ResyncMyMedicalShopperClaims = 79,
  AdminPortalUtilityDebitCardCustoEdit = 80,
  AdminPortalUtilityDrvWealthRiskAgePortEdit = 81,
  AdminPortalUtilityDrvWealthRiskAgeReviEdit = 82,
  AdminPortalUtilityDrvWealthInstrLstEdit = 83,
  AdminPortalUtilitySftpTestFileEdit = 84,
  AdminPortalUtilityOutageNotifyConfigEdit = 85,
  AdminPortalUtilityManageThemeEdit = 86,
  AdminPortalUtilityMobileAppsEdit = 87,
  AdminPortalUtilityOpenSearchEdit = 88,
  AdminPortalUtilityCpfBlackoutDateManagementEdit = 89,
  AdminPortalInvestmentAccountCloseUpload = 90,
  AdminPortalUserManagementView = 91,
  AdminPortalUserManagementEdit = 92,
  AdminPortalUtilityRepointUserInvestmentAccountsEdit = 93,
  AdminPortalUtilityRepointUserInvestmentAccountRequestsView = 94,
  AdminPortalMonitoredStateMachines = 95,
  AdminPortalRevealPersonalInformation = 96,
  InvestmentInstrumentAllowSell = 97,
  BulkLiquidationFileUpload = 98,
  PartnerPortalInvestmentView = 99,
  PartnerManageExternalLinkedAccounts = 100,
  AdminPortalDebitCardSettingBulkImport = 101,
  AdminPortalCardSettingsView = 102,
  AdminPortalCardSettingsEdit = 103,
  AdminPortalContributionCancel = 104,
  AdminPortalHsaInfoView = 105,
  EmployerPortalMassTransitView = 106,
  AdminPortalManageExternalLinkedBankAccounts = 107,
  AdminLegacyDisableInvestmentBuy = 108,
  AdminPortalManageRestrictedBankAccounts = 109,
  AdminPortalViewRealBankAccountNumbers = 110,
  AdminPortalEmailTemplateUtility = 111,
  AdminPortalFiservCodeMappingsEdit  = 112,
  AdminPortalMarketplaceTileView = 113,
  AdminPortalMarketplaceTileEdit = 114,
  AdminPortalUtilityBrowserRulesEdit = 115
}

export interface RolePermissionsCollection {
    [id: number]: Permission[];
}

export interface EmployerPermissions {
    employerId: number;
    permissions: Permission[];
}

export interface PartnerPermissions {
    partnerId: number;
    permissions: Permission[];
}

export interface RolePermissionsResponse {
    employerPermissions: EmployerPermissions[];
    partnerPermissions: PartnerPermissions[];
}

export interface RolePermissionsState {
    fetchState: FetchState;
    employerPermissions: RolePermissionsCollection;
    partnerPermissions: RolePermissionsCollection;
    userPermissions: number[];
}

export interface RoleState {
    partnerRolesFetchState: FetchState;
    partnerRoles: UserRoleModel[];
    employerRolesFetchState: FetchState;
    employerRoles: UserRoleModel[];
}

export interface UserPermissionResponse {
    permissions: number[];
}

export interface PriorYearCutoffDateResponse {
    cutoffDate: boolean;
}

// N.B. these strings appear in the URL
export enum DrivewealthWizardStep {
    AccountTypeChange = "account-type-change",
    SelectAccountType = "select-account-type",
    RequiredDisclosures = "required-disclosures",
    RiskToleranceQuestionnaire = "risk-tolerance-questionnaire",
    PortfolioSelection = "portfolio-selection",
    PortfolioConfirmation = "portfolio-confirmation",
    TermsAndConditions = "terms-conditions",
    CustomerSignature = "customer-signature",
    AutomatedFunding = "auto-funding",
    Completed = "completed"
}

export enum RiskToleranceQuestionnaireAnswer {
    StronglyDisagree = "stronglyDisagree",
    Disagree = "disagree",
    Neutral = "neutral",
    Agree = "agree",
    StronglyAgree = "stronglyAgree"
}

export enum RiskToleranceType {
    None = "none",
    Conservative = "conservative",
    ModeratelyConservative = "moderatelyConservative",
    Moderate = "moderate",
    ModeratelyAggressive = "moderatelyAggressive",
    Aggressive = "aggressive"
}

export interface RiskToleranceQuestionnaireResponse {
    score: number;
    riskToleranceType: RiskToleranceType;
    instrumentListId: number;
}

export interface UserRtqEligibilityResponse {
    curatedRtqEnabledByEmployerOrPartner: boolean;
    hasExistingRtqAndCanRetake: boolean;
}

export interface RiskToleranceQuestionnaireState extends RiskToleranceQuestionnaireResponse {
    fetchState: FetchState;
    postState: FetchState;
    rtqEligibilityFetchState: FetchState;
    userRtqEligibilityResponse: UserRtqEligibilityResponse;
    managedPortfolio: FlatManagedPortfolioResponse;
    rtqUpdated: boolean;
}

export interface RiskToleranceQuestionnaireField {
    id: string;
    label: string;
    userResponse: RiskToleranceQuestionnaireAnswer;
}

export interface RiskToleranceQuestionnaireForm {
    userResponses: RiskToleranceQuestionnaireAnswer[];
    accountId?: number;
}

export interface DrivewealthWizardStrategy {
    portfolioId: string;
}

export interface DrivewealthInvestmentStrategyState {
    managedPortfolio: FlatManagedPortfolioResponse;
}

export interface DrivewealthDripDisclosures {
    dripDisclosureAcknowledgmentRequired: boolean;
    equitiesDripDisclosure: DrivewealthDisclosure;
    mutualFundsDripDisclosure: DrivewealthDisclosure;
}

export interface DrivewealthDisclosure {
    disclosureId: string;
    documentName: string;
    documentUrl: string;
}

export interface DrivewealthWizardState {
    createAccountFetchState: FetchState;
    createAccountError?: DrivewealthCreateAccountError;
    steps: DrivewealthWizardStep[];
    completedSteps: DrivewealthWizardStep[];
    accountType: DrivewealthAccountManagementType;
    investmentStrategy: Partial<DrivewealthWizardStrategy>;
    routerLocation: {
        name?: string,
        params?: object
    };
    enrollment: FetchKeyedResource<DrivewealthEnrollment>;
}

export interface DrivewealthEnrollmentBase {
    employmentStatus: drivewealth.EmploymentStatus;
    employmentType: drivewealth.JobSector;
    occupation: drivewealth.Occupation;
    isBroker: boolean;
    directorCompany: string;
    politicallyExposedNames: string;
    investmentObjective: drivewealth.InvestmentObjective;
    investmentExperience: drivewealth.InvestmentExperience;
    annualIncome: number;
    netWorthTotal: number;
    riskTolerance: drivewealth.RiskTolerance;
    accountType: string;
    citizenship: string;
    signatureContent: string;
    investmentAccountType: DrivewealthAccountManagementType;
}

export interface DrivewealthEnrollmentDraft {
    isDirty: boolean;
    employmentStatus?: drivewealth.EmploymentStatus;
    employmentType?: drivewealth.JobSector;
    occupation?: drivewealth.Occupation;
    citizenship?: string;
    isBroker?: boolean;
    isDirector?: boolean;
    directedCompany?: string | null;
    isPoliticallyExposed?: boolean;
    politicallyExposedNames?: string | null;
    annualIncome?: number;
    netWorthTotal?: number;
    didAgreeToTerms?: boolean;
    signatureContent?: string;
    portfolioId?: string;
}

export interface DrivewealthEnrollment extends DrivewealthEnrollmentDto {
    draft: DrivewealthEnrollmentDraft;
}

export interface DrivewealthEnrollmentDto extends DrivewealthEnrollmentBase {
    isPreenrolled: boolean;
    managedRtqScore: number;
}

export interface DrivewealthCreateCustomerRequest extends DrivewealthEnrollmentBase {
    portfolioId: string;
    equitiesDripDisclosureId: string;
    mutualFundsDripDisclosureId: string;
    managedRtqScore: number;
}

export interface DrivewealthUpdateAccountRequest {
    accountManagementType: string;
    portfolioId: string;
    investmentAccountManagementType: DrivewealthAccountManagementType;
}

export interface DrivewealthUpdateAccountRequestV2 {
  accountManagementType: string;
  portfolioId: string;
  accountId: number;
  investmentAccountManagementType: DrivewealthAccountManagementType;
}

export enum InstrumentStatus {
    None = "none",
    Active = "active",
    Inactive = "inactive",
    CloseOnly = "closeOnly",
    Halted = "halted",
    UnavailableToPurchase = "unavailableToPurchase"
}

export interface AdvisoryFund {
    instrumentId: string;
    stockSymbol: string;
    company: string;
    website: string;
    status: InstrumentStatus;
    balance: number;
    marketPrice: number;
    sharesOwned: number;
    fundType: string;
    imageUrl: string;
    assetCategoryName: string;
    allocationPercentage: number;
}

export interface AdvisoryFundCollection {
    advisoryFunds: AdvisoryFund[];
}

export interface CuratedRtqFundCollection {
    curatedRtqFunds: AdvisoryFund[];
}

export interface ManagedFundCollection {
    managedFunds: FlatInstrument[];
}

export interface CuratedRtqInstrumentCollection {
    curatedRtqInstruments: InstrumentDetail[];
}

export interface DrivewealthHolding {
    instrumentId: string;
    stockSymbol: string;
    fundName: string;
    imageUrl: string;
    fundType: string;
    side: string;
    webSite: string;
    availableForTradingQty: number;
    averagePrice: number;
    costBasis: number;
    marketValue: number;
    marketPrice: number;
    openQuantity: number;
    priorClose: number;
    unrealizedProfitLoss: number;
    unrealizedDayProfitLossPercent: number;
    unrealizedDayProfitLoss: number;
    status: InstrumentStatus;
}

export interface AllowedInvestmentAccountTypes {
    hasAdvisory: boolean;
    hasRiaManaged: boolean;
    hasSelfDirected: boolean;
    allowPreEnrollment: boolean;
    isCuratedRtqEnabled: boolean;
}

export interface AllowedEmployerInvestmentAccountTypes
    extends AllowedInvestmentAccountTypes {
    partnerHasAdvisory: boolean;
    partnerHasRiaManaged: boolean;
    partnerHasSelfDirected: boolean;
    partnerAllowsPreEnrollment: boolean;
    pendingEnrollmentCount: number;
    partnerHasCuratedRtqEnabled: boolean;
}

export enum DrivewealthAccountManagementType {
    None = "none",
    RiaManaged = "riaManaged",
    Advisory = "advisory",
    OpenBrokerage = "openBrokerage",
}

export enum DrivewealthAccountLiquidationStatus {
    NotStarted = "notStarted",
    Pending = "pending",
    Completed = "completed",
    Failed = "failed"
}

export enum InvestmentManagementType {
    Managed = "RIA_MANAGED",
    Advisory = "ADVISORY",
    SelfDirected = "SELF"
}

export type BuyOrSell = "BUY" | "SELL";
export type OrderUnits = "USD" | "SHARES";

export interface DrivewealthOrderRequest {
    stockSymbol: string;
    amount: number;
    units: OrderUnits;
    side: BuyOrSell;
    priceOnScreen: number;
}

export interface DrivewealthServicerOrderRequest extends DrivewealthOrderRequest {
    forUser: string;
}

export interface DrivewealthOrderData {
    stockSymbol: string;
    amount: number;
    units: OrderUnits;
    side: BuyOrSell;
}

export interface DrivewealthManagedDepositRequest {
    amount: number;
}

export interface DrivewealthManagedRedemptionRequest {
    amount?: number;
    isLiquidation: boolean;
}

export interface DrivewealthServicerManagedRedemptionRequest extends DrivewealthManagedRedemptionRequest {
    forUser: string;
}

export interface DrivewealthClosedAccountSummary {
    drivewealthAccountId: number;
    accountId: string;
    accountNumber: string;
    accountManagementType: DrivewealthAccountManagementType;
    dateAccountOpened: string;
    dateAccountClosed: string;
}

export interface DrivewealthAccountSummary {
    drivewealthAccountId: number;
    accountId: string;
    accountNumber: string;
    cashAvailableForTrade: number;
    cashAvailableForWithdrawal: number;
    cashBalance: number;
    equityValue: number;
    holdings: DrivewealthHolding[];
    portfolioId: string;
    portfolioClientId: string;
    portfolioName: string;
    portfolioDescription: string;
    displayBalance: number;
    accountManagementType: DrivewealthAccountManagementType;
    isCuratedRtqAccountType: boolean;
    isDevenirTransferPeriod: boolean;
    liquidationStatus?: DrivewealthAccountLiquidationStatus;
    dateCreated: string;
}

export interface InstrumentHolding {
    stockSymbol: string;
    website: string;
    imageUrl: string;
    fundName: string;
    fundType: FundType;
    isEtfOrEquity: boolean;
    shares: number;
    marketPrice: number;
    marketValue: number;
    totalReturn: number;
    totalReturnPercentage: number;
    costBasis: number;
    lastPrice: number;
    todaysReturn: number;
    rateOfReturn: number;
    todaysReturnPercentage: number;
    status: InstrumentStatus;
}

export interface DrivewealthAccount {
    drivewealthAccountId: number;
    accountName: string;
    accountNumber: string;
    accountStatus: string;
    type: string;
    accountManagementType: string;
    accountType: string;
    portfolioId: string;
    cashAvailableForTrading: number;
    cashAvailableForWithdrawal: number;
    cashBalance: number;
    investmentBalance: number;
    investmentAccountType: DrivewealthAccountManagementType;
    dateAccountClosed: string;
    dateAccountOpened: string;
}

export interface AccountCloseReasonResponse {
    accountCloseReasonId: number;
    description: string;
}

export interface AccountCloseReasonModel {
    closeReasonId: number;
    otherCloseReason: string;
}

export interface InstrumentDetail {
    instrumentId: string;
    stockSymbol: string;
    companyName: string;
    status: InstrumentStatus;
    currentPrice: number;
    imageUrl: string;
    website: string;
    fundType: string;
    isFundEtfOrEquity: boolean;
    requestTime?: Moment;
    assetCategoryName: string;
    allocationPercentage: number;
}

export interface CuratedRtqPieChartDataItem {
    assetCategoryName: string;
    allocationPercentage: number;
}

export interface PieChartDataItem {
    label: string;
    value: number;
}

export interface DelayedQuote {
    stockSymbol: string;
    currentPrice: number;
    requestTime?: Moment;
}

export type DrivewealthCreateAccountError = "UNKNOWN" | "MISSING_PHONE";

export interface HackMap<TValue> {
    [index: string]: TValue;
}

export interface InstrumentDetailState {
    fetchStates: HackMap<FetchState>;
    detailsBySymbol: HackMap<InstrumentDetail>;
}

export interface MorningstarInstrumentPerformanceState {
    selectedTimePeriod: InstrumentPerformanceGraphTimePeriod;
}

export interface InstrumentPerformanceData {
    symbol: string;
    isin: string;
    name: string;
    fundType: FundType;
    companyProfile: string;
    sectorName: string;
    industryName: string;
    webAddress: string;
    basicEPS: number;
    epsGrowth3Y: number;
    dividendYield: number;
    dividendRate: number;
    payoutRatio: number;
    pe: number;
    trailingPerformances: TrailingPerformance[];
    historicalPerformanceSeries: HistoricalPerformance[];
    equityResearchRatings: EquityResearchRatings;
    trailingReturns: TrailingReturns;
    inceptionDate: string;
    export: number;
    netExpenseRatio: number;
    morningstarRating3Y: string;
    etfMStarRating: number;
    etfClosePrice: number;
    stockSectorBreakdown: InvestmentAllocation[];
    bondSectorBreakdown: InvestmentAllocation[];
    countryExposure: InvestmentAllocation[];
    assetAllocation: InvestmentAllocation[];
    etfTrailingPerformances: EtfTrailingPerformance[];
    riskAndVolatilityMeasures: RiskAndVolatilityMeasures[];
}

export interface EtfTrailingPerformance {
    timePeriod: InstrumentPerformanceTimePeriod;
    trailingPerformanceReturn: number;
    benchmarkPerformanceReturn: number;
}

export interface RiskAndVolatilityMeasures {
    timePeriod: string;
    alpha: number;
    beta: number;
    r2: number;
    sharpeRatio: number;
    standardDeviations: number;
}

export enum AssetAllocationType {
    Stock = "1",
    Bond = "3",
    Preferred = "5",
    Convertible = "6",
    Cash = "7",
    Other = "8"
}

export enum FundType {
    Equity = "equity",
    Etf = "etf",
    MutualFund = "mutualFund",
    Other = "other"
}

export interface InvestmentAllocation {
    typeCode: string;
    definition: string;
    investmentPercentage: number;
}

export interface EquityResearchRatings {
    asOfClosePriceDate: string;
    closePrice: number;
    quantitativeFairValueUncertainty: number;
    quantitativeStarRating: string;
    quantitativePriceToFairValue: number;
}

export interface InstrumentPerformanceResponseModel {
    date: string;
    stockPrice: number;
    openPrice: number;
    highPrice: number;
    lowPrice: number;
    volume: number;
}

export interface TrailingPerformance {
    currencyId: string;
    date: string;
    type: string;
    returnType: string;
    return: InstrumentPerformanceReturnData[];
}

export interface HistoricalPerformance {
    currencyId: string;
    startDate: string;
    frequency: string;
    timePeriod: InstrumentPerformanceTimePeriod;
    returnType: string;
    return: InstrumentPerformanceReturnData[];
}

export interface DrivewealthDayTradeCollection {
    items: any[];
}

export interface DrivewealthAccountCollection {
    items: DrivewealthAccount[];
}

export interface DrivewealthClosedAccountSummaryCollection {
    items: DrivewealthClosedAccountSummary[];
}

export interface InstrumentSearchResponse {
    pageCount: number;
    pageNumber: number;
    instruments: InstrumentDetail[];
}

export interface InstrumentSearchResults {
    searchTerm: string;
    fetchState: FetchState;
    pageCount: number;
    pageNumber: number;
    items: InstrumentDetail[];
}

export interface DrivewealthAccountMigrationState {
  fetchState: FetchState;
  partnerAccountMigration: PartnerAccountsMigrationModel;
  accountMigrationPeriodStatus: AccountMigrationPeriodStatus;
}

export enum AccountMigrationPeriodStatus {
    None = "none",
    NotStarted = "notStarted",
    Active = "active",
    Expired = "expired"
}

export interface AccountMigrationPeriodStatusResponse {
    accountMigrationPeriodStatus: AccountMigrationPeriodStatus;
}

export interface DrivewealthState {
    createAccountFetchState: FetchState;
    closeAccountFetchState: FetchState;
    liquidateAccountFetchState: FetchState;
    createAccountError?: DrivewealthCreateAccountError;
    activityHistory: ActivityHistory;
    managedTransfersActivityHistory: ManagedTransfersActivityHistory;
    managedPortfolio: FlatManagedPortfolioResponse;
    instrumentSearch: InstrumentSearchResults;
    enrollNowFetchState: FetchState;
    // deprecated as part of BEND-15881/16053, will switch to accountsV2
    accountsGetAllV2FetchState: FetchState;
    accountIds: number[];
    accountIdsFetchState: FetchState;
    allowedInvestmentAccountTypesFetchState: FetchState;
    summariesGetAllV2FetchState: FetchState;
    allowedInvestmentAccountTypes: AllowedInvestmentAccountTypes;
    accountClaims: DrivewealthAccountClaim[];
    accountClaimsFetchState: FetchState;
    accountCloseReasons: AccountCloseReasonResponse[];
    accountCloseReasonsFetchState: FetchState;
    userAgreementKey: string;
    userAgreementKeyFetchState: FetchState;
    fundCompanies: DropDownModel[];
    assetClasses: DropDownModel[];
    showModalFlag: boolean;
    enrolledAccountId: number;
}

export interface DrivewealthAccountClaim {
    drivewealthAccountMigrationId: number;
    drivewealthApiAccountId: number;
    drivewealthApiUserId: number;
    isClaimed: boolean;
    isRepointedAccount: boolean;
}

export interface DrivewealthAccountsCollection {
    items: DrivewealthAccount[];
}

export enum UserDocumentType {
    None = "none",
    TradeConfirmation = "tradeConfirmation",
    TaxDocument = "taxDocument"
}

export interface DrivewealthDocument {
    documentId: number;
    postedDate: moment.Moment;
    documentType: UserDocumentType;
    description: string;
    accountNumber: string;
}

export enum ActivityHistoryStatus {
    None = "none",
    Pending = "pending",
    Canceled = "canceled",
    Failed = "failed",
    Settled = "settled",
    PartiallyExecuted = "partially executed"
}

export enum OrderCancellationInitator {
    None = "none",
    System = "system",
    User = "user"
}

export enum ActivityHistoryType {
    None = "none",
    Buy = "buy",
    Sell = "sell",
    Dividend = "dividend",
    CorporateAction = "corporateAction",
    CashTransfer = "cashTransfer",
    StockTransfer = "stockTransfer",
    Deposit = "deposit",
    Withdrawal = "withdrawal",
    TransactionFee = "transactionFee",
    CashJournal = "cashJournal",
    CreditAdjustment = "creditAdjustment",
    Interest = "interest",
    SecurityBought = "securityBought",
    SecuritySold = "securitySold",
    StockPriceAdjustment = "stockPriceAdjustment",
    Commission = "commission",
    StockSpinoff = "stockSpinoff",
    StockSplit = "stockSplit",
    BillingFee = "billingFee",
    ManagedInvestmentTransferBuy = "managedInvestmentTransferBuy",
    ManagedInvestmentTransferSell = "managedInvestmentTransferSell",
    ManagedInvestmentTransferSellLiquidation = "managedInvestmentTransferSellLiquidation",
    ManagedInvestmentTransferBuyAutomated = "managedInvestmentTransferBuyAutomated",
    ManagedInvestmentTransferSellInvestmentFee = "managedInvestmentTransferSellInvestmentFee",
    DividendReinvestment = "dividendReinvestment"
}

export interface PendingActivity {
    pendingOrders: ActivityHistoryElement[];
    holdAmount: number;
    hasPendingManagedLiquidation: boolean;
}

export interface ActivityHistoryElement {
    status: ActivityHistoryStatus;
    type: ActivityHistoryType;
    stockSymbol: string;
    companyName: string;
    dollarAmount: number;
    shareAmount?: number;
    tradeDate: moment.Moment;
    settledDate?: moment.Moment;
    investmentFeeDescription: string;
    bendOrderId: string;
    dwOrderId: string;
    isCancellable: boolean;
    isCancelOrderInProgress: boolean;
    orderCancellationInitator: OrderCancellationInitator;
    // properties below not added yet! confirm name
    comment?: string;
    drivewealthDocument: DrivewealthDocument;
}

export interface ActivityHistory {
    completedActivityFetchState: FetchState;
    completedActivity: ActivityHistoryElement[];
    completedActivityFetchKey: FetchStateKey;
    pageNumber: number;
    pageCount: number;
}

export interface ManagedTransfersActivityHistory {
    managedTransfersActivityFetchState: FetchState;
    managedTransfersActivity: ManagedTransfersActivityHistoryElement[];
    managedTransfersActivityFetchKey: FetchStateKey;
    pageNumber: number;
    pageCount: number;
}

export interface ManagedTransfersActivityHistoryElement {
    status: ActivityHistoryStatus;
    type: ActivityHistoryType;
    dollarAmount: number;
    dateCreated: moment.Moment;
    settledDate?: moment.Moment;
    investmentFeeDescription: string;
}

export interface ActivityHistoryPage extends PagedResponse {
    records: ActivityHistoryElement[];
}

export interface ManagedTransfersActivityHistoryPage extends PagedResponse {
    records: ManagedTransfersActivityHistoryElement[];
}

export interface DrivewealthDocumentListPage extends PagedResponse {
    records: DrivewealthDocument[];
}

export interface SweepAllocation {
    symbol: string;
    percentage: number;
    status: InstrumentStatus;
    drivewealthAccountId: number;
    accountType: DrivewealthAccountManagementType;
}

export interface DrivewealthSweep {
    isActive: boolean;
    threshold: number;
    isPreenrollment: boolean;
    isRecommendedThreshold: boolean;
    showRecommendedThreshold: boolean;
    funds: SweepAllocation[];
    v2AutoInvestValidationsEnabled: boolean;
}

export interface SelectedInstrument {
    instrumentId: string;
    stockSymbol: string;
    displaySymbol: string;
    companyName: string;
    website: string;
    imageUrl: string;
    status: InstrumentStatus;
    currentPrice: number;
    allocation: number;
    drivewealthAccountId: number;
    accountType: DrivewealthAccountManagementType;
}

export interface DrivewealthSweepState {
    workingCopy: {
        isActive: boolean;
        threshold: number;
        isRecommendedThreshold: boolean;
        selectedInstruments: SelectedInstrument[];
        isDirty: boolean;
        isInitialized: boolean;
        showAllAdvisoryFunds: boolean;
    };
}

export interface DrivewealthSweepStatus {
    isActive: boolean;
}

export interface DrivewealthSweepStatusPartnerManagement extends DrivewealthSweepStatus {
    cognitoUsername: string;
    partnerId: number;
}

export interface RiskToleranceQuestionnaire {
    startWithdrawing: RiskQuizStartWithdrawing;
    goal: RiskQuizGoal;
    reconsiderStrategy: RiskQuizReconsiderStrategy;
    generalHealth: RiskQuizGeneralHealth;
    sellForExpenses: RiskQuizSellForExpenses;
}

export interface FlatManagedPortfolioResponse {
    portfolioId: string;
    clientId: string;
    name: string;
    description: string;
    score: number;
    instruments: FlatInstrument[];
}

export interface FlatInstrument {
    instrumentId: string;
    target: number;
    fundType: string;
    imageUrl: string;
    fundName: string;
    stockSymbol: string;
    webSite: string;
}

export enum RiskQuizStartWithdrawing {
    TwentyPlus = "twentyPlus",
    SixteenToTwenty = "sixteenToTwenty",
    ElevenToFifteen = "elevenToFifteen",
    SixToTen = "sixToTen",
    ZeroToFive = "zeroToFive"
}

export enum RiskQuizGoal {
    GrowSignificantly = "growSignificantly",
    GrowModestly = "growModestly",
    GrowWithCaution = "growWithCaution",
    ProtectFromLosses = "protectFromLosses"
}

export enum RiskQuizReconsiderStrategy {
    NotReconsider = "notReconsider",
    FifteenThousand = "fifteenThousand",
    TenThousand = "tenThousand",
    FiveThousand = "fiveThousand",
    TwentyFiveHundred = "twentyFiveHundred"
}

export enum RiskQuizGeneralHealth {
    Excellent = "excellent",
    AboveAverage = "aboveAverage",
    Average = "average",
    BelowAverage = "belowAverage",
    Poor = "poor",
}

export enum RiskQuizSellForExpenses {
    Never = "never",
    MarketUp = "marketUp",
    Unlikely = "unlikely",
    Maybe = "maybe",
    Likely = "likely",
}

export enum ContributionScheduleStatus {
    None = "none",
    Active = "active",
    Cancelled = "cancelled"
}

export enum ContributionScheduleType {
    None = "none",
    FirstOfMonth = "firstOfMonth",
    LastOfMonth = "lastOfMonth",
    FirstAndMiddleOfMonth = "firstAndMiddleOfMonth"
}

export interface ScheduledPersonalContribution {
    personalContributionScheduleId: number;
    amount: number;
    contributionScheduleType: ContributionScheduleType;
    status: ContributionScheduleStatus;
    dateScheduledStart: string;
    hasScheduledContributionToday: boolean;
    nextScheduledContribution: ScheduledPersonalContributionNext | null;
    lastContributionForYear: ScheduledPersonalContributionLastForYear | null;
}

export interface ScheduledPersonalContributionNext {
    amount: number;
    date: string;
}

export interface ScheduledPersonalContributionLastForYear {
    amount: number;
    date: string;
}

export interface ScheduledPersonalContributionState {
    fetchState: FetchState;
    scheduledPersonalContribution: ScheduledPersonalContribution;
}

export interface SigningDocument {
    documentId: number;
    version: number;
    name: string;
    content: string;
    agreementCategory: AgreementCategory;
    documentKey: string;
    dateCreated: string;
    contentType: SigningDocumentContentType;
    documentUrl: string;
    pathToFile: string;
}

export interface SignedDocumentKey {
    documentKey: string;
    accountType: DrivewealthAccountManagementType;
    pathToFile: string;
}

export interface SignedDocumentKeyFetchStateDictionary {
    [key: string]: FetchState;
}

export interface DisclosureDocumentLink {
    label: string;
    url: string;
}

export interface DisclosureDocumentLinkDictionary {
    [key: string]: DisclosureDocumentLink;
}

export enum LeafHouseDisclosureDocumentKey {
    ADV = "leafhouse_adv",
    CRS = "leafhouse_crs"
}

export interface SigningDocumentState {
    documentFetchState: FetchState;
    allDocumentsAdminFetchState: FetchState;
    documentsAdmin: SigningDocument[];
    document: SigningDocument;
    signatureFetchState: FetchState;
    signature: DocumentSignature;
    signedDocumentKeys: SignedDocumentKey[];
    signedDocumentKeyFetchState: SignedDocumentKeyFetchStateDictionary;
    custodianDefaultAgreements: CustodianDefaultAgreementModel[];
    custodianDefaultAgreementFetchState: FetchState;
    employerAgreements: EmployerAgreementModel[];
    EmployerAgreementFetchState: FetchState;
    hsabDrivewealthAccountSignatureFetchState: FetchState;
    hsabDrivewealthAccountSignature: DocumentSignature;
    leafHouseDocumentSignaturesFetchState: FetchState;
    leafHouseDocumentSignatures: DocumentSignature[];
}

export interface DocumentSignature {
    documentVersion: number;
    captureName: string;
    signedAt: string;
    pathToFile: string;
    metaData: DocumentSignatureMetaData[];
}

export interface DocumentSaveSignatureRequest {
    documentKey: string;
    captureName: string;
    metaData: DocumentSignatureMetaData[];
    disableNotification?: boolean;
}

export interface DocumentSignatureMetaData {
    key: string;
    value: string;
}

export interface UserPartnerSettings {
    partnerUsesCustomClaims: boolean;
    claimsOptIn: boolean;
    disableTalon: boolean;
    consumerLiveChatEnabled: boolean;
    consumerInitiateCobrowseEnabled: boolean;
    chatFunctionalityType: ChatFunctionalityType;
    isEnrollmentDisabled: boolean;
    disableInvestmentActivity: boolean;
    resourceOptimizationEnabled: boolean;
}

export interface HPHCOptInRequest {
    memberId: string;
    optIn: boolean;
}
export interface ResyncTalonClaimsRequest {
    cognitoUsername: string;
    resyncDate: string;
    partnerId: number;
}
export enum ContributionsViewType {
    Payroll = "payroll",
    Personal = "personal",
    Other = "other"
}

export interface CustomerFormDocumentResponse {
  partnerId: number;
  employerId: number;
  formDocuments: CustomerFormDocument[];
  availablePlanTypes: FormDocumentPlan[];
}
export interface CustomerFormDocument {
  displayName: string;
  downloadUrl: string;
  documentId: number;
  bankCustodian: BankCustodian;
  formDocumentType: FormDocumentType;
  planTypes: FormDocumentPlan[];
}
export interface FormDocumentPlan {
  planType: HsabPlanType;
  resourceFormPlanType: ResourceFormPlanType;
  planName: string;
}
export interface CustomerFormDocumentState {
  fetchState: FetchState;
  formDocuments: CustomerFormDocument[];
  availablePlanTypes: FormDocumentPlan[];
}
export interface CommunicationResponse {
    id: number;
    subject: string;
    message: string;
    actionLink: string;
    actionText: string;
    imageSrc: string;
    imageAlt: string;
    isPrimary: boolean;
}

export interface CommunicationState {
    fetchState: FetchState;
    records: CommunicationResponse[];
    previewFetchState: FetchState;
    previewRecord: CommunicationResponse;
    partnerEmployerList: PartnerEmployerList;
    partnerEmployerListFetchState: FetchState;
}

export interface FundingOptionResponse {
    fundingOption: FundingOption;
}

export interface EnrollmentOptionResponse {
    enrollmentOption: EnrollmentOption;
}

export enum UserEventType {
    Unknown = "unknown",
    PartnerCommunicationWidgetClick = "partnerCommunicationWidgetClick"
}

export enum UserEventKey {
    Unknown = "unknown",
    PartnerCommunicationId = "partnerCommunicationId",
    PartnerCommunicationSubject = "partnerCommunicationSubject",
    PartnerCommunicationLocation = "partnerCommunicationLocation",
    SellingPartnerId = "sellingPartnerId",
    ActiveEmployerId = "activeEmployerId"
}

export interface UserEventKeyDictionary {
    [key: string]: string;
}

export interface UserEventModel {
    userEventKey: UserEventKey;
    values: UserEventKeyDictionary;
}

export interface PartnerContentState {
    partnerContent: Record<PartnerContentType, string>;
    userPartnerContent: string;
    partnerFooterContent: string;
    partnerPostLoginFooterContent: string;
    partnerId: number;
    partnerContentFetchState: FetchState;
    userPartnerContentFetchState: FetchState;
    partnerFooterContentFetchState: FetchState;
    partnerPostLoginFooterContentFetchState: FetchState;
}

export interface PartnerThemeState {
    partnerTheme: PartnerThemeResponse;
    partnerThemeFetchState: FetchState;
}

export interface ThemeState {
    colorStyle: string;
    colorValues: ColorValues;
    fontFace: string;
    pageNumber: number;
    totalRecords: number;
    themePages: ManageThemesResponse[][];
    themes: ManageThemesResponse[];
    themeFetchState: FetchState;
    partnerThemeState: PartnerThemeState;
    activeThemeFetchState: FetchState;
    activeThemes: ManageThemesResponse[];
    employerThemeState: EmployerThemeState;
}

export interface ColorValues {
    primary: string;
    primaryFont: string;
    secondary: string;
    secondaryFont: string;
    tertiary: string;
    tertiaryFont: string;
    button: string;
    buttonFont: string;
    navButton: string;
    navButtonFont: string;
    navButtonInverse: string;
    navButtonFontInverse: string;

}

export interface RgbColor {
    r: number;
    g: number;
    b: number;
}

export interface ColorShifts {
    light: string;
    lighter: string;
    lightest: string;
    dark: string;
    darker: string;
    darkest: string;
}

export enum AssetType {
    None = "None",
    Logo = "Logo",
    DebitCardImage = "DebitCardImage"
}

export enum FamilyMemberRelationship {
    None = "none",
    Spouse = "spouse",
    Child = "child",
    Other = "other",
    Dependent = "dependent"
}

export const familyMemberRelationshipMap: Record<FamilyMemberRelationship, string> = {
    none: "None",
    spouse: "Spouse",
    child: "Child",
    other: "Other",
    dependent: "Dependent"
};

export enum FamilyMemberStatus {
    None = "none",
    Active = "active",
    Inactive = "inactive"
}

export enum SftpVendorTypeOption {
    None = "none",
    Direct = "direct",
    Paylocity = "paylocity",
    Paycor = "payCor",
    Trinity = "trinity"
}

export interface SftpVendorResponse {
    sftpVendorType: SftpVendorTypeOption;
    sftpMappings: SftpMapping[];
}

export interface SftpMapping {
    sftpMapId: number;
    sftpMapCode: string;
}

export interface OutageNotification {
    enabled: boolean;
    message: string;
}

export interface OutageNotificationState {
    fetchState: FetchState;
    outageNotification: OutageNotification;
}

export enum IdentityProofingOptionType {
    Email = "email",
    Sms = "sms",
    Voice = "voice",
    DataEntry = "dataEntry"
}

export interface IdentityOption {
    optionType: IdentityProofingOptionType;
    displayValue: string;
    priorityOrder: string;
}

export interface IdentityProofingOptionsResponse {
    proofingOptions: IdentityOption[];
}

export interface IdentityProofChallengeRequest {
    username: string;
    identityProofingOptionType: IdentityProofingOptionType;
}

export interface IdentityProofChallengeResponse {
    success: boolean;
}

export interface MfaEmailChallengeRequest {
    bendUserId: string;
    email: string;
}

export interface MfaEmailChallengeResponse {
    success: boolean;
}

export interface HsabEmailChangeRequest {
    email: string;
}

export interface HsabEmailChangeResponse {
    success: boolean;
}

export interface HsabEmailChangeVerifyRequest {
    verificationCode: string;
}

export interface HsabEmailChangeVerifyResponse {
    success: boolean;
}

export interface MfaEmailVerifyRequest {
    bendUserId: string;
    verificationCode?: string;
    identityProofingOptionType: IdentityProofingOptionType;
    isMobileApp: boolean;
}

export interface IdentityProofVerifyRequest {
    username: string;
    verificationCode?: string;
    isMobileApp: boolean;
    identityProofingOptionType: IdentityProofingOptionType;
}

export interface IdentityProofDataVerifyRequest {
    username: string;
    data: IdentityProofDataEntryModel;
    isMobileApp: boolean;
}

export interface UserContactVerifiedResponse {
    bendUserId: string;
    signature: string;
    contactInfo: ClaimHsabAccountContactInfo;
}

export interface ClaimHsabAccountContactInfo {
    firstName: string;
    middleInitial: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    mobilePhoneNumber: string;
    zip: string;
}

export interface IdentityProofDataEntryModel {
    ssn: string;
    dateOfBirth: string;
    zip: string;
}

export const bankCustodianMap: Record<BankCustodian, string> = {
    none: "None",
    umb: "UMB",
    elements: "Elements",
    ucb: "UCB",
    avidia: "Avidia",
    allegacy: "Allegacy",
    hsaBank: "HSA Bank",
    omnibus: "Omnibus"
};

export interface ClaimHsabAccountRequest {
    bendUserId: string;
    identityProofingOptionType: IdentityProofingOptionType;
    signature: string;
    username: string;
    password: string;
    email: string;
    isMobileApp: boolean;
}

export interface CredentialFlowState {
    bendUserId: string;
    phone: string;
    mobilePhone: string;
    username: string;
    email: string;
    accountNumber: string;
    identityOptionsFetchState: FetchState;
    identityOptions: IdentityOption[];
    selectedIdentityOption: IdentityOption;
    signature: string;
    isEmailVerified: {
        [email: string]: boolean
    };
}

export interface NotificationPreferenceUpdateRequest {
    notificationPreferences: NotificationPreference[];
}

export interface NotificationPreferencesResponse {
    notificationPreferences: NotificationPreference[];
}

export interface NotificationPreference {
    preferences: Preference[];
    category: string;
    categoryDescription: string;
}

export interface Preference {
    notificationPreferenceType: string;
    name: string;
    configurations: PreferenceConfiguration[];
    threshold: Threshold | null;
}

export interface PreferenceConfiguration {
    communicationDeliveryType: string;
    enabled: boolean;
    optedIn: boolean;
}

export interface Threshold {
    value?: number | string | null;
}

export interface NotificationPreferencesState {
    notificationPreferencesFetchState: FetchState;
    notificationPreferences: NotificationPreference[];
    notificationPreferencesSnapshot: NotificationPreference[];
    hasSavedNotificationPreferences: boolean;
    updatedPreferences: NotificationPreference[];
    isSavingPreferences: boolean;
}

export const CommunicationDeliveryTypeMap: Record<string, string> = {
    EMAIL: "Email",
    SMS: "SMS Text",
    PAPER: "Mail",
};

export enum CommunicationDeliveryType {
    Email = "EMAIL",
    Sms = "SMS",
    Paper = "PAPER",
}

export enum NotificationPreferenceCategory {
    DebitCards = "DEBIT_CARDS",
    Claims = "CLAIMS",
    Statements = "STATEMENTS",
    Contributions = "CONTRIBUTIONS",
    Payments = "PAYMENTS",
}

export const NotificationPreferenceCategoryConfig = {
    DEBIT_CARDS: {
        categoryDescription: "Debit Card"
    },
};

export const NotificationPreferenceTypeConfig = {
    RECEIPT_REMINDER: {
        name: "Claim receipt required",
        info: "A receipt is needed to process a claim. Upload documentation to avoid delays.",
    },
    HOLD_CLAIM_REQUIRING_ATTESTATION_NOTIFICATION: {
        name: "Coverage verification required",
        info: "More information is required before we can continue processing a claim.",
    },
    DENIAL_LETTER: {
        name: "Claim denied",
        info: "A claim couldn’t be processed because it didn’t meet the required criteria.",
    },
    EXPENSE_NOTIFICATION: {
        name: "New expense",
        info: "A new expense may need more information or action before it can be processed.",
    },
    CLAIM_TO_REPAYMENT: {
        name: "Claim applied to repayment",
        info: "A claim amount was used to repay a previous payment or advance instead of being sent to you.",
    },
    DENIAL_LETTER_WITH_REPAYMENT: {
        name: "Paid claim denied",
        info: "A paid claim was denied and may require repayment.",
    },
    RECURRING_CLAIM_COVERAGE_ATTESTATION: {
        name: "Verify coverage for recurring claim",
        info: "Coverage information is required to keep payments on track.",
    },
    CLAIM_CONFIRMATION: {
        name: "Claim filed",
        info: "A claim has been submitted and is being reviewed.",
    },
    HSA_TAX_DOCUMENTS: {
        name: "HSA tax documents",
        info: "HSA tax documents are available.",
    },
    HSA_ACCOUNT_SUMMARY: {
        name: "HSA account summary",
        info: "An HSA account summary is available.",
    },
    HSA_PAYMENT_ISSUED_NOTIFICATION: {
        name: "HSA payment issued",
        info: "A payment was issued out of your HSA.",
    },
    PAYMENT_NOTIFICATION: {
        name: "Spending account payment issued",
        info: "A payment was issued out of your spending account.",
    },
    HSA_WITHDRAWAL_WARNING: {
        name: "HSA distribution exceeds:",
        info: "A distribution exceeds the amount you set.",
    },
    HSA_CONTRIBUTION_NOTIFICATION: {
        name: "HSA contribution posted",
        info: "Funds from a recent HSA contribution are available.",
    },
    HRA_CONTRIBUTION_NOTIFICATION: {
        name: "HRA contribution posted",
        info: "Funds from a recent HRA contribution are available.",
    },
    HSA_CASH_BALANCE_WARNING: {
        name: "HSA cash balance less than:",
        info: "HSA cash balance is below the amount that you set.",
    },
    HSA_CONTRIBUTION_MAXIMUM_WARNING: {
        name: "HSA year-to-date contributions nearing limit",
        info: "HSA year-to-date contributions are nearing the IRS maximum within the amount you set.",
    },
    DEBIT_CARD_FOLLOW_UP_NOTIFICATION: {
        name: "Receipt required for debit card transaction",
        info: "A receipt is needed to process a recent debit card purchase.",
    },
    DEBIT_CARD_MAILED_NOTIFICATION: {
        name: "Debit card shipped",
        info: "A new debit card was shipped to the mailing address on file.",
    },
    DEBIT_CARD_PURCHASE_NOTIFICATION: {
        name: "Debit card purchase",
        info: "A debit card transaction was made.",
    },
    DEBIT_CARD_PURSE_SUSPEND_UNSUSPEND_NOTIFICATION: {
        name: "Debit card funds status change",
        info: "Debit card funds were suspended or unsuspended.",
    },
    DEBIT_CARD_SUSPEND_UNSUSPEND_NOTIFICATION: {
        name: "Debit card status change",
        info: "Your debit card was suspended or unsuspended.",
    },
    DEDUCTIBLE_MET: {
        name: "HRA deductible met",
        info: "The HRA deductible was met and your debit card is now available to use.",
    },
    ON_DEMAND_NOTIFICATION: {
        name: "General notification",
        info: "A general account notification.",
    },
    ELIGIBLE_FOR_INVESTMENT_ACCOUNT: {
        name: "Eligible to invest",
        info: "The minimum HSA cash balance threshold was met. HSA funds are now eligible to be invested.",
    },
    SMART_COMMUTE_ORDER_CONFIRMATION: {
        name: "Smart Commute order confirmation",
        info: "A Smart Commute order was confirmed.",
    },
    SMART_COMMUTE_ORDER_NOT_PROCESSED: {
        name: "Smart Commute order not completed",
        info: "A Smart Commute order couldn’t be processed.",
    },
    SMART_COMMUTE_ORDER_UPDATE: {
        name: "Smart Commute order update",
        info: "Updates related to your Smart Commute order.",
    },
    SMART_COMMUTE_ORDER_RECURRENCE_SCHEDULE: {
        name: "Smart Commute recurring order schedule",
        info: "A Smart Commute recurring order schedule is set.",
    },
    SMART_COMMUTE_ORDER_RECURRENCE_SCHEDULE_EXPIRATION: {
        name: "Smart Commute recurring order schedule expire",
        info: "A Smart Commute recurring schedule expired and may need updates.",
    },
    SMART_COMMUTE_ORDER_PRICE_CHANGE: {
        name: "Smart Commute order price change",
        info: "The price of a Smart Commute order has changed. Review the new amount.",
    },
    FSA_FINAL_FILING_DATE_NOTIFICATION: {
        name: "FSA final filing date",
        info: "Details about the final filing date for your FSA and your remaining balance.",
    },
};

export enum NotificationPreferenceType {
    HsaCashBalanceWarning = "HSA_CASH_BALANCE_WARNING",
    HsaCashMaximumBalanceWarning = "HSA_CONTRIBUTION_MAXIMUM_WARNING",
    HsaWithdrawalWarning = "HSA_WITHDRAWAL_WARNING",
}

export enum FontType {
    Avenir = "avenir",
    Roboto = "roboto",
    Poppins = "poppins",
}

export interface ManageThemesRequest {
    themeId: number;
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
    tertiaryColor: string;
    primaryFontColor: string;
    secondaryFontColor: string;
    tertiaryFontColor: string;
    fontType: FontType;
    isButtonColorPrimary: boolean;
    isNavButtonColorSecondary: boolean;
    isActive: boolean;
}

export interface ManageThemesResponse {
    themeId: number;
    themeName: string;
    primaryColor: string;
    secondaryColor: string;
    tertiaryColor: string;
    primaryFontColor: string;
    secondaryFontColor: string;
    tertiaryFontColor: string;
    fontType: FontType;
    isButtonColorPrimary: boolean;
    isNavButtonColorSecondary: boolean;
    isActive: boolean;
    dateCreated: string;
    dateLastModified: string;
    outcome: string;
}

export interface ThemePagedResponse {
    totalRecords: number;
    pageSize: number;
    pageNumber: number;
    themeList: ManageThemesResponse[];
}

export interface ThemeSearchRequest {
    pageNumber: number;
    pageSize: number;
    searchTerm: string;
}

export interface ThemeGetRequest {
    employerId: number;
    partnerId: number;
    forceUpdate: boolean;
}

export enum HsabAccountType {
    None = "none",
    Hsa = "hsa",
    Notional = "notional"
}

export enum HsabPlanType {
    Unknown = "unknown",
    MedicalFlex = "medicalFlex",
    DependentCare = "dependentCare",
    HealthReimbursementAccount = "healthReimbursementAccount",
    HealthSavingAccount = "healthSavingAccount",
    CommuterTransit = "commuterTransit",
    CommuterParking = "commuterParking",
}

export enum HsabPlanSubType {
    None = "none",
    RetirementHra = "retirementHra",
    LifestyleSpendingAccount = "lifestyleSpendingAccount",
    WellnessHra = "wellnessHra",
    AdoptionFsa = "adoptionFsa",
    LimitedPurposeFsa = "limitedPurposeFsa",
    LimitedPurposeHra = "limitedPurposeHra",
    PostDeductibleFsa = "postDeductibleFsa",
    EducationHra = "educationHra"
}
export const planTypeMap: Record<HsabPlanType, string> = {
    unknown: "Unknown",
    medicalFlex: "Medical Flex",
    dependentCare: "Dependent Care",
    healthReimbursementAccount: "HRA",
    healthSavingAccount: "HSA",
    commuterTransit: "Transit",
    commuterParking: "Parking"
};

export const planSubTypeMap: Record<HsabPlanSubType, string> = {
    none: "",
    retirementHra: "Retirement HRA",
    lifestyleSpendingAccount: "Lifestyle Spending Account",
    wellnessHra: "Wellnes HRA",
    limitedPurposeFsa: "Limited Purpose FSA",
    limitedPurposeHra: "Limited Purpose HRA",
    postDeductibleFsa: "Post Deductible FSA",
    adoptionFsa: "Adoption FSA",
    educationHra: "Education HRA"
};

export enum CommunicationAccount {
    hsa = "hsa",
    nonHsa = "nonHsa",
    bothHsaAndNonHsa = "bothHsaAndNonHsa"
}

export interface HsabAccountNametag {
    accountId: number;
    accountName: string;
    bankAccountType: HsabAccountType;
    planType: HsabPlanType;
    planSubType: HsabPlanSubType;
    accountBalance: number;
    actualAccountBalance: number;
    isActive: boolean;
    balanceDate: string;
    startDate: string;
    endDate: string;
    finalFilingDate: string;
    finalServiceDate: string;
    isPostServiceDate: boolean;
    allowHealthcareClaims: boolean;
    isSmartCommutePlan: boolean;
    allowAutoClaims: boolean;
    offeringId: number;
}

export interface UserHasNotionalAccounts {
    hasAnyNotionalAccount: boolean;
    hasActiveNotionalAccount: boolean;
}

export interface NotionalBalanceResult {
    dateOfAvailableBalance: string;
    availableBalance: number;
    planType: HsabPlanType;
    planName: string;
    records: HsaDailyBalance[];
}

export interface HsabAccountTypesResponse {
    userShouldBeSentToWex: boolean;
    userHasDebitCardAccess: boolean;
    employerName: string;
    planTypes: HsabPlanType[];
    accounts: HsabAccountNametag[];
}

export enum HsabNotionalClaimStatus {
    Applied = "Not Applicable",
    Denied = "Not Needed",
    Freeze = "Received",
    Hold = "Required",
    NotApplicable = "Uploaded",
    Paid = "Paid",
    PendingReceipt = "Pending Receipt",
    PendingReimbursement = "Pending Reimbursement",
}

export enum HsabNotionalReceiptStatus {
    NotApplicable = "Not Applicable",
    NotNeeded = "Not Needed",
    Received = "Received",
    Required = "Required",
    Uploaded = "Uploaded",
}

export interface HsabAccountDisplayType {
    isWexOnlyAccount: boolean;
    isHsabAccountSelectionVisible: boolean;
    notionalDashboardRedirect: boolean;
}

export interface HsabAccountDisplayTypeBatch {
    username: string;
    isWexOnlyAccount: boolean;
    isHsabAccountSelectionVisible: boolean;
    usersAreAutomaticallyRedirectedToWex: boolean;
}

export interface HsabAccountsState {
    fetchState: FetchState;
    accounts: HsabAccountNametag[];
    employerName: string;
    userShouldBeSentToWex: boolean;
    planTypes: HsabPlanType[];
    notionalAccountsFetchState: FetchState;
    userHasNotionalAccounts?: UserHasNotionalAccounts;
    accountDisplayTypeFetchState: FetchState;
    canPerformWithdrawalFunctionFetchState: FetchState;
    canPerformWithdrawalFunction: boolean;
    accountDisplayType: Partial<HsabAccountDisplayType>;
    notionalDashboardIsActive: boolean;
    allowedRoutes: string[];
    notionalBalances: {
        [key: string]: NotionalBalanceResult
    };
    notionalBalancesFetchState: {
        [key: string]: FetchState
    };
    userHasDebitCardAccess: boolean;
    depositAccessFetchState: FetchState;
    canDeposit: boolean;
}

export interface EmployerThemeState {
    employerTheme: EmployerThemeResponse;
    employerThemeFetchState: FetchState;
}

export interface ForgotUsernameRequest {
    dateOfBirth: string;
    zip: string;
    lastFourSsn: string;
    username?: string;
}

export interface ForgotUsernameResponseModel {
    username: string;
    employerName: string;
    dateAccountOpened: string;
}

export interface ForgotUsernameResponse {
    sendToCredentialingFlow: boolean;
    multipleAccountExists: boolean;
    records: ForgotUsernameResponseModel[];
}

export interface UsernameValidateRequest {
    username: string;
    bendUserId: string;
}

export interface OktaEnrollFactorRequest {
    factorType: string;
    profile: OktaFactorProfile;
}

export interface OktaRevertFactorRequest {
    factorType: string;
    factorId: string;
}

export interface AssetRequest {
    employerId: number;
    partnerId: number;
}

export interface BankAccountVerificationAccountHolderResponse {
    bankAccountVerificationAccountHolderId: number;
    userLinkedExternalBankAccountId: number;
    relationship: string;
    ownerName: string;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    ownerNameScore: number;
    firstNameScore: number | null;
    middleNameScore: number | null;
    lastNameScore: number | null;
    suffixScore: number | null;
    nameClassification: string;
    nameClassificationConfidenceScore: number | null;
    asOfDate: string | null;
    dateCreated: string;
    dateLastModified: string;
    bankAccountVerificationAccountHolderAddresses: BankAccountVerificationAccountHolderAddressResponse[];
    bankAccountVerificationAccountHolderEmails: BankAccountVerificationAccountHolderEmailResponse[];
    bankAccountVerificationAccountHolderPhones: BankAccountVerificationAccountHolderPhoneResponse[];
}

export interface BankAccountVerificationAccountHolderAddressResponse {
    bankAccountVerificationAccountHolderAddressId: number;
    ownerAddress: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    city: string;
    state: string;
    country: string;
    zipCode: string;
    addressType: string;
    ownerAddressScore: number | null;
    addressTypeScore: number | null;
    addressLine1Score: number | null;
    addressLine2Score: number | null;
    addressLine3Score: number | null;
    cityScore: number | null;
    stateScore: number | null;
    countryScore: number | null;
    zipCodeScore: number | null;
    dateCreated: string;
    dateLastModified: string;
}

export interface BankAccountVerificationAccountHolderEmailResponse {
    bankAccountVerificationAccountHolderEmailId: number;
    isPrimary: boolean | null;
    email: string;
    emailType: string;
    emailScore: number | null;
    dateCreated: string;
    dateLastModified: string;
}

export interface BankAccountVerificationAccountHolderPhoneResponse {
    bankAccountVerificationAccountHolderPhoneId: number;
    phoneType: string;
    country: string;
    phone: string;
    phoneScore: number | null;
    dateCreated: string;
    dateLastModified: string;
}

export enum HsabSsoDestination {
    None = "none",
    Dashboard = "DASHBOARD",
    Investments = "ACCOUNTS_INVESTMENTS",
    FileClaim = "HSA_FILE_CLAIM",
    ProfileSummary = "PROFILE_SUMMARY",
    Statements = "STATEMENTS_AND_NOTIFICATIONS",
    FeeScheduleAndRates = "TOOLS_AND_SUPPORT",
    CashAccountDetails = "HSA_CASH_ACCOUNT_DETAILS",
    AccountActivity = "ACCOUNT_ACTIVITY",
    AccountClaim = "ACCOUNTS_CLAIMS",
    AccountPayments = "ACCOUNTS_PAYMENTS",
    ReceiptsNeeded = "RECEIPTS_NEEDED",
    LinkHealthcareClaimData = "LINK_HEALTHCARE_CLAIM_DATA",
    MessageCenter = "MESSAGE_CENTER",
    Repayments = "REPAYMENTS",
    DistributeFund = "HSA_DISTRIBUTE_FUNDS",
    BankingCards = "BANKING_CARDS",
    Home = "",
    SmartCommute = "SMARTCOMMUTE",
    ContributeToHSA = "CONTRIBUTE_TO_HSA"
}

export enum HsabSsoDestinationParameters {
    ClaimNumber = "CLAIM_NUMBER",
    Offering = "OFFERING_ID",
}

export interface HsaBankSsoLocationName {
    locationName: HsabSsoDestination;
    isMobileApp: boolean;
    urlOfPageWithButton: string;
    locationParameters?: HsaBankSsoLocationParameter[];
}
export interface UserSearchDocument {
    cognitoUsername: string;
    login: string;
    rowVersion: number;
    firstName: string;
    lastName: string;
    lastNameSort: string;
    nameCompletion: string;
    email: string;
    ssnLast4: string;
    phoneNumber: string;
    mobilePhone: string;
    externalUserKey: string;
    employerEmployeeId: string;
    investmentAccountNumbers: string[];
    isConfirmed: boolean;
    dateCreated: string;
    dateIndexed: string;
    hsaEligibilityDate: string;
    hsaEligibilityEndDate: string;
    hsaTerminationDate: string;
    dateOfBirth: string;
    planType: PlanType;
    planEffectiveDate: string;
    activeEmployerId: number;
    servicingPartners: number[];
    partnerId: number;
    employer: string;
    employerNameSort: string;
    partner: string;
    partnerNameSort: string;
    userId: number;
    isEnrolled: boolean;
    hsaBankAccountNumber: string;
    bankAccountNumbers: string[];
    externalPartnerId: string;
    physicalAddress: Address;
    mailingAddress: Address;
}
export interface EmployerSearchDocument {
    employerId: number;
    rowVersion: number;
    dateCreated: string;
    dateIndexed: string;
    partner: string;
    partnerNameSort: string;
    taxId: string;
    email: string;
    partnerId: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
    phone: string;
    fundingOption: FundingOption;
    isTerminated: boolean;
    externalEmployerId: string;
    enrollmentOption: EnrollmentOption;
    employerStatusType: string;
    employerName: string;
    employerNameSort: string;
    nameCompletion: string;
    servicingPartners: number[];
    sateActivated: string;
    censusManager: CensusManager;
    hasFundingOptionHistory: boolean;
    activationStep: string;
    enrolledEmployeeCount: number;
}
export interface HsaBankSsoLocationParameter {
    parameter: HsabSsoDestinationParameters;
    value: string;
}

export interface HsabSsoLocationResponse {
    samlProviderUrl: string;
}

export interface HsabAccountVerifyRequest {
    bendUserId: string;
    verificationType: AccountVerificationType;
    value: string;
    signature: string;
}

export enum AccountVerificationType {
    accountNumber = "accountNumber",
    cardLastFourDigits = "cardLastFourDigits"
}

export interface HsabAccountVerifyResponse {
    signature: string;
}

export interface MobileAppAllowedPartners {
    partnerId: number;
    partnerName: string;
}

export interface MobileApp {
    mobileAppId: number;
    appIdentifier: string;
    name: string;
    description: string;
    partnerId: number;
    partnerName: string;
    dateCreated: string;
    dateModified: string;
    allowedPartners: MobileAppAllowedPartners[];
    minSuggestedVersion: string;
    minRequiredVersion: string;
    googlePlayStoreUrl: string;
    appleStoreUrl: string;
}

export interface MobileAppAccessCheckResponse {
    isAccessAllowed: boolean;
}

export interface MobileAppSearchRequest {
    pageNumber: number;
    pageSize: number;
    searchTerm: string;
}

export interface MobileAppSearchResponse {
    totalRecords: number;
    pageSize: number;
    pageNumber: number;
    mobileApps: MobileApp[];
}

export interface MobileAppState {
    pageNumber: number;
    totalRecords: number;
    mobileAppPages: MobileApp[][];
    mobileApps: MobileApp[];
    mobileAppFetchState: FetchState;
}

export interface LobAddressValidationRequest {
    address: Address;
    properCase: boolean;
}

export enum LobAddressValidationResult {
    None = "none",
    Confirmed = "confirmed",
    ConfirmedWithSuggestion = "confirmedWithSuggestion",
    NotFound = "notFound"
}

export interface LobAddressValidationResponse {
    result: LobAddressValidationResult;
    suggestedAddress: Address;
    isValid: boolean;
    validationFootnotes: string [];
}

export interface PasswordChangeRequest {
    currentPassword: string;
    newPassword: string;
}

export interface PartnerShowEmployerSignupQuestion {
    showEmployerSignupQuestion: boolean;
}

export interface BankUserAccountNumber {
    accountNumber: string;
}

export interface EbtoRequest {
    campaignId: string;
    firstName: string;
    lastName: string;
    last4Ssn: string;
    dateOfBirth: string;
}

export interface EbtoEmailRequest {
    campaignId: string;
    email: string;
    userId: number;
    decision: CampaignDecision;
}

export enum CampaignDecision {
    NotDecided = "notDecided",
    Stay = "stay",
    TransferOut = "transferOut"
}

export interface EbtoResponse {
    email: string;
    phone: string;
    userId: number;
    decision: CampaignDecision;
    userLocked: boolean;
    lockEndTime: string;
}

export interface EbtoValidationResponse {
    isValid: boolean;
}

export interface EbtoCampaign {
    id: number;
    campaignId: string;
    newCustodian: string;
    newCustodianEffectiveDate: string;
    dateElectionStart: string;
    dateElectionEnd: string;
    dateDistributionsEnd: string;
    dateCutOff: string;
    dateTransfer: string;
    datePricingOffer: string;
    bonusAmount: number;
    isAdminFeeWaived: boolean;
    employerName: string;
}

export enum EbtoStep {
    Claim = "Claim",
    GeneralInformation = "GeneralInformation",
    PricingOffer = "PricingOffer",
    Retained = "Retained",
    TransferProcess = "TransferProcess",
    ESign = "ESign",
    TransferOutConfirmation = "TransferOutConfirmation"
}

export interface EbtoUserFinalizeRequest {
    userId: number;
    eSignName: string;
    decision: CampaignDecision;
}

export interface EbtoState {
    userSubmissionFetchState: FetchState;
    campaignFetchState: FetchState;
    esignFetchState: FetchState;
    offerFetchState: FetchState;
    verificationEmailFetchState: FetchState;
    campaignId: string;
    isValidCampaign: boolean;
    currentEbtoStep: EbtoStep;
    campaign: EbtoCampaign;
    hasEsigned: boolean;
    claimUser: EbtoResponse;
    claimUserFetchState: FetchState;
    confirmationEmailFetchState: FetchState;
    userFlowComplete: boolean;
    autoLogout: boolean;
    isInvalidUser: boolean;
    isVerified: boolean;
    isVerificationLocked: boolean;
}

export interface EbtoVerifyUserRequest {
    userId: number;
    verificationType: number;
    verificationCode: string;
}

export enum EbtoTransferStep {
    Provider,
    HaltDates,
    EnrollmentDates,
    EnrollmentTerms,
    ESign,
    Confirm,
    Blackout
}

export enum EplItemStatus {
    None = "none",
    Eligible = "eligible",
    EligibleOtc = "eligibleOtc",
    DualEligible = "dualEligible",
    PrivateLabel = "privateLabel",
    NotEligible = "notEligible",
    NotFound = "notFound",
    Invalid = "invalid"
}

export enum EplLookupMethod {
    Unknown = "unknown",
    Scan = "scan",
    ManualEntry = "manualEntry"
}

export interface EplResponse {
    status: EplItemStatus;
    statusMessage: string;
}

export interface ProductScannerState {
    fetchState: FetchState;
}

export interface EplItemStatusIcon {
    icon: string;
    color: string;
}

export enum EplItemStatusType {
    GreenCheck = "greenCheck",
    OrangeExclamation = "orangeExclamation",
    RedXMark = "redXMark"
}

export const eplItemStatusIcons: Record<EplItemStatusType, EplItemStatusIcon> = {
    greenCheck: {
        icon: "fa-circle-check",
        color: "green"
    },
    orangeExclamation: {
        icon: "fa-triangle-exclamation",
        color: "orange"
    },
    redXMark: {
        icon: "fa-circle-xmark",
        color: "red"
    }
};

export const eplItemStatusIconMap: Record<EplItemStatus, EplItemStatusIcon> = {
    none: eplItemStatusIcons.orangeExclamation,
    eligible: eplItemStatusIcons.greenCheck,
    eligibleOtc: eplItemStatusIcons.greenCheck,
    dualEligible: eplItemStatusIcons.orangeExclamation,
    privateLabel: eplItemStatusIcons.orangeExclamation,
    notEligible: eplItemStatusIcons.redXMark,
    notFound: eplItemStatusIcons.redXMark,
    invalid: eplItemStatusIcons.orangeExclamation
};

export enum DevicePlatform {
    Unknown = "unknown",
    Browser = "browser",
    iOS = "ios",
    Android = "android",
    Windows = "windows",
    macOS = "macOS",
    Linux = "linux",
    ChromeOS = "chromeOS"
}

export interface LogEmployeeLoginRequest {
    platform: DevicePlatform;
    application: string;
    appVersion: string;
    isMobile: boolean;
    loginMethod: LoginMethod;
}

export interface AppInfo {
    platform: DevicePlatform;
    name: string;
    version: string;
    isMobile: boolean;
}

export interface MobileAppVersion {
    minimumSuggested: string;
    minimumRequired: string;
    googlePlayStoreUrl: string;
    appleStoreUrl: string;
}

export enum UserFeedbackEventAction {
    None = "none",
    AccountSummary = "accountSummary",
    TaxForm = "taxForm",
    ProductScanner = "productScanner",
    AddExpense = "addExpense",
    UploadReceipt = "uploadReceipt",
    SingleContribution = "singleContribution",
    AddRecurringContribution = "addRecurringContribution",
    EditRecurringContribution = "editRecurringContribution",
    RequestReimbursement = "requestReimbursement",
    MyProfile = "myProfile",
    Security = "security",
    BiometricSettings = "biometricSettings",
    ContributionPlanner = "contributionPlanner",
    EditProfile = "editProfile",
    AddAuthorizedSigner = "addAuthorizedSigner",
    UpdateAuthorizedSigner = "updateAuthorizedSigner",
    AddBeneficiary = "addBeneficiary",
    AddDependent = "addDependent",
    UpdateDependent = "updateDependent"
}

export enum UserFeedbackEventType {
    Unknown = "unknown",
    No = "no",
    Yes = "yes",
    Dismiss = "dismiss"
}

export enum UserFeedbackSatisfactionLevel {
    None = "none",
    VeryDissatisfied = "veryDissatisfied",
    Dissatisfied = "dissatisfied",
    Neutral = "neutral",
    Satisfied = "satisfied",
    VerySatisfied = "verySatisfied"
}

export interface UserFeedbackResponse {
    satisfactionLevel: UserFeedbackSatisfactionLevel;
    content?: string;
    contactEmail?: string;
    accountTypes?: string;
    platform?: DevicePlatform;
    application?: string;
    appVersion?: string;
}

export interface UserFeedbackTriggerModel {
    eligibleActions: UserFeedbackEventAction[];
}

export interface UserFeedbackState {
    triggerActions: UserFeedbackEventAction[];
    userFeedbackActions: UserFeedbackEventAction[];
    shouldPrompt: boolean;
    fetchState: FetchState;
    isRouteReady: boolean;
    userFeedback: UserFeedbackResponse;
}

export interface SecureDocumentUploadState {
  categories: FileUploadCategoryModel [];
  fetchState: FetchState;
}

export interface UserFeedbackSaveRequest extends UserFeedbackSaveEventWithResponseModel {
    action: UserFeedbackEventAction;
}

export interface UserFeedbackSaveEventWithResponseModel extends UserFeedbackSaveEventModel {
    userFeedback?: UserFeedbackResponse;
}

export interface UserFeedbackSaveEventModel {
    response: UserFeedbackEventType;
}

export enum UserHsaAccountStatus {
    NoAccount = "noAccount",
    Open = "open",
    ClosedAtPartnerBank = "closedAtPartnerBank"
}

export const accountStatusMap: Record<UserHsaAccountStatus, string> = {
    noAccount: "No Account",
    open: "Open",
    closedAtPartnerBank: "Closed"
};

export interface HsabHsaAccountTransactionState {
    fetchStateDictionary: HsabTransactionFetchStateDictionary;
    fetchKey: FetchStateKey;
    transactionsDictionary: HsabTransactionDictionary;
    page: number;
    transactionFileExportFetchState: FetchState;
    flowIdAssociations: { [key: string]: FlowIdUsageResponse };
}

export interface FlowIdUsageResponse {
    flowId: string;
    reimbursementId: number | null;
    hsabExpenseId: number | null;
    hsabExpenseReimbursementId: number | null;
    hasAnyAssociation: boolean;
}

export interface HsabTransactionDictionary {
    [key: string]: HsabTransactionDetail[];
}

export interface HsabTransactionFetchStateDictionary {
    [key: number]: FetchState;
}

export interface HsabNotionalTransactionState {
    fetchStateDictionary: HsabNotionalTransactionFetchStateDictionary;
    transactionsDictionary: HsabNotionalTransactionDictionary;
    page: number;
    transactionFileExportFetchState: { [accountId: number]: FetchState };
}


export interface HsabNotionalTransactionDictionary {
    [accountId: number]: HsabTransactionDetail[];
}

export interface HsabNotionalTransactionFetchStateDictionary {
    [accountId: number]: FetchState;
}

export interface HsabTransactionDetail {
    effectiveDate: string;
    postingDate: string;
    type: string;
    creditDebitIndicator: string;
    description: string;
    amount: number;
    runningBalance: number;
    checkNumber: string;
    claimPaymentMethod: string;
    claimNumber: string;
    eobNumber: string;
    receiptStatus: string;
    claimReceiptStatus: HsabExpenseDisplayStatus;
    claimsStatus: string;
    consumerNote: string;
    paymentDate: string;
    paymentStatus: string;
    flowId: string;
    systemTransactionID: number;
    isPosted: boolean;
}

export enum FileUploadCategory {
  Unknown = "unknown",
  IdentityVerification = "identityVerification",
  Forms = "forms"
}

export interface FileUploadCategoryModel {
  category: FileUploadCategory;
  displayName: string;
  maxFiles: number;
  fileSize: number;
  allowedFileTypes: string[];
}

export interface FileUploadCategoryResponse {
  categories: FileUploadCategoryModel[];
}

export interface FileUploadErrorModel {
  fileName: string;
  message: string;
}

export interface FileUploadResponse {
  success: boolean;
  errors: FileUploadErrorModel[];
}

export enum ResourceFormPlanType {
  Unknown = "unknown",
  MedicalFlex = "medicalFlex",
  DependentCare = "dependentCare",
  HealthReimbursementAccount = "healthReimbursementAccount",
  HealthReimbursementAccountDeductibles = "healthReimbursementAccountDeductibles",
  HealthReimbursementAccountDental = "healthReimbursementAccountDental",
  HealthReimbursementAccountVision = "healthReimbursementAccountVision",
  HealthSavingAccount = "healthSavingAccount",
  IndividualPremium = "individualPremium",
  CommuterTransit = "commuterTransit",
  CommuterParking = "commuterParking",
  VoluntaryEmployeesBeneficiaryAssociation = "voluntaryEmployeesBeneficiaryAssociation",
  RetirementHra = "retirementHra",
  LifestyleSpendingAccount = "lifestyleSpendingAccount",
  WellnessHra = "wellnessHra",
  AdoptionFsa = "adoptionFsa",
  LimitedPurposeFsa = "limitedPurposeFsa",
  LimitedPurposeHra = "limitedPurposeHra",
  PostDeductibleFsa = "postDeductibleFsa",
  EducationHra = "educationHra"
}
export const resourceFormPlanTypeMap: Record<ResourceFormPlanType, string> = {
    unknown: "Unknown",
    medicalFlex: "Medical Flex",
    dependentCare: "Dependent Care",
    healthReimbursementAccount: "HRA",
    healthReimbursementAccountDeductibles: "healthReimbursementAccountDeductibles",
    healthReimbursementAccountDental: "healthReimbursementAccountDental",
    healthReimbursementAccountVision: "healthReimbursementAccountVision",
    healthSavingAccount: "HSA",
    individualPremium: "Individual Premium",
    commuterTransit: "Transit",
    commuterParking: "Parking",
    voluntaryEmployeesBeneficiaryAssociation: "VEBA",
    retirementHra: "Retirement HRA",
    lifestyleSpendingAccount: "Lifestyle Spending Account",
    wellnessHra: "Wellnes HRA",
    limitedPurposeFsa: "Limited Purpose FSA",
    limitedPurposeHra: "Limited Purpose HRA",
    adoptionFsa: "Adoption FSA",
    postDeductibleFsa: "Post Deductibe FSA",
    educationHra: "Education HRA"
};

export interface FormDocumentOptOutRequest {
  formDocumentId: number;
  isOptOut: boolean;
}
export interface PartnerFormDocumentResponse {
  partnerId: number;
  isSellingPartner: boolean;
  bankCustodian: number;
  documents: ResourceFormDocument[];
  partnersServiced: PartnerIdAndName[];
  supportedPlanTypes: ResourceFormPlanType[];
  isEmployerExclusionList: boolean;
  isAdminPartner: boolean;
}

export interface ResourceFormDocumentBase {
  formDocumentId: number;
  downloadUrl: string;
  displayName: string;
 resourceFormPlanTypes: ResourceFormPlanType[];
  dateAvailableStart: string;
  dateAvailableEnd: string | null;
  formDocumentType: FormDocumentType;
  sellingPartnerIds: number[];
  employers: CommunicationEmployerGroup[];
  isEmployerExclusionList: boolean;
}

export interface ResourceFormDocumentSavePayload {
  FormDocumentId: number;
  DownloadUrl: string;
  DisplayName: string;
  ResourceFormPlanTypes: ResourceFormPlanType[];
  DateAvailableStart: string;
  DateAvailableEnd: string | null;
  FormDocumentType: FormDocumentType;
  SellingPartnerIds: number[];
  Employers: CommunicationEmployerGroup[];
  isEmployerExclusionList: boolean;
}

export enum FormDocumentAssociationType {
  None = 0,
  AdminPartnerCreated = 1,
  SellingPartnerCreated = 2,
  OptOut = 3
}
export enum FormDocumentType {
    None = "none",
    Document = "document",
    Link = "link",
}
export interface ResourceFormDocument extends ResourceFormDocumentBase {
  associations: FormDocumentAssociationType[];
  canOptOut: boolean;
  isOptedOut: boolean;
  isActive: boolean;
}

export interface PartnerIdAndName {
  partnerId: number;
  name: string;
}

export interface RecurringSchedule {
  scheduleType: TransferScheduleType;
  scheduleInterval: number;
  dates?: string[];
  days?: TransferScheduleWeekDay[];
  dayOfWeek?: TransferScheduleWeekDay;
  scheduleMonthBoundary?: TransferScheduleMonthBoundary;
}

export enum ContributionFrequencyType {
  Month = 0,
  Week = 1
}

export interface PersonalContributionSchedulesState {
  personalContributionSchedules: {
    fetchState: FetchState;
    fetchKey: FetchStateKey;
    pageNumber: number;
    schedules: TransferSchedule[];
  };
  futureContributions: {
    fetchKey: FetchStateKey;
    fetchState: FetchState;
    contributions: FutureContribution[];
  };
  upcomingTotalContributions: {
    fetchState: FetchState,
    upcomingContributionsTotal: 0,
  };
  proposedPersonalContributionScheduleCalendars: {
    fetchState: FetchState;
    pageNumber: number;
    calendarEntries: FutureContribution[];
  };
  personalContributionSchedulingEligibility: {
    fetchState: FetchState;
    isEligible: boolean;
  };
  schedule: TransferSchedule;
  scheduleFetchState: FetchState;
}

export interface DistributionSchedulesState {
    distributionSchedules: {
        fetchState: FetchState;
        fetchKey: FetchStateKey;
        pageNumber: number;
        schedules: TransferSchedule[];
    };
}

export enum TransferScheduleType {
  None = "none",
  MonthlyByDate = "monthlyByDate",
  MonthlyByDay = "monthlyByDay",
  Weekly = "weekly",
  OneTime = "oneTime"
}

export enum TransferScheduleStatus {
  None = "none",
  Active = "active",
  Cancelled = "cancelled",
  Revised = "revised",
  Completed = "completed",
  Failed = "failed",
  AwaitingAccountActivation = "awaitingAccountActivation"
}

export enum TransferScheduleWeekDay {
  None = "none",
  Monday = "monday",
  Tuesday = "tuesday",
  Wednesday = "wednesday",
  Thursday = "thursday",
  Friday = "friday"
}

export enum TransferScheduleMonthBoundary {
  None = "none",
  First = "first",
  Second = "second",
  Third = "third",
  Fourth = "fourth",
  Last = "last"
}

export interface TransferScheduleWeeklyDetails {
  dayOfWeek: TransferScheduleWeekDay;
}

export interface TransferScheduleMonthlyByDateDetails {
  dayOfMonth?: number;
  isLastDay?: boolean;
}

export interface TransferScheduleMonthlyByDayDetails {
  monthBoundary: TransferScheduleMonthBoundary;
  dayOfWeek: TransferScheduleWeekDay;
}

export interface TransferScheduleOneTimeDetails {
  isPriorYearContribution: boolean;
}

export interface TransferSchedule {
  scheduleId: number;
    linkedExternalBankAccountId?: number;
  scheduleType: TransferScheduleType;
  scheduleInterval: number;
    amount?: number;
  scheduleStartDate: string;
  scheduleEndDate?: string;
  dateLastModified: string;
  status: TransferScheduleStatus;
  userConfirmationDate: string;
  weeklyDetails: TransferScheduleWeeklyDetails[];
  monthlyByDateDetails: TransferScheduleMonthlyByDateDetails[];
  monthlyByDayDetails: TransferScheduleMonthlyByDayDetails[];
  oneTimeDetails?: TransferScheduleOneTimeDetails[];
  upcomingDates?: string[];
  nextDate?: string;
  isLocked: boolean;
  cancelledBy?: string;
}


export interface GenerateScheduleCalendarEntriesRequest {
    startRange: string;
    endRange: string;
    schedule: TransferSchedule;
}

export interface CalendarEntriesResult {
    schedule: TransferSchedule;
    startRange: string;
    endRange: string;
    calendarDates: string[];
}

export interface FutureContribution {
  personalContributionScheduleId: number;
  calendarDate: string;
  expectedPostDate: string;
  amount: number;
  linkedExternalBankAccountId: string;
  status: string;
  effectiveYear: number;
  isPriorYearContribution: boolean;
}

export enum StoreType {
    noStore = "noStore",
    hsaStore = "hsaStore",
    amazonMarketPlace = "amazonMarketPlace"
}

export enum ContributionType {
    All = "all",
    PostTax = "postTax",
    EmployeePayroll = "employeePayroll",
    EmployerPayroll = "employerPayroll"
}

export enum ManagedExpenseType {
    Unknown = "unknown",
    AvailableExpense = "availableExpense",
    SubmittedExpense = "submittedExpense",
    Transaction = "transaction",
    RecurringTemplate = "recurringTemplate",
}

export interface ManagedExpensePageRequest {
    pageNumber?: number;
    pageSize?: number;
    baseFilter: ManagedExpenseFilterType;
    textSearch: string | null;
    categoryFilter: HsabExpenseCategory | null;
    startDateFilter: string | null;
    endDateFilter: string | null;
    sortByColumn: ManagedExpensesSortableColumns | null;
    sortOrder: SortOrder | null;
}

export enum ManagedExpensesSortableColumns {
    Amount = "amount",
    Date = "date",
    Provider = "provider",
    PlanName = "planName",
    Status = "status",
    Source = "source",
    Created = "created",
    LastModified = "lastModified",
    ScheduleDescription = "scheduleDescription",
    Category = "category",
}

export enum ManagedExpenseFilterType {
    None = "none",
    MyExpenses = "myExpenses",
    ActionNeeded = "actionNeeded",
    HealthPlan = "healthPlan",
    MyTalonClaims = "myTalonClaims",
    RecurringExpenseTemplates = "recurringExpenseTemplates",
}

export interface ManagedExpenseResponse {
    managedExpenseType: ManagedExpenseType;
    expenseId: number | null;
    reimbursementId: number | null;
    expenseCategory: HsabExpenseCategory;
    amount: number;
    dateOfService: string;
    dateOfServiceEnd: string;
    displayText: string;
    secondaryDisplayText: string;
    planDisplayText: string[];
    status: HsabExpenseDisplayStatus[];
    originator: HsabExpenseOriginator;
    showErrorIndicator: boolean;
    dateCreated: string;
    dateLastModified: string;
}
export interface EmployerBankSettingsRequest {
    dateMassTransitStart: string;
    dateMassTransitEnd: string;
    employerId: number;
}
export interface EmployerBankSettingsResponse extends EmployerBankSettingsRequest {
    hasMassTransitPlan: boolean;
    status: MassTransitManualClaimStatus;
    enableRetailContributionWarning: boolean;
}
export enum MassTransitManualClaimStatus {
    Inactive = "inactive",
    Active = "active",
    WillBeActiveOnFutureDate = "willBeActiveOnFutureDate"
}
export const massTransitManualClaimStatusMap: Record<MassTransitManualClaimStatus, string> = {
    inactive: "Inactive",
    active: "Active",
    willBeActiveOnFutureDate: "Will be active on a future date",
};
export interface EmployerBankSettingsState {
    fetchState: FetchState;
    employerId: number;
    bankSettings: EmployerBankSettingsResponse;
}
export interface UnusedDistributionResponse {
    managedExpenseType: ManagedExpenseType;
    expenseId: number | null;
    reimbursementId: number | null;
    expenseCategory: HsabExpenseCategory;
    amount: number;
    dateOfService: string;
    displayText: string;
    planDisplayText: string[];
    status: HsabExpenseDisplayStatus[];
    originator: HsabExpenseOriginator;
    flowId: string;
    recipients: UnusedDistributionRecipient[];
}
export interface UnusedDistributionRecipient {
    firstName: string;
    middleInitial: string;
    lastName: string;
    relationship: string;
}
export interface ManagedExpenseInitialState {
    currentPage: {
        fetchState: FetchState,
        page: number;
        pageSize: number;
        totalRecords: number;
        baseFilter: ManagedExpenseFilterType,
        textSearch: string | null;
        categoryFilter: HsabExpenseCategory | null;
        startDateFilter: string | null;
        endDateFilter: string | null;
        dateFilterDisplayText: string | null;
        sortByColumn: ManagedExpensesSortableColumns | null;
        sortOrder: SortOrder | null;
        numberOfPages: number;
        records: ManagedExpenseResponse[];
    };
    showAdditionalFilters: boolean;
    filterOptions: ManagedExpenseFilterOptions;
    actionNeededPreview: {
        fetchState: FetchState;
        recordsPreview: ManagedExpenseResponse[];
        totalRecords: number | null;
    };
    userHasHealthPlanExpense: boolean;
    userHasHealthPlanExpenseFetchState: FetchState;
    userHasRecurringExpenses: boolean;
    userHasRecurringExpensesFetchState: FetchState;
}
export interface HsabExpenseFormState {
    expenseForm: HsabExpenseRequest | null;
    modalMode: HsabExpenseFormMode;
    unusedDistribution: UnusedDistributionResponse | null;
    expense: HsabExpenseResponse | null;
    claim: Claim | null;
    checkedShoeboxExpenseEvidence: HsabExpenseEvidenceShoeboxResponse[];
    currentPayFromOptions: PayFromPlanOption[];
    currentlySelectedCategory: HsabExpenseCategoryDisplay | null;
    isTravelCostReimbursementEnabled: boolean;
    agreedToTerms: boolean;
    recentlySubmitted: boolean;
    payFromOption: string;
    selectedPayee: HsabPayee;
    excludeHsaPlans: boolean;
    forceHsaPlans: boolean;
    currentPayeeSelectorState: SelectorState;
    isSaving: boolean;
    formStepToDisplay: HsabExpenseFormStep;
    generatedCalendarEntries: CalendarEntriesResult;
    calendarEntriesFetchState: FetchState;
    scheduleDescription: string;
}

export enum HsabExpenseFormStep {
    details,
    schedule,
    addBankAccount,
    confirmation,
}
export const enum SelectorState {
    uninitialized,
    Searching,
    Selected,
    Editing,
    Creating,
    DeleteConfirmation,
}
export interface HsabExpenseEvidenceShoeboxInitialState {
    currentPage: {
        fetchState: FetchState,
        page: number;
        pageSize: number;
        totalRecords: number;
        numberOfPages: number;
        records: HsabExpenseEvidenceShoeboxResponse[];
    };
    isModalOpen: boolean;
    modalOpenedBy: HsabExpenseEvidenceShoeboxModalSource | null;
    isBrowsingUploadedFiles: boolean;
}

export interface HsabExpenseEvidenceShoeboxResponse {
    hsabExpenseEvidenceShoeboxId: number;
    physicalFileName: string;
    displayFileName: string;
    dateDeleted: string | null;
    dateCreated: string;
    dateUploaded: string | null;
    dateLastModified: string;
    imageType: ExpenseEvidenceImageType;
    receiptType: ExpenseEvidenceReceiptType;
    isChecked?: boolean;
}

export enum ExpenseEvidenceImageType {
    None = "none",
    Pdf = "pdf",
    Png = "png",
    Jpg = "jpg",
    Jpeg = "jpeg",
    Gif = "gif",
    MobileReceipt = "mobileReceipt",
}

export enum ExpenseEvidenceReceiptType {
    None = "none",
    Expense = "expense",
    NotionalClaim = "notionalClaim",
    HsaDistribution = "hsaDistribution",
    Organizer = "organizer",
}

export enum HsabExpenseOriginator {
    Unknown = "unknown",
    Consumer = "consumer",
    DebitCard = "debitCard",
    CarrierClaim = "carrierClaim",
    InsuranceClaim = "insuranceClaim",
    InteractiveVoiceRecognition = "interactiveVoiceRecognition",
    Import = "import",
    Administrator = "administrator",
    RecurringExpense = "recurringExpense",
    IntegratedPaymentNetwork = "integratedPaymentNetwork",
    Disbursement = "disbursement",
    Migrated = "migrated",
}
export const hsabExpenseOriginatorTypeMap: Record<HsabExpenseOriginator, string> = {
    unknown: "Unknown",
    consumer: "Consumer",
    debitCard: "Debit Card",
    carrierClaim: "Health Plan",
    insuranceClaim: "Health Plan",
    interactiveVoiceRecognition: "Interactive Voice Recognition",
    import: "Import",
    administrator: "Administrator",
    recurringExpense: "Recurring Expense",
    integratedPaymentNetwork: "Integrated Payment Network",
    disbursement: "Disbursement",
    migrated: "Migrated",
};
export const hsabExpenseCategoryMap: Record<HsabExpenseCategory, string> = {
    none: "none",
    unknown: "Unknown",
    medical: "Medical",
    dental: "Dental",
    vision: "Vision",
    pharmacy: "Pharmacy",
    premium: "Premium",
    dependentCare: "Dependent Care",
    parking: "Parking",
    massTransit: "Mass Transit",
    lifestyle: "Lifestyle",
    tuition: "Tuition",
};
export enum HsabExpensePaymentStatus {
    Unknown = "Unknown",
    Unpaid = "unpaid",
    PartiallyPaid = "partiallyPaid",
    Paid = "paid",
    MarkedAsPaid = "markedAsPaid",
    Flagged = "flagged",
    Applied = "applied",
}
export interface ExpenseSubmitLogItem {
    fileName: string;
    dateCreated: string;
}
export interface HsabExpenseSubmissionErrorResponse {
    hsabExpenseId: number;
    errorMessage: string;
    dateCreated: string;
    dateLastModified: string;
}
export interface HsabExpenseResponse {
    hsabExpenseId: number;
    externalExpenseId: string;
    cognitoUsername: string;
    expenseName: string;
    providerName: string;
    amount: number;
    dateOfService: string;
    comment: string;
    isPayToPayee: boolean;
    payeeName: string;
    payeeAddress1: string;
    payeeAddress2: string;
    payeeCity: string;
    payeeStateCode: string;
    payeeZipCode: string;
    payeeMemberNumber: string;
    expenseCategory: HsabExpenseCategory | null;
    expenseType: string;
    expenseSubType: string;
    expenseSubTypeDisplayName: string;
    numberOfMiles: number | null;
    mileageDollarValue: number | null;
    copayAmount: number | null;
    coinsuranceAmount: number | null;
    deductibleAmount: number | null;
    insurancePaidAmount: number | null;
    providerDiscountAmount: number | null;
    coverageAmount: number | null;
    billedAmount: number | null;
    isFiledBasedOnMedicalAmounts: boolean | null;
    originator: HsabExpenseOriginator | null;
    externalPaymentStatus: HsabExpensePaymentStatus;
    externalReferenceId: string | null;
    dateCreated: string;
    dateLastModified: string;
    reimbursement: HsabExpenseReimbursementResponse;
    expenseEvidence: HsabExpenseEvidenceResponse[];
    isAnyAttachmentInFailedStatus: boolean;
    isSubmitted: boolean;
    isSelected: boolean;
    recipientExternalId: string;
    importedRecipients: FamilyMember[];
    recipientType: HsabRecipientType;
    manuallyMarkedAsPaidStatus: ManuallyMarkedAsPaidStatus;
    isFileUploadAllowed: boolean;
    isValidRecipient: boolean;
    isCloned: boolean;
    linkedDebitCardTransactionId?: number;
    linkedClaimNumbers: string[];
    schedule: TransferSchedule;
    relationships: HsabExpenseRelationshipResponse[];
    displayStatus: HsabExpenseDisplayStatus;
    hsabExpenseSubmissionError: HsabExpenseSubmissionErrorResponse | null;

    // Notional, handled by wex
    isRecurring: boolean;

    // HSA, handled by us
    isRecurringTemplate: boolean;
}

export interface HsabExpenseRelationshipResponse {
    hsabExpenseRelationshipId: number;
    relationshipType: RelationshipType;
    sourceHsabExpenseId: number;
    sourceExpense: HsabExpenseResponse;
    targetHsabExpenseId: number;
    targetExpense: HsabExpenseResponse;
    dateCreated: string;
}

export enum RelationshipType {
    Unknown = "unknown",
    ClonedDueToWexRequirements = "clonedDueToWexRequirements",
    ClonedFromRecurringTemplate = "clonedFromRecurringTemplate",
}

export interface ExpenseReimburseMeLimit {
    amountReimbursedToday: number;
    dailyLimit: number;
    dateCalculated: string;
}
export interface HsabExpenseReimbursementResponse {
    hsabExpenseReimbursementId: number;
    hsabExpenseId: number;
    userId: number;
    externalReimbursementId: string;
    externalTransactionId: string;
    isSuspectedDuplicate: boolean;
    planName: string;
    planYearName: string;
    approvedAmount: number | null;
    deniedAmount: number | null;
    appliedDeductibleAmount: number | null;
    appliedCoinsuranceAmount: number | null;
    appliedCopayAmount: number | null;
    totalClaimAmount: number;
    paidAmount: number;
    reimbursementStatus: WexClaimStatus;
    displayStatus: HsabExpenseDisplayStatus;
    evidenceStatus: WexReceiptStatus;
    expenseCategory: WexExpenseAccountType | null;
    expenseType: string;
    expenseSubType: string;
    planYearFinalFilingDate: string | null;
    scheduledForReimbursementDate: string | null;
    numberOfMiles: number | null;
    mileageDollarValue: number | null;
    dateCreated: string;
    dateLastModified: string;
    denialReasons: HsabExpenseReimbursementDenialReasonResponse[];
    isHsaReimbursement: boolean;
    originator: string;
    repayments: HsabExpenseReimbursementRepaymentResponse[];
    informationRequests: HsabExpenseReimbursementInformationRequestResponse[];
    overflowReimbursements: HsabExpenseReimbursementResponse[];
}

export interface HsabExpenseEvidenceResponse {
    hsabExpenseEvidenceId: number;
    fileName: string;
    dateCreated: string;
    dateUploaded: string;
    wexUploadStatus: WexUploadStatus | null;
}

export interface HsabExpenseReimbursementDenialReasonResponse {
    hsabExpenseReimbursementDenialReasonId: number;
    reimbursementId: number;
    denialCode: string;
    denialDateTime: string;
    isUploadReceiptAllowed: boolean;
    reason: string;
}
export interface HsabExpenseReimbursementInformationRequestResponse {
    hsabExpenseReimbursementInformationRequestId: number;
    reimbursementId: number;
    code: string;
    date: string;
    reason: string;
}

export interface HsabExpenseReimbursementRepaymentResponse {
    hsabExpenseReimbursementRepaymentId: number;
    hsabExpenseReimbursementId: number;
    externalRepaymentId: number;
    totalAmount: number;
    outstandingAmount: number;
    externalReimbursementId: string;
    method: ExpenseReimbursementMethod;
    status: ExpenseReimbursementStatus;
    dateCreated: string;
    dateLastModified: string;
}

export enum ExpenseReimbursementMethod {
    None = "none",
    Check = "check",
    Payroll = "payroll",
    Provider = "provider",
    ElectronicFundsTransfer = "electronicFundsTransfer",
}
export const expenseReimbursementMethodMap: Record<ExpenseReimbursementMethod, string> = {
    none: "N/A",
    check: "Check",
    payroll: "Payroll",
    provider: "Provider",
    electronicFundsTransfer: "Electronic Transfer"
};
export enum ExpenseReimbursementStatus {
    None = "none",
    Pending = "pending",
    Scheduled = "scheduled",
    Repaid = "repaid",
    Cancelled = "cancelled",
}
export const expenseReimbursementStatusMap: Record<ExpenseReimbursementStatus, string> = {
    none: "N/A",
    pending: "Pending",
    scheduled: "Scheduled",
    repaid: "Repaid",
    cancelled: "Cancelled"
};
export interface HsabSubmitExpenseRequest {
    expenseId: number;
    payFromOption: string;
    isValidPreHsaStartDateExpense: boolean;
    payFromIsHsaOnly: boolean;
    excludeHsaPlans?: boolean;
    forceHsaPlans?: boolean;
    payFromPlanOptions?: PayFromPlanOption[] | null;
    autoRefreshClaimStatus: boolean;
}

export interface HsabExpenseRequest {
    hsabExpenseId: number | null;
    expenseName: string;
    providerName: string;
    amount: number;
    dateOfService: string;
    comment: string;
    isPayToPayee: boolean;
    payeeName: string;
    payeeAddress1: string;
    payeeAddress2: string;
    payeeCity: string;
    payeeStateCode: string;
    payeeZipCode: string;
    payeeMemberNumber: string;
    expenseCategory: HsabExpenseCategory | null;
    expenseType: string;
    expenseSubType: string;
    expenseSubTypeDisplayName: string;
    numberOfMiles: number | null;
    mileageDollarValue: number | null;
    stagedFilesForUpload?: File[] | null;
    recipientExternalId: string;
    manuallyMarkedAsPaidStatus?: ManuallyMarkedAsPaidStatus;
    recipientType: HsabRecipientType;
    linkedDebitCardTransactionId?: number;
    hsabExpenseEvidenceShoeboxIds?: number[] | null;
    externalReferenceId: string;
    hashValue?: string;

    // Notional Recurring handled by Wex;
    isRecurring: boolean;

    // HSA Recurring handled by us
    isRecurringTemplate: boolean;
    schedule?: TransferSchedule;
}

export enum HsabRecipientType {
    None = "none",
    Subscriber = "subscriber",
    Dependant = "dependant",
}
export interface HsabExpenseSubmitResponse {
    expense: HsabExpenseResponse;
    reimbursement: HsabExpenseReimbursementResponse;
}

export interface HsabExpenseByExternalIdRequest {
    externalReferenceId?: string;
    externalReimbursementId?: string;
    externalTransactionId?: string;
}

export interface HsabExpenseEvidenceUploadResponse {
    evidenceCreated: HsabExpenseEvidenceResponse[];
    errors: string[];
}

export interface HsabExpenseEvidenceShoeboxUploadResponse {
    evidenceCreated: HsabExpenseEvidenceShoeboxResponse[];
    errors: string[];
}

export interface HsabExpenseCategoryDisplay {
    display: string;
    category: HsabExpenseCategory;
    external: WexExpenseAccountType;
    showTypeSelector: boolean;
    defaultTypeWhenSelected: string | null;
    defaultSubTypeWhenSelected?: (plans: HsabAccountNametag[]) => string | null;
}

export enum HsabExpenseFormSubmitMethod {
    save,
    saveAndSubmit,
}
export interface FeeCollectionTypeState {
    feeCollectionType: FeeCollectionType;
    fetchState: FetchState;
    isDefaultData: boolean;
}

export interface InvestmentFeeScheduleState {
    feeSchedules: InvestmentFeeSchedule[];
    fetchState: FetchState;
    isDefaultData: boolean;
}

export interface HsabBalanceState {
    fetchState: FetchState;
    fetchKey: FetchStateKey;
    dailyBalances: BankDailyBalanceRecord[];
    dailyBalancesDictionary: {
        [key: number]: BankDailyBalanceRecord[]
    };
    currentEndDateKey: string;
}

export interface ConsumerBankAccountState {
    fetchState: FetchState;
    consumerBankAccounts: ConsumerBankAccountsResponse[];
}

export interface ConsumerBankAccountsResponse {
    bankAccount: BankAccount;
    bankAccountKey: BankAccountKey;
    bankAccountUse: ConsumerBankAccountUse;
}
export interface BankAccount {
    addressLine1: string;
    addressLine2: string;
    bankAccountStatus: ConsumerBankAccountStatus;
    bankAccountType: ConsumerBankAccountType;
    bankName: string;
    city: string;
    country: string;
    generatePreNote: boolean;
    nickName: string;
    state: string;
    zipCode: string;
}

export interface BankAccountKey {
    accountLastFour: string;
    bankAccountKey: string;
}

export enum ConsumerBankAccountStatus {
    Unknown = "unknown",
    Unspecified = "unspecified",
    Inactive = "inactive",
    Active = "active",
    Pending = "pending",
}

export enum ConsumerBankAccountUse {
    Unknown = "unknown",
    Unspecified = "unspecified",
    DirectDeposit = "directDeposit",
    ReimbursementDebitCard = "reimbursementDebitCard",
    VebaWithdrawals = "vebaWithdrawals",
    Other = "other",
}

export enum ConsumerBankAccountType {
    Unknown = "unknown",
    Unspecified = "unspecified",
    Checking = "checking",
    Savings = "savings",
}

export interface InvestmentCustodianDisplayName {
    key: InvestmentCustodian;
    displayName: string;
}

export enum EmployeeNavItem {
    None = "none",
    Dashboard = "dashboard",
    Balances = "balances",
    ContributionPlanner = "contributionPlanner",
    Resources = "resources",
    ChangePlans = "changePlans",
    HealthcareExpenses = "healthcareExpenses",
    Settings = "settings",
    Invest = "invest",
    Contributions = "contributions",
    ProductScanner = "productScanner",
    NotionalDashboard = "notionalDashboard",
    ExpenseManagement = "expenseManagement",
    MessageCenter = "messageCenter",
    Summary = "summary",
    Transfers = "transfers"
}

export interface WexDebitCardsState {
    fetchState: FetchState;
    wexDebitCards: CardWexRecord[];
    wexNewCardRequestModalOpen: boolean;
    wexManageCardModalOpen: boolean;
    WexDebitCardManageCardViewState: WexDebitCardManageCardViewState;
    RowSelected: CardWexRecord;
}

export interface CardListWexResponse {
    cards: CardWexRecord[];
}

export interface CardWexRecord {
    cardNumberLast4: string | null;
    isPinSet: boolean;
    firstName: string;
    lastName: string;
    cardStatus: DebitCardStatus;
}

export enum DebitCardStatus {
    None = "none",
    Active = "active",
    Inactive = "inactive",
    Cancelled = "cancelled",
    ReplacementRequested = "replacementRequested",
    Pending = "pending",
    Error = "error"
}

export enum WexDebitCardManageCardViewState {
    ManageCardForm,
    DeactivateCardForm,
    UpdatePinForm,
    ConfirmAddressForm,
    ConfirmDeactivateCardForm
}

export enum DebitCardManagerModalViewState {
    None,
    ManageCardForm,
    DeactivateCardForm,
    UpdatePinForm,
    PinsNotMatchingForm,
    PinSetCompleteForm,
    ConfirmAddressForm,
    DeactivateCardCompleteForm,
    RequestNewCardForm,
    RequestNewCardConfirmForm,
    RequestNewCardCompleteForm,
    PinsMatchingPreviousForm
}

export enum WexDebitCardDeactivationReasons {
    LostStolen = "Lost/Stolen",
    FraulentActivity = "Fraudulent charges/activity",
    DamagedCard = "Card is damaged"
}

export interface ContributionActivityList {
    contributionActivity: ContributionActivity[];
    pageNumber: number;
    numberOfPages: number;
}

export interface ContributionActivity {
    contributionDate: string;
    amount: number;
    type: TransactionSource;
    status: TransactionApiStatus;
    displayDescription: string;
    displaySource: string;
    displayRejectionReason: string;
}

export interface AddressFormState {
    saveDisabled: boolean;
    isValid: boolean;
}

export interface AppUpdatePromptState {
    canShowPrompt: boolean;
    fetchState: FetchState;
}

export enum CardProvider {
    None = "none",
    Fiserv = "fiserv",
    Wex = "wex"
}

export interface CardProviderCapability {
    canManageCard: boolean;
    canReplaceCard: boolean;
    canDeactivateCard: boolean;
    canOrderNewCards: boolean;
    canInitializePin: boolean;
    canUpdatePin: boolean;
    canConfirmCardReplacement: boolean;
    canOrderCardWhenAddingAuthSigner: boolean;
}

export interface CardProviderOptionsState {
    fetchState: FetchState;
    cardProvider: CardProvider;
    capability: null|CardProviderCapability;
}

export interface CardProviderOptionsResponse {
    cardProvider: CardProvider;
    isUserFound: boolean;
    capability: CardProviderCapability;
}


export interface UserCapabilitiesState {
    fetchState: FetchState;
    capabilities: null|UserCapabilitiesResponse;
}

export interface UserCapabilitiesResponse {
    cardManagementScreenVisible: boolean;
}


export interface AdminPortalUser {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    userName: string;
    status: string;
    employeeId: string;
    userId: number;
    roles: Role[];
    groups: string[];
}

export interface DropDownModel {
    value: string;
    text: string;
}

export interface InstrumentSearchRequest {
    searchTerm: string;
    accountId: number;
    availableToBuyOnly: boolean;
    fundTypeBit: string;
    fundCompanyId: number;
    assetClassId: number;
    indexFundOnly: boolean;
    pageSize: number;
    pageNumber: number;
}

export interface SearchAdminUsers {
        pageNumber: number;
        pageSize: number;
        searchFirstName: string;
        searchLastName: string;
        searchEmail: string;
        searchRole: number | null;
}
export interface AdminUserResponse {
        users: AdminPortalUser[];
        pageNumber: number;
        pageSize: number;
        total: number;
        pageRange: number[];
        totalPages: number;
}
export interface AdminUserStatusChangeRequest {
    userId: number;
    changeToStatus: idpStatusChange;
    currentStatus: string;
}

export interface FeePackageModel {
    feeScheduleId: string;
    feePackageName: string;
}

export interface FeePackageListResponse {
    feePackages: FeePackageModel[];
}

export interface PurseStackingOrderModel {
    purseStackingOrderValueId: number;
    purseStackingOrderValue: string;
    displayOrder: number;
}

export interface PurseStackingOrdersListResponse {
    purseStackingOrderModels: PurseStackingOrderModel[];
}

export enum DebitCardDeactivationReasons {
    Lost = "Lost",
    Stolen = "Stolen",
    FraudulentActivity = "FraudulentActivity",
    DamagedCard = "DamagedCard"
}

export enum HsabExpenseFormMode {
    none = 0,
    createOrEdit = 1,
    debitCard = 2,
    confirmSubmission = 3,
    reviewSubmission = 4,
    recurringTemplate = 5,
}

export enum HsabExpenseEvidenceShoeboxModalSource {
    none = 0,
    expenseManagement = 1,
    expenseForm = 2,
    advancedNav = 3,
    cardRowExpenseEvidence = 4,
}

export interface PlanYearEnrollmentDetailsResponse {
    insuranceClaimSettings: InsuranceClaimSettings;
    planYearEnrollmentDetails: PlanYearEnrollmentDetails[];
}

export interface InsuranceClaimSettings {
    isOptedIn: boolean;
    isHsaAutoPayEnabled: boolean;
    isNotionalAutoPayEnabled: boolean;
}

export interface PlanYearEnrollmentDetails {
    planYearName: string;
    planYearStartDate: string;
    isPartialEnrollment: boolean;
    isDebitCard: boolean;
    reimbursementMethod: HsabReimbursementMethod;
    planEnrollmentDetails: PlanEnrollmentDetails[];
}

export enum HsabReimbursementMethod {
    Unknown = "unknown",
    DirectDeposit = "directDeposit",
    Check = "check",
}

export interface PlanEnrollmentDetails {
    electionAmount: number;
    employerContributionAmount: number;
    employerContributionLevel: number;
    enrollmentEffectiveDate: string;
    healthDataEnrollment: string;
    isEnrolledInClaimsExchange: boolean;
    planName: string;
    terminationDate: string;
}

export class CognitoAuthOptions {
    userPoolId: string;
    clientId: string;
    identityPoolId: string;
}

export interface UserMfaOptions {
    mfaOptions: MfaItem[];
}

export interface UserInterestRates {
  interestRates: UserInterestRate[];
}

export interface UserInterestRate {
  interestRate: number;
  balanceMin: number;
  balanceMax: number;
}

export interface EmployerActiveFeeSchedules {
  feeSchedules: EmployerFeeSchedule[];
}

export interface EmployerFeeSchedule {
  packageName: string;
  feeName: string;
  feeAmount: number;
}

export interface EmployerFeeScheduleTableItem {
  key: string;
  packageName: string;
  feeType: string;
  amount: string;
}

export interface InterestRateTableItem {
  balance: string;
  interestRate: string;
}

export interface MessageCenterState {
    messagesFetchState: FetchState;
    messageFetchState: FetchState;
    messageUpdateFetchState: FetchState;
    messageCenterMessageResponse: MessageCenterGetMessagesResponse;
    selectedMessage: MessageCenterMessage;
}

export interface MessageCenterGetMessagesResponse {
    pageSize: number;
    pageNumber: number;
    messages: MessageCenterMessage[];
    totalRecords: number;
    pageRange: number;
    totalPages: number;
}

export interface MessageCenterMessage {
    messageCenterMessageId: number;
    messageId: string;
    subject: string;
    content: string;
    createDate: string;
    isArchived: boolean;
    isRead: boolean;
    sender: string;
    receiver: string;
    isSaved: boolean;
    messageCenterDocuments: MessageCenterDocument[];
}

export interface MessageCenterDocument {
    documentId: string;
    documentName: string;
}

export enum CitizenshipType {
  Unknown = "unknown",
  UsCitizen = "usCitizen",
  ResidentAlien = "residentAlien",
  NonResidentAlien = "nonResidentAlien"
}

export const citizenshipTypeDisplayMap: Record<CitizenshipType, string> = {
  unknown: "Unknown",
  usCitizen: "U.S. citizen",
  residentAlien: "Lawful permanent resident",
  nonResidentAlien: "Non-U.S. resident"
};

export enum EmploymentCategory {
  Unknown = "unknown",
  Employed = "employed",
  NotEmployed = "notEmployed",
  SelfEmployed = "selfEmployed",
  Retired = "retired"
}

export const employmentCategoryDisplayMap: Record<EmploymentCategory, string> = {
  unknown: "Unknown",
  employed: "Employed",
  notEmployed: "Not employed",
  selfEmployed: "Self-employed",
  retired: "Retired"
};

export interface UserDiscretionaryData {
  citizenship: CitizenshipType;
  citizenshipCountry: string;
  employmentCategory: EmploymentCategory;
  employerName: string;
  jobTitle: string;
}

export interface CountryList {
  countries: Country[];
}

export interface CountryState {
  fetchState: FetchState;
  countries: Country[];
}

export interface Country {
  code: string;
  name: string;
}

export interface UserDiscretionaryDataState {
  fetchState: FetchState;
  discretionaryData: UserDiscretionaryData;
}

export enum HealthPlanType {
  Unknown = "unknown",
  Hdhp = "hdhp",
  Bronze = "bronze",
  Catastrophic = "catastrophic",
  Other = "other"
}

export interface MessageCenterRequest {
    pageNumber: number;
    pageSize: number;
    messageFilter: MessageFilterType;
}

export interface UpdateMessageRequest {
    messageId: number;
    isSaved: boolean;
    isArchived: boolean;
}

export enum MessageFilterType {
    All = 1,
    Saved = 2,
    Archived = 3,
    Inbox = 4
}

export enum MessageActionType {
    Save = 1,
    Archive = 2
}

export interface UserPersonalInfo {
    ssn: string;
}

export interface UserPersonalInfoState {
    fetchState: FetchState;
    personalInfo: UserPersonalInfo;
}

export enum BalanceTransferStep {
    TransferFunds = 0,
    TrusteeToTrusteeTransferDetails = 1,
    TrusteeToTrusteeTransferFormDownload = 2,
    TrusteeToTrusteeTransferFinish = 3,
    RolloverTransferDetails = 4,
    RolloverTransferFormDownload = 5,
    RolloverTransferFinish = 6
}

export const balanceTransferStepKeyRing = {
    "trustee-details": BalanceTransferStep.TrusteeToTrusteeTransferDetails,
    "trustee-download": BalanceTransferStep.TrusteeToTrusteeTransferFormDownload,
    "trustee-finish": BalanceTransferStep.TrusteeToTrusteeTransferFinish,
    "rollover-details": BalanceTransferStep.RolloverTransferDetails,
    "rollover-download": BalanceTransferStep.RolloverTransferFormDownload,
    "rollover-finish": BalanceTransferStep.RolloverTransferFinish
};

type BalanceTransferStepKey = keyof typeof balanceTransferStepKeyRing;

export { BalanceTransferStepKey };

export enum BalanceTransferMethod {
    None = 0,
    TrusteeToTrustee = 1,
    Rollover = 2
}

export interface RolloverDetails {
    amount: number;
}
export interface HSARepContactInfo {
    firstName: string;
    lastName: string;
    title: string;
    phone: string;
    email: string;
    timeZoneSidKey: string;
    startOfDay: string;
    endOfDay: string;
    mediumPhotoUrl: string;
    businessHours: string;
    mediumPhotoContent: string;
    mediumPhotoFileType: string;
    repTypeLabel: string;
    repTypeHoverText?: string;
    showPhone: boolean;
    hoursConfig: string;
    customHours: string;
}

export interface BalanceTransferState {
    transferMethod: BalanceTransferMethod;
    rolloverDetails: RolloverDetails;
    trusteeToTrusteeDetails: TrusteeToTrusteeDetails;
    isOnboarding: boolean;
}

export interface HSAServiceRepContactInfo {
    firstName: string;
    lastName: string;
    title?: string;
    phone: string;
    email: string;
    showPhone?: boolean;
    repTypeHoverText?: string;
    businessHours?: string;
    hoursConfig?: string;
}

export interface TrusteeToTrusteeDetails {
    trusteeAccountNumber: string;
    transferEntireBalance: boolean;
    transferPartialBalance: boolean;
    amount: number;
}
export interface HSARepData {
    aeContact?: HSARepContactInfo;
    csmContact?: HSARepContactInfo;
    brContact: HSARepContactInfo;
    serviceContact?: HSAServiceRepContactInfo;
}

export interface TrusteeToTrusteeBalanceTransferFormRequest {
    trusteeAccountNumber: string;
    transferEntireBalance: boolean;
    amount: number;
    hsaBankAccountNumber: string;
}

export interface RolloverBalanceTransferFormRequest {
    amount: number;
    hsaBankAccountNumber: string;
}

export interface BalanceTransferFormResponse {
    url: string;
    downloadUrl: string;
}

export interface ImportantDateState {
    fetchState: FetchState;
    importantDates: string[];
}

export interface ImportantDateRequest {
    startDate: Date;
    count: number;
}

export interface BannerState {
    fetchState: FetchState;
    defaultBanners: PartnerBanner[];
    customBanners: PartnerBanner[];
    displayBanners: PartnerBanner[];
    displayDefaultBanners: boolean;
}

export interface PartnerBanner {
    bannerId: string;
    partnerId: string;
    titleA: string;
    titleB: string;
    description: string;
    linkUrl: string;
    linkText: string;
    carouselOrder: number;
    error?: string;
}

export interface BannerOrderItem {
    bannerId: string;
    carouselOrder: number;
}

export interface UpdatePartnerBannerOrderRequest {
    banners: BannerOrderItem[];
}

export interface NewPartnerBanner {
    titleA: string;
    titleB: string;
    description: string;
    linkUrl: string;
    linkText: string;
}

export interface DisplayDefaultBannersRequest {
    isDisplayingDefaultBanners: boolean;
}

export interface EmbeddedServiceBootstrap {
    settings: {
        language: string;
    };
    init: (
        orgId: string,
        deploymentName: string,
        baseUrl: string,
        config: { scrt2URL: string }
    ) => void;
    prechatAPI: PrechatAPI;
    userVerificationAPI: UserVerificationAPI;
    onEmbeddedMessagingReady: () => void;
    removeEventListeners?: () => void;
}

export interface PreChatVisibleField {
    [fieldName: string]: { value: string | null, isEditableByEndUser: boolean};
}

export interface PreChatHiddenField {
    [fieldName: string]: string;
}

export interface PrechatAPI {
    setVisiblePrechatFields: (fields: PreChatVisibleField) => void;
    setHiddenPrechatFields: (fields: PreChatHiddenField) => void;
}

export interface UserVerificationAPI {
    clearSession: () => Promise<void>;
}

export interface PlanSetting {
    offeringId: number;
    groupOrEmployerId: string;
    externalEmployerId: string;
    planName: string;
    planYearName: string;
    planYearStartDate: Date;
    planYearEndDate: Date;
}

export interface HSARepResponse {
    employerDetails: HSARepData[];
    useServiceContact: boolean;
    informationSource: InformationSource;
}
export interface InformationSource {
    informationType: string;
}

export enum Payor {
    Unknown = "unknown",
    Accountholder = "accountholder",
    Employer = "employer"
}

export interface HsaStoreInfoState {
    status: FetchState;
    hsaStoreInfos: HsaStoreInfo[];
}

export interface HsaStoreInfo {
    tileHeader: string;
    tileDescription: string;
    storeButtonText: string;
    storeLinkUrl: string;
    eligibilityLinkText: string;
    eligibilityLinkUrl: string;
    delayInSeconds: number;
    logo: string;
    logoHeightInPx: number;
    logoWidthInPx: number;
    altImageText: string;
    name: string;
}
export interface Product {
    productId: number;
    name: string;
    description: string;
    type: string;
    moreInfoUrl: string;
}


export interface HsaStoreVisibility {
  hsaStoreInfoId: number;
  storeName: string;
  enabled: boolean;
}

export interface HsaStoreVisibilityRequest {
    hsaStoreInfoId: number;
    enabled: boolean;
}

export interface AdminDefaultHsaStoreInfo {
  id: number | null;
  name: string;
  logoKey: string;
  altImageText: string;
  logoHeightInPx: number;
  logoWidthInPx: number;
  tileHeader: string;
  tileDescription: string;
  storeButtonText: string;
  storeLinkUrl: string;
  eligibilityLinkText: string;
  eligibilityLinkUrl: string;
  enabled: boolean;
  delayInSeconds: number;
  displayOrder: number;
}

export enum BrowserIdentifier {
    None = "none",
    Chrome = "chrome",
    Firefox = "firefox",
    Edge = "edge",
    Safari = "safari"
}

export const browserNameMap: Record<BrowserIdentifier, string> = {
    none: "",
    chrome: "Chrome",
    firefox: "Firefox",
    edge: "Microsoft Edge",
    safari: "Safari"
};

export interface BrowserRules {
    [browser: string]: number;
}

export interface BrowserRulesResponse {
    browserRules: BrowserRules;
}

export interface BrowserState {
    deviceInfo: Bowser.Parser.ParsedResult;
    browserRules: BrowserRules;
    isUnsupported: boolean;
    minVersion: number;
}

export const browserMap: Record<string, BrowserIdentifier> = {
    "Chrome": BrowserIdentifier.Chrome,
    "Firefox": BrowserIdentifier.Firefox,
    "Microsoft Edge": BrowserIdentifier.Edge,
    "Safari": BrowserIdentifier.Safari
};

export const browserIconMap: Record<BrowserIdentifier, string> = {
    none: "",
    chrome: "chrome.svg",
    firefox: "firefox.svg",
    edge: "edge.svg",
    safari: "safari.svg"
};

export interface HsaStoreLogoResponse {
    key: string;
    url: string;
}
